/**
 * Catálogo de textos del andamio (layout, auth, estados, errores).
 *
 * Cómo agregar textos desde un módulo nuevo
 * -----------------------------------------
 * Agregá las claves al catálogo `es` con prefijo del módulo
 * (`alerts.inbox.title`) y después a `en` y `pt`. `MessageKey` sale del
 * catálogo `es`, así que **el `tsc` te obliga a traducir**: una clave que
 * falte en `en` o `pt` es un error de compilación, no una cadena en español
 * que aparece en medio de una interfaz en portugués.
 *
 * Los tres catálogos viajan en el bundle. Son unos pocos KB y cargar el
 * idioma por red agrega un parpadeo de texto sin traducir en cada arranque.
 */
import type { Language } from './languages';

const es = {
  // ─────────────────────────────────────────────────────────── aplicación
  'app.name': 'XYVONA',
  'app.subtitle': 'Autenticación de producto',
  'app.skipToContent': 'Saltar al contenido',

  // ─────────────────────────────────────────────────────────────── sesión
  'auth.login.title': 'Ingresar al dashboard',
  'auth.login.intro': 'Usá las credenciales de tu marca.',
  'auth.login.tenant': 'Marca',
  'auth.login.tenantHint': 'El identificador corto de tu laboratorio.',
  'auth.login.email': 'Correo electrónico',
  'auth.login.password': 'Contraseña',
  'auth.login.submit': 'Ingresar',
  'auth.login.submitting': 'Verificando…',
  'auth.login.required': 'Completá los tres campos.',
  'auth.logout': 'Cerrar sesión',
  'auth.loggingOut': 'Cerrando sesión…',
  'auth.session.restoring': 'Restaurando la sesión…',
  'auth.session.expired': 'La sesión venció. Ingresá de nuevo.',
  'auth.session.reused':
    'Se cerraron todas tus sesiones por seguridad: el token de esta sesión se usó dos veces. Si no fuiste vos, avisá al administrador de tu marca.',
  'auth.session.revoked': 'Un administrador cerró tu sesión.',
  'auth.role.label': 'Rol',

  // ───────────────────────────────────────────────────────────── mi cuenta
  'account.title': 'Mi cuenta',
  'account.link': 'Mi cuenta',
  'account.identity.title': 'Quién soy',
  'account.identity.name': 'Nombre',
  'account.identity.email': 'Correo electrónico',
  'account.password.title': 'Cambiar la contraseña',
  'account.password.intro':
    'Se cierran todas tus sesiones abiertas en otros dispositivos. En este seguís adentro.',
  'account.password.current': 'Contraseña actual',
  'account.password.new': 'Contraseña nueva',
  'account.password.newHint': 'Al menos 12 caracteres. Mejor una frase que una palabra.',
  'account.password.confirm': 'Repetí la contraseña nueva',
  'account.password.submit': 'Cambiar la contraseña',
  'account.password.submitting': 'Cambiando…',
  'account.password.required': 'Completá los tres campos.',
  'account.password.tooShort': 'La contraseña nueva necesita al menos 12 caracteres.',
  'account.password.mismatch': 'Las dos contraseñas nuevas no coinciden.',
  'account.password.same': 'La contraseña nueva es igual a la actual.',
  'account.password.wrongCurrent': 'La contraseña actual no es correcta.',
  'account.password.federated':
    'Tu cuenta autentica contra el directorio de tu organización. La contraseña se cambia allá.',
  'account.password.changed': 'Contraseña cambiada.',
  'account.password.changedOthers':
    'Contraseña cambiada. Se cerraron {n} sesiones en otros dispositivos.',
  'account.password.changedRelogin':
    'Contraseña cambiada. No pudimos renovar esta sesión: ingresá de nuevo con la nueva.',

  // ────────────────────────────────────────────────────────── navegación
  'nav.title': 'Módulos',
  'nav.pending': 'En construcción',
  'nav.pendingHint': 'Este módulo todavía no está implementado.',
  'nav.open': 'Abrir navegación',
  'nav.close': 'Cerrar navegación',
  'nav.language': 'Idioma de la interfaz',

  'module.overview': 'Vista general',
  'module.map': 'Mapa de calor',
  'module.products': 'Productos',
  'module.batches': 'Lotes',
  'module.alerts': 'Alertas y casos',
  'module.labels': 'Etiquetas',
  'module.analytics': 'Analítica',
  'module.integrations': 'Integraciones',
  'module.admin': 'Administración',
  'module.clients': 'Clientes',
  'module.requests': 'Pedidos',

  'module.overview.description': 'Escaneos, tasa de autenticación y alertas abiertas.',
  'module.map.description': 'Dónde se escanea y dónde aparece la anomalía.',
  'module.products.description': 'Catálogo, GTIN y tier de seguridad.',
  'module.batches.description': 'Estado del lote, activadas vs. escaneadas, recall.',
  'module.alerts.description': 'Bandeja, asignación, investigación y cierre.',
  'module.labels.description': 'Órdenes, rollos, stock y códigos.',
  'module.analytics.description': 'Tendencias, penetración por región y exportación.',
  'module.integrations.description': 'API keys, webhooks y logs de entrega.',
  'module.admin.description': 'Usuarios, roles, SSO y auditoría.',

  // ───────────────────────────────────────────────────── estados de vista
  'state.loading': 'Cargando…',
  'state.empty.title': 'No hay nada para mostrar',
  'state.empty.detail': 'Cuando haya datos van a aparecer acá.',
  'state.error.title': 'No se pudo cargar',
  'state.error.retry': 'Reintentar',
  'state.error.trace': 'Referencia para soporte',
  'state.loadMore': 'Cargar más',
  'state.loadingMore': 'Cargando más…',
  'state.allLoaded': 'No hay más resultados.',
  'state.refreshing': 'Actualizando…',

  // ──────────────────────────────────────────────────────────── permisos
  'guard.forbidden.title': 'No tenés permiso para ver esto',
  'guard.forbidden.detail':
    'Esta sección requiere el rol {required}. El tuyo es {actual}. Pedíselo al administrador de tu marca.',
  'guard.forbidden.back': 'Volver a la vista general',

  // ──────────────────────────────────────────────────────────── errores
  'error.network': 'No hay conexión con la API.',
  'error.network.hint': 'Revisá tu red. El dashboard reintenta solo.',
  'error.timeout': 'La consulta tardó demasiado.',
  'error.timeout.hint': 'Probá de nuevo o achicá el rango de fechas.',
  'error.unauthorized': 'Tu sesión no es válida.',
  'error.invalidCredentials': 'Usuario, contraseña o marca incorrectos.',
  'error.forbidden': 'Tu rol no permite esta operación.',
  'error.backofficeOnly': 'Comprar y generar etiquetas es de XYVONA.',
  'error.backofficeOnly.hint': 'Tu marca pide rollos desde Etiquetas › Pedidos y los recibe ya generados.',
  'error.notFound': 'No encontramos lo que buscabas.',
  'error.conflict': 'El dato cambió mientras lo editabas.',
  'error.validation': 'Hay campos con valores inválidos.',
  'error.rateLimited': 'Demasiadas peticiones. Esperá unos segundos.',
  'error.internal': 'Error interno de la plataforma.',
  'error.internal.hint': 'Pasale la referencia a soporte para que lo busquen en el log.',
  'error.invalidCursor': 'La paginación se desactualizó. Volvé a cargar la lista.',
  'error.unknown': 'Ocurrió un error inesperado.',

  // ──────────────────────────────────────────────────── vista de ejemplo
  'users.title': 'Usuarios',
  'users.description': 'Quién tiene acceso al dashboard de esta marca.',
  'users.column.name': 'Nombre',
  'users.column.email': 'Correo',
  'users.column.role': 'Rol',
  'users.column.status': 'Estado',
  'users.column.lastLogin': 'Último ingreso',
  'users.status.active': 'Activo',
  'users.status.inactive': 'Inactivo',
  'users.never': 'Nunca',
  'users.empty': 'Esta marca no tiene usuarios cargados.',
  'users.count': '{count} usuarios cargados',

  // ─────────────────────────── gestión de usuarios y roles (#70, para #45)
  'users.column.authMode': 'Autenticación',
  'users.column.actions': 'Acciones',
  'users.authMode.password': 'Contraseña',
  'users.authMode.oidc': 'Directorio (OIDC)',
  'users.new': 'Nuevo usuario',

  'users.filter.title': 'Buscar y filtrar',
  'users.filter.description':
    'Con doscientos usuarios, "cargar más" no es navegación. Los tres filtros los resuelve la API.',
  'users.filter.search': 'Buscar',
  'users.filter.searchHint': 'Busca en el correo y en el nombre. Enter para aplicar.',
  'users.filter.searchPlaceholder': 'maría, @labfarma…',
  'users.filter.searchAction': 'Buscar',
  'users.filter.role': 'Rol',
  'users.filter.allRoles': 'Todos los roles',
  'users.filter.status': 'Estado',
  'users.filter.allStatuses': 'Activos e inactivos',
  'users.filter.empty': 'Ningún usuario coincide con el filtro.',

  'users.form.title': 'Nuevo usuario',
  'users.form.description':
    'La contraseña la genera el servidor y se muestra una sola vez. El correo es único dentro de esta marca.',
  'users.form.email': 'Correo',
  'users.form.emailHint': 'Con este correo va a iniciar sesión, junto con el nombre de la marca.',
  'users.form.emailInvalid': 'Escribí un correo válido.',
  'users.form.name': 'Nombre',
  'users.form.nameHint': 'Cómo figura en la tabla y en las asignaciones de alertas.',
  'users.form.role': 'Rol',
  'users.form.submit': 'Crear usuario',
  'users.form.submitting': 'Creando…',

  'users.action.self': 'Vos',
  'users.action.federated': 'Clave en el directorio',
  'users.action.changeRole': 'Rol de {email}',
  'users.action.deactivate': 'Dar de baja',
  'users.action.reactivate': 'Reactivar',
  'users.action.reset': 'Restablecer contraseña',

  'users.confirm.role.title': 'Cambiar el rol',
  'users.confirm.role.detail':
    '{who} pasa a {role}. Se cierran sus sesiones abiertas y el cambio queda en la auditoría con tu nombre.',
  'users.confirm.role.accept': 'Sí, cambiar el rol',
  'users.confirm.deactivate.title': 'Dar de baja',
  'users.confirm.deactivate.detail':
    '{who} pierde el acceso ahora y se cierran sus sesiones. La cuenta no se borra: la auditoría tiene que seguir diciendo quién hizo qué.',
  'users.confirm.deactivate.accept': 'Sí, dar de baja',
  'users.confirm.reactivate.title': 'Reactivar',
  'users.confirm.reactivate.detail': '{who} vuelve a poder entrar con su contraseña anterior.',
  'users.confirm.reactivate.accept': 'Sí, reactivar',
  'users.confirm.reset.title': 'Restablecer la contraseña',
  'users.confirm.reset.detail':
    'Se genera una contraseña temporal para {who} y se cierran todas sus sesiones. La temporal se muestra una sola vez.',
  'users.confirm.reset.accept': 'Sí, restablecer',

  'users.temp.title': 'Contraseña temporal',
  'users.temp.detail':
    'Pasásela a {email} por un canal seguro. No se vuelve a mostrar: si se pierde, se restablece de nuevo.',
  'users.temp.sessions': 'Se cerraron {count} sesiones abiertas.',
  'users.temp.dismiss': 'Listo, ya la copié',

  // ──────────────────────────────────────────────────────── placeholder
  // ─────────────────────────────────── activación en planta (#41)
  'module.activation': 'Activación',
  'module.activation.description': 'Línea de empaque: activar unidades y ver qué se rechaza.',

  'activation.title': 'Activación en planta',
  'activation.description':
    'Simula la línea de empaque: leer códigos, activarlos contra un lote de producción y ver qué se rechaza.',
  'activation.rule':
    'Una unidad no autentica hasta estar activada. Las etiquetas llegan de fábrica ya registradas: si nacieran válidas, un rollo robado del depósito autenticaría producto falsificado. Sólo se activa desde RECIBIDA, y siempre con el código impreso — nunca con un identificador interno.',

  'activation.console.title': 'Consola de línea',
  'activation.console.description':
    'Un código por renglón, como los deja un lector. También se aceptan separados por coma, punto y coma o espacio.',

  'activation.field.batch': 'Lote de producción',
  'activation.field.batchHint': 'A este lote quedan vinculadas las unidades: GTIN, lote y vencimiento.',
  'activation.field.batchPlaceholder': 'Elegí un lote',
  'activation.field.batchBlocked': 'no admite activaciones',
  'activation.field.line': 'Línea',
  'activation.field.lineHint': 'Queda registrada en el lote de activación.',
  'activation.field.key': 'Clave de idempotencia',
  'activation.field.keyHint':
    'Reenviar la misma tanda con esta clave no activa dos veces. Reusar la clave con otro contenido es un error y la API lo rechaza.',
  'activation.field.codes': 'Códigos leídos',
  'activation.field.codesPlaceholder': '07VZFEF9G08Y8B57K165',
  'activation.field.codesHint': 'Hasta 50.000 etiquetas por tanda.',

  'activation.scan.ready': '{count} listos',
  'activation.scan.duplicates': '{count} repetidos',
  'activation.scan.unreadable': '{count} no legibles',
  'activation.scan.unreadableHint': 'No entran en el formato del código y no se envían:',

  'activation.submit': 'Activar tanda',
  'activation.missing.batch': 'Para habilitar, elegí primero el lote.',
  'activation.missing.codes': 'Para habilitar, pegá los códigos leídos en el campo de arriba.',
  'activation.missing.key': 'Para habilitar, falta la clave de idempotencia.',
  'activation.submitting': 'Activando…',
  'activation.submit.offline': 'Guardar en la cola',

  'activation.offline.on': 'Sin red',
  'activation.offline.off': 'En línea',
  'activation.offline.disconnect': 'Simular corte de red',
  'activation.offline.reconnect': 'Restablecer la red',
  'activation.offline.explain':
    'La línea no para cuando se cae la red: acumula y reenvía. La misma tanda reenviada no activa dos veces.',
  'activation.offline.stored': 'La tanda quedó en la cola local. Se envía al restablecer la red.',
  'activation.offline.replaced':
    'Ya había una tanda con esa clave en la cola y quedó reemplazada por ésta.',
  'activation.offline.queueTitle': 'Cola de la línea',
  'activation.offline.queueDescription': '{runs} tandas · {codes} etiquetas esperando red.',
  'activation.offline.queuedAt': 'Cerrada',
  'activation.offline.sync': 'Sincronizar',
  'activation.offline.syncing': 'Sincronizando…',
  'activation.offline.discard': 'Descartar',

  'activation.result.replayed': 'Ya recibida',
  'activation.result.fresh': 'Activada ahora',
  'activation.result.replayedHint':
    'Esta tanda ya se había recibido: la respuesta es la guardada y no se activó nada de nuevo. Así es como un reenvío después de un corte de red no duplica.',
  'activation.result.requested': 'Enviadas',
  'activation.result.activated': 'Activadas',
  'activation.result.already': 'Ya activadas',
  'activation.result.rejected': 'Rechazadas',
  'activation.result.elapsed': 'Resuelto en {ms} ms.',
  'activation.result.cacheStale':
    'No se pudo invalidar el cache de verificación. Las unidades están activadas; el consumidor puede ver el estado anterior unos minutos. Reenviar la tanda reintenta la invalidación.',
  'activation.result.verifyHint':
    'Verificá una de estas etiquetas como consumidor: desde ahora tiene que decir genuino.',
  'activation.result.resend': 'Reenviar igual',
  'activation.result.open': 'Ver rechazos',

  'activation.rejects.title': 'Etiquetas rechazadas',
  'activation.rejects.description': 'Sacalas de la caja antes de que salgan de planta.',
  'activation.rejects.sample':
    'Se muestran {shown} de {total}. El detalle completo queda auditado en la tanda.',
  'activation.rejects.column.code': 'Código',
  'activation.rejects.column.outcome': 'Motivo',
  'activation.rejects.column.existingBatch': 'Lote actual',
  'activation.rejects.column.existingStatus': 'Estado',

  'activation.outcome.activated': 'Activada',
  'activation.outcome.alreadyActivated': 'Ya estaba activada',
  'activation.outcome.duplicate': 'Repetida en la tanda',
  'activation.outcome.wrongState': 'Estado inválido',
  'activation.outcome.otherBatch': 'De otro lote',
  'activation.outcome.terminal': 'Anulada, robada o retirada',
  'activation.outcome.unknown': 'Sin unidad',
  'activation.outcome.invalidCode': 'Código inválido',

  'activation.why.activated': 'Pasó de RECIBIDA a ACTIVADA: desde ahora autentica.',
  'activation.why.alreadyActivated':
    'Ya estaba activada contra este mismo lote. Es lo que hace idempotente al reenvío.',
  'activation.why.duplicate': 'El mismo código vino dos veces en la tanda.',
  'activation.why.wrongState': 'No llegó a RECIBIDA: la etiqueta no pasó por el depósito.',
  'activation.why.otherBatch': 'Ya estaba activada contra otro lote de producción.',
  'activation.why.terminal': 'Anulada, robada, retirada o destruida: no vuelve al circuito.',
  'activation.why.unknown': 'El código verifica, pero no hay ninguna unidad de esta marca con ese id.',
  'activation.why.invalidCode': 'El HMAC no verifica: código forjado o mal leído.',

  'activation.runs.title': 'Tandas recibidas',
  'activation.runs.description': 'Cada envío de la línea, con lo que activó y lo que rechazó.',
  'activation.runs.empty': 'Todavía no se activó ninguna tanda.',
  'activation.runs.emptyHint': 'Elegí un lote de producción, pegá los códigos leídos y activá.',
  'activation.runs.column.received': 'Recibida',
  'activation.runs.column.requested': 'Enviadas',

  'activation.detail.back': '← Volver a la activación',
  'activation.detail.description': 'Lote de producción #{batch} · línea {line}',
  'activation.detail.rejected': '{count} rechazadas',
  'activation.detail.clean': 'Sin rechazos',
  'activation.detail.clientSentAt': 'Reloj de la línea',
  'activation.detail.duration': 'Duración',
  'activation.detail.durationValue': '{ms} ms',

  // ───────────────── modos A y C de activación (#79) y excepciones (#80)
  'activation.modes.link': 'Modos A y C · rango y en línea',
  'activation.modes.title': 'Rango y línea',
  'activation.modes.description':
    'Los otros dos mecanismos de activación, para clientes que no usan lector de tanda.',
  'activation.modes.back': '← Volver a la tanda',
  'activation.modes.rule':
    'Tres modos, un solo resultado para el consumidor: una unidad activada por cualquiera de ellos autentica igual, y los tres dejan la misma entrada en el registro de auditoría. Lo que cambia es quién puede usar cuál.',
  'activation.modes.badge.range': 'Modo A',
  'activation.modes.badge.line': 'Modo C',
  'activation.modes.badge.exceptions': 'Terminal',

  'activation.range.title': 'Modo A · activar por rango',
  'activation.range.description':
    'Para el productor sin automatización: «rollo 47, series 1.000.000 a 1.010.000 → lote L2026-0801».',
  'activation.range.from': 'Serie desde',
  'activation.range.to': 'Serie hasta',
  'activation.range.width': '{count} unidades en el rango. El rango tiene que salir de un rollo emitido: si no, la API lo rechaza.',
  'activation.range.submit': 'Activar el rango',
  'activation.range.order':
    'Primero se anulan las excepciones declaradas y después se activa el rango, en la misma transacción. Al revés no se podría: una unidad ya activada no se anula.',
  'activation.range.inRange': 'En el rango',
  'activation.range.exceptionRef': 'Registro de excepciones #{id}.',

  'activation.exceptions.title': 'Registro de excepciones',
  'activation.exceptions.description':
    'Marcar etiquetas como anuladas: dañadas, mal aplicadas, salteadas o descartadas en impresión.',
  'activation.exceptions.legend': 'Excepciones de este rango',
  'activation.exceptions.rule':
    'Obligatorio. Activar un rango sin declarar los descartes deja unidades activadas sin producto real: autentican como genuinas y no corresponden a nada. Si el rollo se aplicó entero, eso también se firma.',
  'activation.exceptions.terminal':
    'Anular es terminal y no se deshace. Sólo se puede anular una etiqueta que todavía no se activó; para una que ya está en la calle van el retiro del lote o la destrucción documentada.',
  'activation.exceptions.mode.none': 'Sin descartes (lo firmo)',
  'activation.exceptions.mode.ranges': 'Por tramos de series',
  'activation.exceptions.mode.codes': 'Por códigos leídos',
  'activation.exceptions.reason': 'Motivo',
  'activation.exceptions.note': 'Detalle',
  'activation.exceptions.noteHint': 'Obligatorio si el motivo es «otro».',
  'activation.exceptions.ranges': 'Tramos descartados',
  'activation.exceptions.rangesPlaceholder': '1000010-1000024',
  'activation.exceptions.rangesHint': '{tramos} tramos · {count} unidades. Un tramo por renglón: «1000-1010» o «1000».',
  'activation.exceptions.codes': 'Códigos descartados',
  'activation.exceptions.invalidRanges': 'Hay renglones que no son un tramo y no se envían: {list}',
  'activation.exceptions.outside':
    'Hay tramos que caen fuera del rango que se va a activar. Anularían etiquetas de otro rollo.',
  'activation.exceptions.byRange': 'Anular por rango de series en vez de por códigos',
  'activation.exceptions.voided': 'Anuladas',
  'activation.exceptions.submit': 'Registrar y anular',
  'activation.exceptions.rejects': 'No se pudieron anular',

  'activation.reason.damaged': 'Etiqueta dañada',
  'activation.reason.misapplied': 'Mal aplicada',
  'activation.reason.skipped': 'Salteada por el aplicador',
  'activation.reason.printDiscard': 'Descartada en impresión',
  'activation.reason.orderCancelled': 'Orden cancelada',
  'activation.reason.other': 'Otro (explicar)',

  'activation.voidOutcome.voided': 'Anulada',
  'activation.voidOutcome.alreadyVoided': 'Ya estaba anulada',
  'activation.voidOutcome.activated': 'Ya activada: no se anula',
  'activation.voidOutcome.terminal': 'Robada, retirada o destruida',
  'activation.voidOutcome.wrongState': 'Estado que no admite anulación',
  'activation.voidOutcome.unknown': 'Sin unidad',
  'activation.voidOutcome.invalidCode': 'Código inválido',

  'activation.line.title': 'Modo C · activar en línea',
  'activation.line.description':
    'El sistema de visión de la línea, unidad por unidad o por paquete.',
  'activation.line.code': 'Código leído',
  'activation.line.codeHint': 'Enter activa. Varios códigos separados por espacio son un paquete.',
  'activation.line.submit': 'Activar',
  'activation.line.p50': 'p50 {ms} ms',
  'activation.line.samples': '{count} lecturas medidas',
  'activation.line.budget':
    'Una línea a 300 unidades por minuto son 5 por segundo. Si la API tarda más que eso, la línea baja su velocidad — y eso el cliente no lo acepta.',
  'activation.line.failSafe':
    'Si XYVONA no responde, el sistema de visión encola y la línea sigue. Nunca al revés.',
  'activation.detail.notFound': 'Esa tanda de activación no existe.',

  'activation.conflicts.title': 'Rechazos auditados',
  'activation.conflicts.description':
    'La traza que pide un auditor: qué se intentó activar, contra qué lote y en qué estado estaba realmente la unidad.',
  'activation.conflicts.empty': 'Esta tanda no tuvo rechazos.',
  'activation.conflicts.emptyHint':
    'Todas las etiquetas enviadas estaban en estado RECIBIDA y se activaron.',
  'activation.conflicts.column.case': 'Caso',
  'activation.conflicts.column.reason': 'Motivo',
  'activation.conflicts.column.attempted': 'Lote intentado',
  'activation.conflicts.column.existing': 'Lote actual',
  'activation.conflicts.column.status': 'Estado de la unidad',
  'activation.conflicts.column.when': 'Registrado',

  'activation.unitStatus.created': 'Creada',
  'activation.unitStatus.printed': 'Impresa',
  'activation.unitStatus.shipped': 'Despachada',
  'activation.unitStatus.received': 'Recibida',
  'activation.unitStatus.activated': 'Activada',
  'activation.unitStatus.inMarket': 'En mercado',
  'activation.unitStatus.verified': 'Verificada',
  'activation.unitStatus.voided': 'Anulada',
  'activation.unitStatus.stolen': 'Robada',
  'activation.unitStatus.recalled': 'Retirada',
  'activation.unitStatus.destroyed': 'Destruida',

  // ────────────────────────────── bandeja de alertas y casos (#42)
  'alerts.inbox.title': 'Bandeja de alertas',
  'alerts.inbox.description':
    'Cada alerta muestra la evidencia que la disparó. Se opera con el teclado: ? para la ayuda.',
  'alerts.case': 'Caso #{id}',
  'alerts.unassigned': 'Sin asignar',
  'alerts.yes': 'sí',
  'alerts.no': 'no',
  'alerts.column.batch': 'Lote',

  'alerts.summary.open': 'Abiertas',
  'alerts.summary.unassigned': 'Sin dueño',
  'alerts.summary.mine': 'Mías',

  'alerts.filter.status': 'Estado',
  'alerts.filter.severity': 'Severidad',
  'alerts.filter.severityFrom': '{severity} o más',
  'alerts.filter.type': 'Regla',
  'alerts.filter.assignment': 'Asignación',
  'alerts.filter.sort': 'Orden',
  'alerts.filter.sortSeverity': 'Más graves primero',
  'alerts.filter.sortRecent': 'Más recientes primero',
  'alerts.filter.window': 'Período',
  'alerts.filter.window1': 'Últimas 24 h',
  'alerts.filter.window7': 'Últimos 7 días',
  'alerts.filter.window30': 'Últimos 30 días',
  'alerts.filter.window90': 'Últimos 90 días',
  'alerts.filter.windowAll': 'Todo',
  'alerts.filter.any': 'Todas',
  'alerts.filter.mine': 'Asignadas a mí',
  'alerts.filter.unassigned': 'Sin dueño',
  'alerts.filter.reset': 'Limpiar filtros',

  'alerts.list.title': 'Casos',
  'alerts.list.description': 'Ordenados por severidad: primero lo que hay que atender.',
  'alerts.list.count': '{count} cargados',
  'alerts.list.empty': 'No hay alertas con estos filtros.',
  'alerts.list.emptyHint': 'Ampliá el período o sacá el filtro de estado.',

  'alerts.severity.critical': 'Crítica',
  'alerts.severity.high': 'Alta',
  'alerts.severity.medium': 'Media',
  'alerts.severity.low': 'Baja',
  'alerts.severity.info': 'Informativa',

  'alerts.status.open': 'Abierta',
  'alerts.status.investigating': 'En investigación',
  'alerts.status.confirmed': 'Confirmada',
  'alerts.status.dismissed': 'Descartada',

  'alerts.type.notActivated': 'Código no activado',
  'alerts.type.stolenRoll': 'Rollo robado',
  'alerts.type.recalled': 'Lote retirado',
  'alerts.type.secretReuse': 'Reúso del código oculto',
  'alerts.type.impossibleTravel': 'Viaje imposible',
  'alerts.type.territoryDiversion': 'Desvío de territorio',
  'alerts.type.geoCluster': 'Cluster fuera de zona',
  'alerts.type.duplicateScan': 'Escaneo duplicado',
  'alerts.type.velocitySpike': 'Pico de escaneos',
  'alerts.type.lowDeviceDiversity': 'Baja diversidad de dispositivos',
  'alerts.type.datacenterAsn': 'ASN de datacenter',

  'alerts.resolution.counterfeit': 'Falsificación confirmada',
  'alerts.resolution.diversion': 'Desvío confirmado',
  'alerts.resolution.theft': 'Robo confirmado',
  'alerts.resolution.falsePositive': 'Falso positivo',
  'alerts.resolution.consumerError': 'Error del consumidor',
  'alerts.resolution.testScan': 'Escaneo de prueba',
  'alerts.resolution.noAction': 'Sin acción',

  'alerts.evidence.title': 'Evidencia',
  'alerts.evidence.empty': 'La regla no dejó campos de evidencia.',
  'alerts.evidence.hidden': 'oculto: identificador interno',
  'alerts.evidence.unknownPlace': 'lugar desconocido',

  'alerts.travel.first': 'Primer escaneo',
  'alerts.travel.second': 'Segundo escaneo',
  'alerts.travel.distance': 'Distancia',
  'alerts.travel.elapsed': 'Tiempo entre escaneos',
  'alerts.travel.speed': 'Velocidad implícita',
  'alerts.travel.limit': 'Umbral de la regla',
  'alerts.travel.km': '{value} km',
  'alerts.travel.kmh': '{value} km/h',
  'alerts.travel.minutes': '{value} min',
  'alerts.travel.plotLabel': 'Los dos escaneos: {from} y {to}',
  'alerts.travel.approximate': 'Ubicación aproximada, a nivel ciudad.',

  'alerts.detail.none': 'Ningún caso seleccionado',
  'alerts.detail.noneHint': 'Elegí uno de la lista o apretá j para empezar por el más grave.',
  'alerts.detail.missing': 'Ese caso no existe o es de otra marca.',
  'alerts.detail.opened': 'Abierto el {when}',
  'alerts.detail.context': 'Contexto',
  'alerts.detail.territory': 'Territorio',
  'alerts.detail.relatedUnit': 'Otras alertas de la unidad',
  'alerts.detail.relatedBatch': 'Otras alertas del lote',
  'alerts.detail.assignee': 'Asignado a',
  'alerts.detail.resolution': 'Motivo de cierre',
  'alerts.detail.actions': 'Gestión del caso',
  'alerts.detail.readOnly':
    'Tu rol lee la bandeja pero no la opera. Asignar, cerrar y anotar requieren rol de analista.',

  'alerts.action.assignSelf': 'Asignarme',
  'alerts.action.unassign': 'Liberar',
  'alerts.action.assignTo': 'Asignar a',
  'alerts.action.moveTo': 'Pasar a {status}',
  'alerts.action.note': 'Agregar nota',

  'alerts.notes.title': 'Notas de investigación',
  'alerts.notes.empty': 'Todavía no hay notas.',
  'alerts.history.title': 'Historial del caso',
  'alerts.history.hint': 'Sale de la auditoría encadenada por hash, la misma que mira un auditor.',
  'alerts.history.empty': 'Sin movimientos registrados.',

  'alerts.dialog.statusTitle': 'Pasar el caso a {status}',
  'alerts.dialog.noteTitle': 'Agregar una nota',
  'alerts.dialog.resolution': 'Motivo de cierre',
  'alerts.dialog.note': 'Nota',
  'alerts.dialog.noteOptional': 'Nota (opcional)',
  'alerts.dialog.submit': 'Guardar',
  'alerts.dialog.saving': 'Guardando…',
  'alerts.dialog.cancel': 'Cancelar',
  'alerts.dialog.submitHint': 'Ctrl+Enter guarda · Esc cancela. Las notas no se editan ni se borran.',

  'alerts.shortcuts.title': 'Atajos de teclado',
  'alerts.shortcuts.open': 'Atajos (?)',
  'alerts.shortcut.next': 'Siguiente caso',
  'alerts.shortcut.previous': 'Caso anterior',
  'alerts.shortcut.first': 'Primero / último',
  'alerts.shortcut.last': 'Último caso',
  'alerts.shortcut.investigate': 'Marcar en investigación',
  'alerts.shortcut.confirm': 'Confirmar el caso',
  'alerts.shortcut.dismiss': 'Descartar el caso',
  'alerts.shortcut.reopen': 'Reabrir el caso',
  'alerts.shortcut.refresh': 'Actualizar la bandeja',
  'alerts.shortcut.help': 'Esta ayuda',
  'alerts.shortcut.close': 'Cerrar el diálogo',

  'placeholder.title': 'Módulo en construcción',
  'placeholder.detail':
    'Esta sección se implementa en el issue #{issue}. El andamio —sesión, navegación, cliente de API y estados— ya está listo.',
  'placeholder.issue': 'Issue',

  'notFound.title': 'Esa página no existe',
  'notFound.detail': 'Puede que el enlace esté viejo.',

  // ───────────────────────────────────── confirmación de acciones sensibles
  'confirm.cancel': 'Cancelar',
  'confirm.accept': 'Confirmar',
  'confirm.typeToConfirm': 'Escribí {word} para confirmar.',

  // ─────────────────────────────── #45 · administración: roles y auditoría
  'admin.sections': 'Secciones de administración',
  'admin.tab.users': 'Usuarios',
  'admin.tab.audit': 'Auditoría',
  'admin.tab.portal': 'Portal del consumidor',

  'users.roles.title': 'Qué puede hacer cada rol',
  'users.roles.admin': 'Administra usuarios y llaves, y lee la auditoría.',
  'users.roles.analyst': 'Opera lotes, alertas y casos; pide exportaciones y reportes.',
  'users.roles.viewer': 'Sólo lectura: tableros, lotes y bandeja de alertas.',
  'users.roles.audited':
    'Toda alta, baja o cambio de rol queda en la auditoría, con quién lo hizo y cuándo.',
  'users.roles.seeAudit': 'Ver los cambios de acceso',

  'audit.title': 'Auditoría',
  'audit.description': 'Quién hizo qué, cuándo, y si el registro está intacto.',
  'audit.chain.title': 'Integridad de la cadena',
  'audit.chain.checking': 'Recalculando la cadena…',
  'audit.chain.ok': 'Cadena íntegra',
  'audit.chain.broken': 'Cadena rota',
  'audit.chain.okDetail': 'Se recalcularon {entries} entradas y todas cierran con la anterior.',
  'audit.chain.brokenDetail':
    '{broken} de {entries} entradas no cierran. La primera es la #{seq}.',
  'audit.chain.reason': 'Motivo',
  'audit.chain.reason.contenido_alterado':
    'El contenido de la entrada cambió después de escribirse.',
  'audit.chain.reason.enlace_roto': 'La entrada no engancha con el hash de la anterior.',
  'audit.chain.reason.falta_una_fila': 'Falta una entrada en la secuencia.',
  'audit.chain.reason.genesis_incorrecto': 'La primera entrada de la cadena no es la esperada.',
  'audit.chain.reason.hash_version_desconocida':
    'La entrada usa una versión de hash que este servidor no conoce.',
  'audit.chain.reason.other': 'El servidor lo informa como: {reason}',
  'audit.chain.anchor': 'Ancla',
  'audit.chain.anchorHint':
    'Anotá la entrada y su hash. Que ese par siga igual en la próxima visita prueba que nadie reescribió el pasado.',
  'audit.chain.entries': 'Entradas',
  'audit.chain.elapsed': 'Tardó',
  'audit.chain.recheck': 'Verificar de nuevo',
  'audit.chain.rechecking': 'Verificando…',
  'audit.chain.formula': 'Fórmula del hash',
  'audit.chain.empty': 'Esta marca todavía no tiene entradas de auditoría.',

  'audit.filter.entityType': 'Tipo de entidad',
  'audit.filter.entityTypeHint': 'Por ejemplo: batch, alert, api_key, user.',
  'audit.filter.action': 'Acción',
  'audit.filter.apply': 'Aplicar',
  'audit.filter.clear': 'Limpiar',
  'audit.column.seq': 'Entrada',
  'audit.column.when': 'Cuándo',
  'audit.column.action': 'Acción',
  'audit.column.entity': 'Entidad',
  'audit.column.actor': 'Quién',
  'audit.entry.system': 'Sistema',
  'audit.entry.details': 'Detalle de la entrada {seq}',
  'audit.entry.payload': 'Contenido firmado',
  'audit.entry.prevHash': 'Hash anterior',
  'audit.entry.rowHash': 'Hash de la entrada',
  'audit.entry.hashVersion': 'Versión de hash',
  'audit.entry.broken': 'Acá se rompe la cadena',
  'audit.empty': 'No hay entradas con esos filtros.',

  // ────────────────────────────────────── #46 · portal del consumidor
  'portal.title': 'Portal del consumidor',
  'portal.description': 'La cuenta con la que el comprador guarda los códigos que verificó.',
  'portal.state.enabled': 'Habilitado',
  'portal.state.disabled': 'No disponible',
  'portal.brand': 'Marca',
  'portal.unavailable.title': 'Esta marca no tiene portal del consumidor',
  'portal.reason.industry_restricted':
    'Es una marca farmacéutica. Guardar qué medicamentos escaneó una persona identificada es un dato de salud —categoría especial bajo GDPR, LGPD y la Ley 6534/2020—, así que el portal no existe para este tipo de marca (ADR-011). No está apagado: los endpoints responden que no existe.',
  'portal.reason.disabled_by_tenant':
    'La marca todavía no habilitó el portal. Viene apagado por defecto.',
  'portal.reason.unknown': 'El servidor no ofrece el portal para esta marca.',
  'portal.preview.title': 'Lo que ve el comprador',
  'portal.preview.detail':
    'Esta es la pantalla del portal contra la API real. La sesión es del comprador, no la tuya: no se guarda al recargar y no comparte nada con tu sesión del dashboard.',
  'portal.login.title': 'Ingresar',
  'portal.login.submit': 'Ingresar',
  'portal.register.title': 'Crear cuenta',
  'portal.register.submit': 'Crear la cuenta',
  'portal.field.email': 'Correo',
  'portal.field.password': 'Contraseña',
  'portal.field.passwordHint': 'Diez caracteres como mínimo.',
  'portal.field.name': 'Nombre (opcional)',
  'portal.notice':
    'Al crear la cuenta se guarda el consentimiento junto con la versión del texto de privacidad ({version}).',
  'portal.session.logout': 'Cerrar la sesión del portal',
  'portal.account.title': 'Mi cuenta',
  'portal.account.email': 'Correo',
  'portal.account.name': 'Nombre',
  'portal.account.created': 'Cuenta creada',
  'portal.codes.title': 'Mis códigos',
  'portal.codes.description': 'Los códigos que verificaste, con la fecha en que los guardaste.',
  'portal.codes.empty': 'Todavía no guardaste ningún código.',
  'portal.codes.column.ref': 'Referencia',
  'portal.codes.column.label': 'Etiqueta',
  'portal.codes.column.result': 'Resultado',
  'portal.codes.column.saved': 'Guardado',
  'portal.codes.column.actions': 'Acciones',
  'portal.codes.add': 'Guardar un código',
  'portal.codes.field.code': 'Código del QR',
  'portal.codes.field.label': 'Etiqueta (opcional)',
  'portal.codes.save': 'Guardar',
  'portal.codes.saving': 'Guardando…',
  'portal.codes.delete': 'Borrar',
  'portal.codes.delete.title': 'Borrar este código',
  'portal.codes.delete.detail':
    'Se borra de tu lista. La verificación no se borra: lo que desaparece es que esté asociada a tu cuenta.',
  'portal.result.genuine': 'Verificado',
  'portal.result.already_seen': 'Ya verificado antes',
  'portal.result.recalled': 'Lote retirado',
  'portal.rights.title': 'Tus datos',
  'portal.rights.export': 'Descargar mis datos',
  'portal.rights.exportHint':
    'Un JSON con la cuenta, los códigos guardados y los consentimientos. Es el derecho de acceso.',
  'portal.rights.delete': 'Borrar mi cuenta',
  'portal.rights.deleteHint':
    'Borra la cuenta, sus sesiones, sus consentimientos y los códigos guardados. Es el derecho de supresión.',
  'portal.delete.title': 'Borrar la cuenta',
  'portal.delete.detail':
    'Se borra la cuenta y, con ella, la asociación entre esa persona y los códigos que verificó. No se puede deshacer.',
  'portal.delete.confirmWord': 'BORRAR',
  'portal.delete.done':
    'La cuenta se borró. Ya no queda ninguna asociación entre esa persona y los códigos que había guardado.',

  // ───────────────────────────────── #47 · reportes formales y exportación
  'module.reports': 'Reportes',
  'module.reports.description': 'Reportes formales del período, firmados y exportables.',
  'reports.title': 'Reportes formales',
  'reports.description':
    'Un documento del período, con quién lo generó y cuándo. Distinto de la analítica interactiva: esto es lo que se archiva.',
  'reports.form.period': 'Período',
  'reports.form.from': 'Desde',
  'reports.form.to': 'Hasta',
  'reports.form.generate': 'Generar el reporte',
  'reports.form.regenerate': 'Generar de nuevo',
  'reports.period.last7': 'Últimos 7 días',
  'reports.period.last30': 'Últimos 30 días',
  'reports.period.last90': 'Últimos 90 días',
  'reports.period.custom': 'Personalizado',
  'reports.form.invalidRange': 'La fecha de inicio tiene que ser anterior a la de fin.',
  'reports.doc.title': 'Reporte de autenticación de producto',
  'reports.doc.brand': 'Marca',
  'reports.doc.period': 'Período',
  'reports.doc.generatedBy': 'Generado por',
  'reports.doc.generatedAt': 'Generado el',
  'reports.doc.reference': 'Referencia',
  'reports.doc.kpis': 'Resumen del período',
  'reports.doc.totalScans': 'Escaneos',
  'reports.doc.genuine': 'Verificados',
  'reports.doc.suspicious': 'Sospechosos',
  'reports.doc.suspiciousRate': 'Tasa de sospecha',
  'reports.doc.openAlerts': 'Alertas abiertas',
  'reports.doc.criticalAlerts': 'Alertas críticas abiertas',
  'reports.doc.byResult': 'Escaneos por resultado',
  'reports.doc.result': 'Resultado',
  'reports.doc.scans': 'Escaneos',
  'reports.doc.share': 'Participación',
  'reports.doc.countries': 'Países con más escaneos',
  'reports.doc.country': 'País',
  'reports.doc.daily': 'Actividad diaria',
  'reports.doc.day': 'Día',
  'reports.doc.units': 'Unidades',
  'reports.doc.refreshed': 'Datos agregados al {when}.',
  'reports.doc.privacy':
    'Los datos de este reporte son agregados. No contiene datos personales de consumidores.',
  'reports.doc.footer':
    'Documento generado por XYVONA. Su contenido se puede contrastar con el registro de auditoría del período.',
  'reports.doc.empty': 'No hubo escaneos en el período elegido.',
  'reports.print': 'Imprimir o guardar en PDF',
  'reports.result.genuine': 'Verificado',
  'reports.result.already_seen': 'Ya escaneado',
  'reports.result.not_activated': 'Sin activar',
  'reports.result.invalid': 'No verificable',
  'reports.result.stolen': 'Rollo robado',
  'reports.result.recalled': 'Lote retirado',
  'reports.result.out_of_territory': 'Fuera de territorio',
  'reports.export.title': 'Exportaciones',
  'reports.export.description':
    'El detalle en CSV, para archivar o cruzar con otro sistema. Cada pedido y cada descarga queda en la auditoría.',
  'reports.export.kind': 'Contenido',
  'reports.export.format': 'Formato',
  'reports.export.format.csv': 'CSV (UTF-8)',
  'reports.export.format.csv_excel': 'CSV para Excel',
  'reports.export.kind.alerts': 'Alertas',
  'reports.export.kind.batches': 'Lotes',
  'reports.export.kind.units': 'Unidades',
  'reports.export.kind.scans_daily': 'Escaneos por día (agregado)',
  'reports.export.kind.geo_cells': 'Celdas geográficas (agregado)',
  'reports.export.request': 'Pedir la exportación',
  'reports.export.requesting': 'Pidiendo…',
  'reports.export.column.kind': 'Contenido',
  'reports.export.column.status': 'Estado',
  'reports.export.column.rows': 'Filas',
  'reports.export.column.requestedBy': 'Pedido por',
  'reports.export.column.created': 'Pedido el',
  'reports.export.column.actions': 'Acciones',
  'reports.export.status.queued': 'En cola',
  'reports.export.status.running': 'Generando',
  'reports.export.status.done': 'Listo',
  'reports.export.status.failed': 'Falló',
  'reports.export.status.expired': 'Vencido',
  'reports.export.download': 'Descargar',
  'reports.export.downloading': 'Descargando…',
  'reports.export.empty': 'Todavía no se pidió ninguna exportación.',

  // ──────────────────────────────────────────────── mapa de calor (#43)
  'map.title': 'Mapa de calor',
  'map.description':
    'Celdas geográficas agregadas por el servidor. Nunca escaneos individuales.',
  'map.loading': 'Cargando el mapa…',
  'map.cells': '{count} celdas',
  'map.canvas.label':
    'Mapa de calor de escaneos por celda geográfica. La misma información está en la tabla que sigue.',
  'map.recenter': 'Reencuadrar',
  'map.empty': 'No hay celdas para estos filtros',
  'map.empty.detail':
    'Probá con un período más largo, con una precisión menor o bajando el umbral de escaneos por celda.',
  'map.truncated':
    'La respuesta llegó al tope de celdas. Bajá la precisión para ver el período completo.',

  'map.filter.period': 'Período',
  'map.filter.days': '{days} días',
  'map.filter.layer': 'Capa',
  'map.filter.precision': 'Detalle',
  'map.filter.minScans': 'Mínimo por celda',
  'map.filter.alertOverlay': 'Superponer alertas',

  'map.layer.all': 'Todos los escaneos',
  'map.layer.genuine': 'Genuinos',
  'map.layer.suspicious': 'Sospechosos',
  'map.layer.notActivated': 'Sin activar',
  'map.layer.stolen': 'Rollo robado',
  'map.layer.recalled': 'Lote retirado',
  'map.layer.outOfTerritory': 'Fuera de territorio',

  'map.precision.3': 'País (~156 km)',
  'map.precision.4': 'Región (~39 km)',
  'map.precision.5': 'Ciudad (~4,9 km)',
  'map.precision.6': 'Barrio (~1,2 km)',

  'map.legend.title': 'Escaneos por celda',
  'map.legend.alerts': 'Celdas con escaneos sospechosos',

  'map.stat.scans': 'Escaneos del período',
  'map.stat.cells': 'Celdas dibujadas',
  'map.stat.suspicious': 'Escaneos sospechosos',
  'map.stat.cellSize': 'Tamaño de celda',
  'map.stat.cellSizeValue': '{width} × {height} km',

  'map.footer.window': 'Del {from} al {to}',
  'map.footer.refreshed': 'Agregados al {when}',
  'map.footer.queryMs': 'Consulta: {ms} ms',

  'map.table.title': 'Celdas del período',
  'map.table.description':
    'La misma información del mapa, navegable con el teclado. Cada fila enfoca su celda.',
  'map.table.caption': 'Celdas geográficas ordenadas por cantidad de escaneos.',
  'map.table.cell': 'Celda',
  'map.table.country': 'País',
  'map.table.scans': 'Escaneos',
  'map.table.share': 'Del total',
  'map.table.suspicious': 'Sospechosos',
  'map.table.peakUnits': 'Unidades (día pico)',
  'map.table.focus': 'Enfocar la celda en {lat}, {lon}',
  'map.table.more': 'Mostrar más ({shown} de {total})',
  'map.table.clearSelection': 'Quitar selección',

  // ────────────────────────────────── analítica y exportaciones (#44)
  'analytics.title': 'Analítica',
  'analytics.description':
    'Tendencias, tasa de autenticación y penetración por región del período elegido.',
  'analytics.period': 'Período',
  'analytics.days': '{days} días',
  'analytics.refresh': 'Recalcular',
  'analytics.refreshing': 'Recalculando…',
  'analytics.refreshed': 'Recalculado: {rows} filas en {ms} ms.',
  'analytics.window': 'Del {from} al {to}',
  'analytics.refreshedAt': 'Agregados al {when}',

  'analytics.kpi.scans': 'Escaneos',
  'analytics.kpi.authRate': 'Tasa de autenticación',
  'analytics.kpi.authRateHint': '{genuine} escaneos genuinos',
  'analytics.kpi.suspiciousRate': 'Tasa de sospecha',
  'analytics.kpi.suspiciousHint': '{scans} escaneos sospechosos',
  'analytics.kpi.openAlerts': 'Alertas abiertas',
  'analytics.kpi.criticalHint': '{count} críticas',

  'analytics.chart.peak': 'máx. {value}',
  'analytics.chart.empty': 'Sin escaneos en este período.',
  'analytics.chart.emptySuspicious': 'Ningún escaneo sospechoso en el período.',
  'analytics.trend.title': 'Tendencia',
  'analytics.trend.description':
    'Escaneos por tramo. El tramo se agranda solo cuando el período es largo, así el gráfico dibuja siempre la misma cantidad de puntos.',
  'analytics.trend.scans': 'Escaneos',
  'analytics.trend.scansSummary': 'Escaneos por tramo: {points} puntos, {total} en total.',
  'analytics.trend.suspicious': 'Escaneos sospechosos',
  'analytics.trend.suspiciousSummary':
    'Escaneos sospechosos por tramo: {points} puntos, {total} en total.',
  'analytics.trend.showData': 'Ver los números',
  'analytics.trend.tableCaption': 'Escaneos por tramo del período.',
  'analytics.trend.column.period': 'Tramo',
  'analytics.trend.column.scans': 'Escaneos',
  'analytics.trend.column.genuine': 'Genuinos',
  'analytics.trend.column.suspicious': 'Sospechosos',
  'analytics.trend.column.units': 'Unidades',
  'analytics.trend.grain': 'Cada punto agrupa {days} día(s) · {points} puntos dibujados.',

  'analytics.regions.title': 'Penetración por región',
  'analytics.regions.description': 'Dónde se escanea el producto, por país.',
  'analytics.regions.country': 'País',
  'analytics.regions.scans': 'Escaneos',
  'analytics.regions.share': 'Del total',
  'analytics.regions.suspicious': 'Sospechosos',

  'analytics.results.title': 'Resultados de verificación',
  'analytics.results.description': 'Cómo terminó cada escaneo del período.',
  'analytics.results.result': 'Resultado',
  'analytics.results.scans': 'Escaneos',
  'analytics.results.share': 'Del total',

  'analytics.result.genuine': 'Genuino',
  'analytics.result.alreadySeen': 'Ya escaneado',
  'analytics.result.notActivated': 'Sin activar',
  'analytics.result.invalid': 'Código inválido',
  'analytics.result.stolen': 'Rollo robado',
  'analytics.result.recalled': 'Lote retirado',
  'analytics.result.outOfTerritory': 'Fuera de territorio',

  'analytics.tab.label': 'Vistas de analítica',
  'analytics.tab.trends': 'Tendencias',
  'analytics.tab.exports': 'Exportaciones',

  'analytics.export.new': 'Nueva exportación',
  'analytics.export.newDescription':
    'El archivo se genera en segundo plano. Cuando esté listo aparece abajo con un botón de descarga.',
  'analytics.export.kind': 'Contenido',
  'analytics.export.format': 'Formato',
  'analytics.export.format.csv': 'CSV (UTF-8)',
  'analytics.export.format.excel': 'CSV para Excel',
  'analytics.export.since': 'Desde',
  'analytics.export.batch': 'Lote',
  'analytics.export.create': 'Generar',
  'analytics.export.creating': 'Pidiendo…',
  'analytics.export.privacy':
    'Ninguna exportación lleva datos de consumidores: son agregados y el inventario de la marca. Quién la pidió y quién la descargó quedan registrados en la auditoría.',
  'analytics.export.title': 'Exportaciones',
  'analytics.export.description': 'Las últimas exportaciones de esta marca y quién las pidió.',
  'analytics.export.working': 'Generando…',
  'analytics.export.empty': 'Todavía no hay exportaciones',
  'analytics.export.emptyHint': 'Generá una desde el formulario de arriba.',
  'analytics.export.download': 'Descargar',
  'analytics.export.downloaded': 'Se descargó {name}.',

  'analytics.export.kind.scansDaily': 'Escaneos por día',
  'analytics.export.kind.geoCells': 'Celdas geográficas',
  'analytics.export.kind.alerts': 'Alertas',
  'analytics.export.kind.batches': 'Lotes',
  'analytics.export.kind.units': 'Unidades',
  'analytics.export.kind.labels': 'Etiquetas (listado filtrado)',

  'analytics.export.status.queued': 'En cola',
  'analytics.export.status.running': 'Generando',
  'analytics.export.status.done': 'Listo',
  'analytics.export.status.failed': 'Falló',
  'analytics.export.status.expired': 'Vencido',

  'analytics.export.column.kind': 'Contenido',
  'analytics.export.column.status': 'Estado',
  'analytics.export.column.rows': 'Filas',
  'analytics.export.column.requestedBy': 'Pedido por',
  'analytics.export.column.created': 'Pedido el',
  'analytics.export.column.expires': 'Vence',

  // ───────────────────────────────────────── formularios (#38, #39, #40)
  'form.required': 'Este campo es obligatorio.',
  'form.save': 'Guardar',
  'form.saving': 'Guardando…',
  'form.cancel': 'Cancelar',

  // ─────────────────────────────────────────────── productos (#38)
  'products.title': 'Catálogo de productos',
  'products.description': 'SKU, GTIN y tier de seguridad de cada presentación.',
  'products.count': '{count} productos cargados',
  'products.empty': 'Esta marca no tiene productos cargados.',
  'products.empty.detail': 'El catálogo es lo primero: un lote y una orden de etiquetas se cuelgan de un producto.',
  'products.column.sku': 'SKU',
  'products.column.name': 'Nombre',
  'products.column.gtin': 'GTIN',
  'products.column.tier': 'Tier de seguridad',
  'products.column.risk': 'Riesgo',
  'products.column.status': 'Estado',
  'products.column.created': 'Alta',
  'products.column.updated': 'Última modificación',
  'products.active': 'Activo',
  'products.inactive': 'Inactivo',
  'products.back': '← Volver al catálogo',
  'products.new': 'Nuevo producto',
  'products.new.title': 'Nuevo producto',
  'products.new.description': 'El SKU y el nombre son obligatorios. El GTIN se valida contra GS1.',
  'products.edit': 'Editar',
  'products.edit.title': 'Editar producto',
  'products.deactivate': 'Desactivar',
  'products.deactivate.title': 'Desactivar el producto',
  'products.deactivate.description': 'El producto sale de circulación. No se borra.',
  'products.deactivate.detail': '{sku} — {name} deja de estar en circulación: no se va a poder elegir para lotes ni para órdenes de etiquetas nuevas. Los lotes y las unidades que ya existen no se tocan — un auditor tiene que poder ver el catálogo tal como estaba cuando se serializó ese lote. Se puede volver a activar desde Editar.',
  'products.deactivate.confirm': 'Desactivar el producto',
  'products.reactivate': 'Reactivar',
  'products.column.deactivated': 'Baja',
  'products.deactivated.unknown': 'Antes de que se registrara la fecha',
  'products.detail.badId': 'El identificador del producto no es válido.',
  'products.detail.gs1': 'Cadena GS1 (01)',
  'products.detail.batches': 'Ver los lotes de este producto →',
  'products.field.sku': 'SKU',
  'products.field.sku.hint': 'Único dentro de la marca. Es el que usa el ERP.',
  'products.field.name': 'Nombre comercial',
  'products.field.gtin': 'GTIN',
  'products.field.gtin.hint': 'GTIN-8, 12, 13 o 14. Se normaliza a 14 dígitos y se valida el verificador.',
  'products.field.tier': 'Tier de seguridad',
  'products.field.tier.hint': 'Calibra la regla de reuso de código para este producto.',
  'products.field.active': 'Estado',
  'products.field.active.hint': 'Un producto inactivo no admite lotes nuevos.',
  'products.gtin.none': 'sin GTIN',
  'products.gtin.normalized': 'Se va a guardar como {gtin}.',
  'products.gtin.notDigits': 'El GTIN sólo puede tener dígitos.',
  'products.gtin.length': 'El GTIN debe tener 8, 12, 13 o 14 dígitos (escribiste {length}).',
  'products.gtin.checkDigit': 'El dígito verificador no es válido: debería terminar en {expected}.',
  'products.tier.qr_only': 'Sólo QR',
  'products.tier.qr_scratch': 'QR + código bajo raspado',
  'products.tier.qr_nfc': 'QR + NFC',
  'products.tier.nfc': 'Sólo NFC',

  // ───────────────────────────────────────────────── lotes (#39)
  'batches.title': 'Lotes de producción',
  'batches.description': 'Estado, territorio, vencimiento y unidades activadas de cada lote.',
  'batches.count': '{count} lotes cargados',
  'batches.empty': 'No hay lotes con estos filtros.',
  'batches.empty.detail': 'Probá quitando el filtro de producto o de estado.',
  'batches.back': '← Volver a los lotes',
  'batches.column.lot': 'Lote',
  'batches.column.product': 'Producto',
  'batches.column.expiry': 'Vencimiento',
  'batches.column.territory': 'Territorio',
  'batches.column.activated': 'Unidades activadas',
  'batches.column.risk': 'Riesgo',
  'batches.column.status': 'Estado',
  'batches.column.actions': 'Acciones',
  'batches.column.created': 'Alta',
  'batches.column.updated': 'Última modificación',
  'batches.territory.none': 'sin asignar',
  'batches.activated.hint': 'Las unidades activadas son las que ya pasaron por la línea de empaque. Es el alcance real de un retiro.',
  'batches.recalledAt': 'Retirado el',
  'batches.filter.title': 'Filtros',
  'batches.filter.description': 'Quedan en la URL: el enlace muestra la misma lista del otro lado.',
  'batches.filter.product': 'Producto',
  'batches.filter.status': 'Estado',
  'batches.filter.allProducts': 'Todos los productos',
  'batches.filter.allStatuses': 'Todos los estados',
  'batches.status.open': 'En envasado',
  'batches.status.released': 'En el mercado',
  'batches.status.recalled': 'Retirado',
  'batches.status.closed': 'Agotado',
  'batches.new': 'Nuevo lote',
  'batches.new.title': 'Nuevo lote de producción',
  'batches.new.description': 'Producto, código de lote y vencimiento son la identidad GS1 del lote.',
  'batches.edit': 'Editar',
  'batches.edit.title': 'Editar lote',
  'batches.detail.badId': 'El identificador del lote no es válido.',
  'batches.field.product': 'Producto',
  'batches.field.product.hint': 'No se puede cambiar después: reescribiría la identidad de unidades ya activadas.',
  'batches.field.lot': 'Código de lote',
  'batches.field.lot.hint': 'Es el (10) de GS1. Único por producto.',
  'batches.field.expiry': 'Vencimiento',
  'batches.field.expiry.hint': 'Es el (17) de GS1. Obligatorio: sin él no hay retiro por vencimiento.',
  'batches.field.territory': 'Territorio',
  'batches.field.territory.hint': 'Código de territorio dado de alta. Vacío = sin restricción de zona.',
  'batches.field.status': 'Estado',
  'batches.field.status.hint': 'El retiro no está acá: tiene su propia acción, con confirmación.',
  'batches.recall': 'Retirar del mercado',
  'batches.recall.title': 'Retirar el lote del mercado',
  'batches.recall.step1.description': 'Revisá el alcance antes de seguir. Este retiro no se puede deshacer.',
  'batches.recall.step2.description': 'Escribí el código de lote para confirmar cuál se retira.',
  'batches.recall.scope.title': 'Alcance del retiro',
  'batches.recall.scope.units': 'Unidades activadas afectadas',
  'batches.recall.effects.title': 'Qué pasa al confirmar',
  'batches.recall.effects.consumer': 'Quien escanee una unidad de este lote va a ver una alerta sanitaria explícita, con la indicación de no consumir el producto.',
  'batches.recall.effects.alerts': 'Cada escaneo posterior de este lote genera una alerta crítica en la bandeja.',
  'batches.recall.effects.irreversible': 'No hay forma de deshacerlo desde el dashboard ni desde la API.',
  'batches.recall.reason': 'Motivo del retiro',
  'batches.recall.reason.hint': 'Queda registrado con tu usuario y la hora. Lo va a leer alguien que no estuvo en esta decisión.',
  'batches.recall.reason.tooShort': 'Escribí al menos {min} caracteres explicando el motivo.',
  'batches.recall.continue': 'Continuar',
  'batches.recall.back': 'Volver',
  'batches.recall.confirm': 'Retirar del mercado',
  'batches.recall.recalling': 'Retirando…',
  'batches.recall.confirm.summary': 'Vas a retirar el lote {lot} de {product}, con {units} unidades activadas en el mercado.',
  'batches.recall.typeLot': 'Escribí {lot} para confirmar',
  'batches.recall.typeLot.hint': 'Tal cual, respetando mayúsculas y guiones.',
  'batches.recall.typeLot.mismatch': 'No coincide con el código de lote.',
  'batches.recall.done.title': 'Lote retirado',
  'batches.recall.done.close': 'Cerrar',
  'batches.recall.done.detail': 'El lote {lot} quedó retirado del mercado.',
  'batches.recall.done.consumer': 'La página del consumidor ya muestra la alerta sanitaria para este lote.',
  'batches.recall.done.audit': 'La acción quedó registrada con tu usuario, el motivo y la hora.',
  'batches.recalled.banner': 'Lote retirado del mercado',
  'batches.recalled.detail': 'El lote {lot} se retiró el {when}. Los consumidores que lo escaneen ven una alerta sanitaria.',

  // ─────────────────────────────────────── etiquetas y rollos (#40)
  'labels.title': 'Etiquetas',
  'labels.tab.orders': 'Órdenes',
  'labels.tab.rolls': 'Rollos',
  'labels.tab.stock': 'Stock',
  'labels.tab.units': 'Códigos',
  'labels.journey':
    'Orden → Rollo → Código (sin lote) → Activación en planta → Código con lote y producto → Escaneo del consumidor.',

  // ────────────────── gestión de la etiqueta individual (#89)
  'labels.units.title': 'Códigos',
  'labels.units.description':
    'Un código por vez: veredicto, QR, banderas de estado y acciones.',
  'labels.units.count': '{count} etiquetas',
  'labels.units.scope': 'Buscar por',
  'labels.units.scope.hint': 'Toda la marca, o desde un lote, un rollo o un código.',
  'labels.units.scope.all': 'Toda la marca',
  'labels.units.scope.batch': 'Lote',
  'labels.units.scope.roll': 'Rollo',
  'labels.units.scope.code': 'Código impreso',
  'labels.units.scope.value.id': 'Número',
  'labels.units.scope.value.code': 'Código',
  'labels.units.scope.apply': 'Buscar',
  'labels.units.filter.state': 'Estado',
  'labels.units.filter.all': 'Todos',
  'labels.units.filter.verdict': 'Veredicto',
  'labels.units.filter.product': 'Producto',
  'labels.units.filter.scanned': 'Escaneada',
  'labels.units.filter.scanned.yes': 'Al menos una vez',
  'labels.units.filter.scanned.no': 'Nunca',
  'labels.units.filter.alerts': 'Alertas',
  'labels.units.filter.alerts.open': 'Con alertas abiertas',
  'labels.units.filter.expires': 'Lote vence hasta',
  'labels.units.filter.expires.hint': 'Sólo las activadas: las demás no tienen lote todavía.',
  'labels.units.export': 'Exportar este listado',
  'labels.units.export.sending': 'Encolando…',
  'labels.units.export.queued': 'Exportación encolada: verla en Analítica › Exportaciones',
  'labels.units.export.failed': 'No se pudo encolar la exportación.',
  'labels.units.noScope': 'Falta el número',
  'labels.units.noScope.detail':
    'Elegiste buscar por lote, rollo o código pero no dijiste cuál. Completalo, o volvé a «Toda la marca».',
  'labels.units.batchRef': 'Lote #{id}',
  'labels.units.empty': 'No hay etiquetas en ese ámbito',
  'labels.units.empty.detail':
    'Puede que el lote todavía no tenga unidades activadas, o que el filtro de estado no deje pasar ninguna.',
  'labels.units.column.code': 'Código',
  'labels.units.column.state': 'Estado',
  'labels.units.column.verdict': 'Veredicto',
  'labels.units.column.scans': 'Escaneos',
  'labels.units.column.lastScan': 'Último escaneo',
  'labels.units.column.activated': 'Activada',

  'labels.units.state.created': 'Creada',
  'labels.units.state.printed': 'Impresa',
  'labels.units.state.shipped': 'Despachada',
  'labels.units.state.received': 'Recibida',
  'labels.units.state.activated': 'Activada',
  'labels.units.state.inMarket': 'En el mercado',
  'labels.units.state.verified': 'Verificada',
  'labels.units.state.voided': 'Desactivada',
  'labels.units.state.stolen': 'Robada',
  'labels.units.state.recalled': 'Retirada',
  'labels.units.state.destroyed': 'Destruida',
  'labels.units.state.compromised': 'Falsificada',

  'labels.units.verdict.genuine': 'Genuino',
  'labels.units.verdict.alreadySeen': 'Ya verificada antes',
  'labels.units.verdict.unverified': 'No verifica',
  'labels.units.verdict.stolen': 'Robada',
  'labels.units.verdict.compromised': 'Código clonado',
  'labels.units.verdict.recalled': 'Lote retirado',

  'labels.units.detail.description':
    'Lo que el sistema le contesta hoy a quien escanee esta etiqueta.',
  'labels.units.detail.consumerUrl': 'URL del QR',
  'labels.units.detail.scans': 'Escaneos',
  'labels.units.detail.openAlerts': 'Alertas abiertas',
  'labels.units.detail.riskScore': 'Riesgo',
  'labels.units.detail.batch': 'Lote',
  'labels.units.detail.expiry': 'Vencimiento',
  'labels.units.detail.product': 'Producto',
  'labels.units.detail.territory': 'Territorio',
  'labels.units.detail.roll': 'Rollo',
  'labels.units.detail.created': 'Creada',
  'labels.units.detail.activated': 'Activada',
  'labels.units.detail.firstScan': 'Primer escaneo',
  'labels.units.detail.lastScan': 'Último escaneo',
  'labels.units.detail.from': 'Estado actual',
  'labels.units.identity.title': 'Identidad y trazabilidad',

  'labels.units.qr.alt': 'Código QR de la etiqueta {code}',
  'labels.units.qr.download': 'Descargar QR (SVG)',
  'labels.units.qr.downloading': 'Descargando…',
  'labels.units.qr.hint':
    'El QR lleva esa URL adentro. Escaneándolo con un teléfono se ve exactamente lo que ve un consumidor.',

  'labels.units.flags.title': 'Banderas de estado',
  'labels.units.flags.description':
    'Cada bandera sale de un dato del modelo. La columna de la derecha dice de cuál.',
  'labels.units.flags.column.flag': 'Bandera',
  'labels.units.flags.column.value': 'Valor',
  'labels.units.flags.column.source': 'De dónde sale',
  'labels.units.flags.yes': 'Sí',
  'labels.units.flags.no': 'No',
  'labels.units.flag.scanned': 'Escaneada',
  'labels.units.flag.scanned.source': 'Tiene al menos un escaneo registrado.',
  'labels.units.flag.expired': 'Vencida',
  'labels.units.flag.expired.source': 'La fecha de vencimiento del lote ya pasó.',
  'labels.units.flag.authenticates': 'Activa',
  'labels.units.flag.authenticates.source':
    'El estado autentica: es lo único que hace que el consumidor lea «genuino».',
  'labels.units.flag.suspicious': 'Sospechosa',
  'labels.units.flag.suspicious.source': 'Tiene alertas abiertas sin resolver.',
  'labels.units.flag.compromised': 'Falsificada',
  'labels.units.flag.compromised.source':
    'El código se confirmó clonado o apareció en producto falsificado.',
  'labels.units.flag.stolen': 'Robada',
  'labels.units.flag.stolen.source': 'Se denunció el robo de esta etiqueta o de su rollo.',
  'labels.units.flag.recalled': 'Retirada',
  'labels.units.flag.recalled.source': 'La unidad o su lote están retirados del mercado.',
  'labels.units.flag.voided': 'Desactivada',
  'labels.units.flag.voided.source': 'Se anuló: activación equivocada o etiqueta descartada.',
  'labels.units.flag.terminal': 'Sin salida',
  'labels.units.flag.terminal.source': 'El estado es terminal: ninguna acción la mueve.',
  'labels.units.flag.reappeared': 'Encontrada',
  'labels.units.flag.reappeared.source':
    'Está fuera de circulación y volvió a escanearse después de haberlo quedado.',

  'labels.units.actions.title': 'Acciones',
  'labels.units.actions.description':
    'Sólo aparecen las que la máquina de estados permite desde el estado actual.',
  'labels.units.actions.terminal':
    'Esta etiqueta está en un estado terminal: no hay acción que la mueva.',
  'labels.units.action.stolen': 'Marcar como robada',
  'labels.units.action.stolen.effect':
    'La etiqueta pasa a robada y cada escaneo posterior genera una alerta crítica.',
  'labels.units.action.compromised': 'Marcar como falsificada',
  'labels.units.action.compromised.effect':
    'El código se declara clonado y deja de autenticar. Si el producto legítimo sigue en el mercado, también deja de verificar.',
  'labels.units.action.void': 'Desactivar',
  'labels.units.action.void.effect':
    'La etiqueta se anula: deja de autenticar y no vuelve al circuito.',
  'labels.units.action.effect.audit':
    'Queda registrado en la auditoría con tu usuario, la hora y el motivo.',
  'labels.units.action.effect.irreversible': 'No se puede deshacer.',
  'labels.units.action.step1': 'Qué le pasa a la etiqueta y por qué lo hacés.',
  'labels.units.action.step2': 'Tipeá el código impreso para confirmar.',
  'labels.units.action.typeCode': 'Código de la etiqueta',
  'labels.units.action.confirm': 'Aplicar',
  'labels.units.action.applying': 'Aplicando…',
  'labels.units.action.confirm.summary':
    'Vas a cambiar el estado de la etiqueta {code}. Esta acción no se deshace.',
  'labels.units.action.done.title': 'Listo',
  'labels.units.action.done.close': 'Cerrar',
  'labels.units.action.done.detail': 'La etiqueta {code} quedó en estado {state}.',
  'labels.units.action.done.audit': 'Entrada de auditoría #{seq}.',
  'labels.units.action.done.cacheWarning':
    'No se pudo avisarle al servicio de verificación. El cambio ya está aplicado y llega en pocos minutos.',
  'labels.units.reason': 'Motivo',
  'labels.units.reason.hint':
    'Qué comprobaste. Lo va a leer alguien que no estuvo acá, quizá dentro de tres años.',
  'labels.units.reason.tooShort': 'Escribí al menos una frase.',
  'labels.units.reference': 'Referencia',
  'labels.units.reference.hint': 'Número de denuncia, expediente o id del caso.',

  'labels.units.evidence.download': 'Descargar paquete de evidencia',
  'labels.units.evidence.downloading': 'Preparando…',
  'labels.units.evidence.privacy':
    'Incluye la ficha, los escaneos con geo a nivel ciudad, las alertas y la custodia del rollo. Sin datos personales: no lleva hashes de IP ni de dispositivo ni coordenadas exactas. Queda registrado quién lo exportó.',

  'labels.units.history.title': 'Historial de estado',
  'labels.units.history.description':
    'Append-only: las acciones manuales sobre esta etiqueta, con su motivo.',
  'labels.units.history.empty': 'Esta etiqueta no tuvo acciones manuales.',
  'labels.units.history.column.when': 'Cuándo',
  'labels.units.history.column.change': 'Cambio',
  'labels.units.history.column.reason': 'Motivo',
  'labels.units.history.column.actor': 'Quién',
  'labels.units.history.column.audit': 'Auditoría',
  'labels.orders.title': 'Órdenes de etiquetas',
  'labels.orders.description': 'Qué se le compró al impresor y en qué estado está cada orden.',
  'labels.orders.count': '{count} órdenes cargadas',
  'labels.orders.empty': 'No hay órdenes con este filtro.',
  'labels.orders.empty.detail': 'Una orden se crea en borrador y no reserva ningún código hasta que se genera.',
  'labels.orders.back': '← Volver a las órdenes',
  'labels.orders.new': 'Nueva orden',
  'labels.orders.new.title': 'Nueva orden de etiquetas',
  'labels.orders.new.description': 'La orden nace en borrador: todavía no reserva códigos y se puede cancelar.',
  'labels.orders.create': 'Crear orden',
  'labels.orders.filter.status': 'Estado',
  'labels.orders.filter.all': 'Todos los estados',
  'labels.orders.column.code': 'Orden',
  'labels.orders.column.supplier': 'Proveedor',
  'labels.orders.column.quantity': 'Etiquetas',
  'labels.orders.column.rolls': 'Rollos',
  'labels.orders.column.serials': 'Rango de series',
  'labels.orders.column.status': 'Estado',
  'labels.orders.column.generated': 'Generada',
  'labels.orders.column.created': 'Alta',
  'labels.order.status.draft': 'Borrador',
  'labels.order.status.generating': 'Generando',
  'labels.order.status.generated': 'Generada',
  'labels.order.status.cancelled': 'Cancelada',
  'labels.order.badId': 'El identificador de la orden no es válido.',
  'labels.order.serials.pending': 'se asigna al generar',
  'labels.order.field.code': 'Código de orden',
  'labels.order.field.code.hint': 'El número de la orden de compra al proveedor.',
  'labels.order.field.supplier': 'Proveedor',
  'labels.order.field.quantity': 'Etiquetas a fabricar',
  'labels.order.field.quantity.invalid': 'Un número entero entre 1 y {max}.',
  'labels.order.field.unitsPerRoll': 'Etiquetas por rollo',
  'labels.order.field.unitsPerRoll.invalid': 'Un número entero entre 1 y {max}.',
  'labels.order.field.product': 'Producto',
  'labels.order.field.product.hint': 'Opcional. Las etiquetas se pueden fabricar antes de saber a qué SKU van.',
  'labels.order.field.product.none': 'Sin producto asignado',
  'labels.order.field.keyVersion': 'Versión de clave',
  'labels.order.field.notes': 'Notas',
  'labels.order.rollPreview': 'Esta orden produce {rolls} rollos para {units} etiquetas.',
  'labels.order.rollPreview.unknown': 'Completá cantidad y etiquetas por rollo para ver cuántos rollos salen.',
  'labels.order.generate': 'Generar códigos',
  'labels.order.generate.section': 'Generación de códigos',
  'labels.order.generate.section.hint': 'Reserva el rango de series, crea los rollos y produce el archivo cifrado para el impresor.',
  'labels.order.generate.title': 'Generar los códigos de esta orden',
  'labels.order.generate.description': 'Es la operación que reserva definitivamente un rango de códigos.',
  'labels.order.generate.confirm': 'Generar ahora',
  'labels.order.generate.effect.units': 'Las unidades nacen en CREATED: una etiqueta recién fabricada no autentica nada hasta que se active en la línea de empaque.',
  'labels.order.generate.effect.range': 'El rango de series queda reservado y no se puede reutilizar, ni siquiera cancelando la orden.',
  'labels.order.generate.effect.key': 'El archivo del impresor se cifra antes de salir del proceso. La clave se entrega por fuera de la plataforma.',
  'labels.order.progress.title': 'Generando {units} etiquetas…',
  'labels.order.progress.elapsed': 'Tiempo transcurrido',
  'labels.order.progress.serverStatus': 'Estado en el servidor',
  'labels.order.progress.seconds': '{seconds} s',
  'labels.order.progress.note': 'La generación corre dentro de una transacción, así que no hay un porcentaje que leer: el estado del servidor se relee cada segundo y al terminar se muestran los tiempos reales por fase.',
  'labels.order.generated.title': 'Códigos generados',
  'labels.order.generated.units': 'Unidades',
  'labels.order.generated.rolls': 'Rollos',
  'labels.order.generated.elapsed': 'Duración',
  'labels.order.generated.rate': 'Velocidad',
  'labels.order.generated.rate.value': '{rate} unidades/s',
  'labels.order.cancel': 'Cancelar orden',
  'labels.order.cancel.title': 'Cancelar la orden',
  'labels.order.cancel.description': 'Sólo se puede cancelar mientras esté en borrador.',
  'labels.order.cancel.detail': 'Se va a cancelar la orden {code}. Una orden ya generada no se cancela: lo que se anula ahí son los rollos, uno por uno.',
  'labels.order.cancel.confirm': 'Cancelar la orden',
  'labels.order.rolls.title': 'Rollos de esta orden',
  'labels.order.rolls.description': 'Cada rollo lleva un rango contiguo de series y ningún rango se solapa con otro.',
  'labels.order.rolls.empty': 'Esta orden todavía no produjo rollos.',
  'labels.printFile.title': 'Archivo para el impresor',
  'labels.printFile.description': 'Enlace firmado y temporal al archivo cifrado de datos variables.',
  'labels.printFile.request': 'Pedir el enlace de descarga',
  'labels.printFile.request.hint': 'El enlace se genera en el momento y vence: no se guarda en cache.',
  'labels.printFile.notGenerated': 'Esta orden todavía no tiene archivo.',
  'labels.printFile.notGenerated.detail': 'El archivo se produce al generar los códigos.',
  'labels.printFile.key.title': 'La clave de descifrado no viaja por acá',
  'labels.printFile.key.detail': 'Se le entrega al proveedor por un canal fuera de la plataforma. No la mandes por el mismo medio que este enlace: separadas, un enlace interceptado no da acceso a ningún secreto.',
  'labels.printFile.records': 'Registros',
  'labels.printFile.bytes': 'Tamaño (bytes)',
  'labels.printFile.expires': 'Vence en',
  'labels.printFile.expires.value': '{minutes} minutos',
  'labels.printFile.cipher': 'Cifrado',
  'labels.printFile.sha256': 'SHA-256 del archivo',
  'labels.printFile.nonce': 'Nonce',
  'labels.printFile.download': 'Descargar el archivo cifrado',
  'labels.printFile.renew': 'Renovar el enlace',
  'labels.rolls.title': 'Rollos',
  'labels.rolls.description': 'Inventario físico de etiquetas y su cadena de custodia.',
  'labels.rolls.count': '{count} rollos cargados',
  'labels.rolls.empty': 'No hay rollos con este filtro.',
  'labels.rolls.empty.detail': 'Los rollos se crean al generar los códigos de una orden.',
  'labels.rolls.back': '← Volver a los rollos',
  'labels.rolls.filter.state': 'Estado de custodia',
  'labels.rolls.filter.state.hint': 'Filtrá por «robado» para ver los rollos denunciados.',
  'labels.rolls.filter.all': 'Todos los estados',
  'labels.rolls.column.code': 'Rollo',
  'labels.rolls.column.order': 'Orden',
  'labels.rolls.column.serials': 'Rango de series',
  'labels.rolls.column.units': 'Etiquetas',
  'labels.rolls.column.state': 'Estado',
  'labels.rolls.column.supplier': 'Proveedor',
  'labels.rolls.column.updated': 'Última novedad',
  'labels.roll.badId': 'El identificador del rollo no es válido.',
  'labels.roll.noSupplier': 'Sin proveedor registrado',
  'labels.roll.index': 'Número de rollo en la orden',
  'labels.roll.carrier': 'Transportista',
  'labels.roll.tracking': 'Guía de transporte',
  'labels.roll.printedAt': 'Impreso',
  'labels.roll.shippedAt': 'Despachado',
  'labels.roll.receivedAt': 'Recibido',
  'labels.roll.workerStatus': 'Estado proyectado (motor de reglas)',
  'labels.roll.state.ordered': 'Pedido',
  'labels.roll.state.printed': 'Impreso',
  'labels.roll.state.shipped': 'Despachado',
  'labels.roll.state.received': 'Recibido',
  'labels.roll.state.in_use': 'En uso',
  'labels.roll.state.consumed': 'Consumido',
  'labels.roll.state.stolen': 'Robado',
  'labels.roll.state.destroyed': 'Destruido',
  'labels.roll.state.voided': 'Anulado',
  'labels.chain.title': 'Cadena de custodia',
  'labels.chain.interrupted': 'La cadena se interrumpió: el rollo está en «{state}».',
  'labels.transition.title': 'Mover el rollo',
  'labels.transition.description': 'Sólo se ofrecen los destinos legítimos desde el estado actual. Nada apunta hacia atrás: una corrección se registra como evento nuevo.',
  'labels.transition.terminal': 'El rollo está en «{state}» y no tiene movimientos posibles.',
  'labels.transition.to': 'Nuevo estado',
  'labels.transition.to.hint': 'El robo no está acá: tiene su propia acción, con confirmación.',
  'labels.transition.location': 'Lugar',
  'labels.transition.reference': 'Referencia',
  'labels.transition.reference.hint': 'Remito, número de guía o acta de destrucción.',
  'labels.transition.carrier': 'Transportista',
  'labels.transition.tracking': 'Guía de transporte',
  'labels.transition.note': 'Nota',
  'labels.transition.submit': 'Registrar el movimiento',
  'labels.transition.done': 'Movimiento registrado: el rollo quedó en «{to}» y afectó a {units} etiquetas.',
  'labels.custody.title': 'Historial de custodia',
  'labels.custody.description': 'Append-only: los eventos no se editan ni se borran. Una cadena de custodia editable no responde nada.',
  'labels.custody.empty': 'Este rollo todavía no tiene eventos de custodia.',
  'labels.custody.empty.detail': 'El primer evento se registra al mover el rollo desde «Pedido».',
  'labels.custody.from': 'desde {from}',
  'labels.custody.units': 'Etiquetas afectadas',
  'labels.custody.location': 'Lugar',
  'labels.custody.reference': 'Referencia',
  'labels.custody.actor': 'Registrado por',
  'labels.custody.actor.value': 'usuario #{id}',
  'labels.custody.complete': 'Principio de la cadena.',
  'labels.stolen.action': 'Denunciar robo',
  'labels.stolen.title': 'Denunciar el rollo como robado',
  'labels.stolen.step1.description': 'Revisá el alcance y contá qué pasó. Esto no se puede deshacer.',
  'labels.stolen.step2.description': 'Escribí el código del rollo para confirmar cuál se denuncia.',
  'labels.stolen.scope.units': 'Etiquetas del rollo',
  'labels.stolen.effects.units': 'Todas las etiquetas del rollo que todavía no se activaron pasan a «robada» y dejan de poder autenticar.',
  'labels.stolen.effects.alerts': 'Cada escaneo posterior de cualquiera de esas etiquetas genera una alerta crítica, con la evidencia de dónde apareció.',
  'labels.stolen.effects.activated': 'Las unidades ya activadas no se tocan: producto legítimo que ya está en la calle no se desactiva.',
  'labels.stolen.effects.irreversible': '«Robado» es un estado sin salida: el rollo no puede volver a moverse.',
  'labels.stolen.note': 'Qué pasó',
  'labels.stolen.note.hint': 'Obligatorio. La alerta crítica que esto genera la va a leer un investigador que no estuvo acá.',
  'labels.stolen.note.tooShort': 'Contá qué pasó, aunque sea en una línea.',
  'labels.stolen.policeReport': 'Número de denuncia policial',
  'labels.stolen.policeReport.hint': 'Opcional, pero es lo que sostiene el caso ante un auditor.',
  'labels.stolen.location': 'Lugar del robo',
  'labels.stolen.confirm': 'Denunciar el robo',
  'labels.stolen.reporting': 'Registrando la denuncia…',
  'labels.stolen.confirm.summary': 'Vas a denunciar el robo del rollo {code}, con {units} etiquetas.',
  'labels.stolen.typeCode': 'Escribí {code} para confirmar',
  'labels.stolen.done.title': 'Robo registrado',
  'labels.stolen.done.detail': 'El rollo {code} quedó marcado como robado y {units} etiquetas dejaron de poder autenticar.',
  'labels.stolen.done.next': 'A partir de ahora, cualquier escaneo de esas etiquetas genera una alerta crítica en la bandeja.',
  'labels.stolen.banner': 'Rollo denunciado como robado',
  'labels.stolen.banner.detail': 'La denuncia se registró el {when}. Cada escaneo de sus etiquetas genera una alerta crítica.',
  // ───────────────────────── programación de reportes (#70, criterio de #47)
  'reports.schedule.title': 'Envío programado',
  'reports.schedule.description':
    'El reporte se genera en el servidor y sale por correo, sin que nadie tenga que entrar. Cada envío queda en la auditoría.',
  'reports.schedule.adminOnly':
    'Programar un envío es de administrador: decide qué datos de la marca le llegan a otras personas, todas las semanas y sin que nadie vuelva a mirar.',
  'reports.schedule.form.period': 'Qué período cubre',
  'reports.schedule.form.cadence': 'Cada cuánto',
  'reports.schedule.form.weekday': 'Día de la semana',
  'reports.schedule.form.monthday': 'Día del mes',
  'reports.schedule.form.hour': 'Hora',
  'reports.schedule.form.recipients': 'Quiénes lo reciben',
  'reports.schedule.form.recipientsHint':
    'Sólo usuarios de tu marca. Un envío automático a una casilla externa sacaría datos de la marca todas las semanas sin que nadie lo revise.',
  'reports.schedule.form.submit': 'Programar',
  'reports.schedule.form.saving': 'Programando…',
  'reports.schedule.period.lastWeek': 'La semana pasada',
  'reports.schedule.period.lastMonth': 'El mes pasado',
  'reports.schedule.period.last30d': 'Los últimos 30 días',
  'reports.schedule.cadence.weekly': 'Semanal',
  'reports.schedule.cadence.monthly': 'Mensual',
  'reports.schedule.weekday.1': 'Lunes',
  'reports.schedule.weekday.2': 'Martes',
  'reports.schedule.weekday.3': 'Miércoles',
  'reports.schedule.weekday.4': 'Jueves',
  'reports.schedule.weekday.5': 'Viernes',
  'reports.schedule.weekday.6': 'Sábado',
  'reports.schedule.weekday.7': 'Domingo',
  'reports.schedule.monthlyOn': 'El día',
  'reports.schedule.empty': 'No hay envíos programados',
  'reports.schedule.emptyHint': 'Programá uno y el reporte sale solo, con el período ya calculado.',
  'reports.schedule.paused': 'En pausa',
  'reports.schedule.column.what': 'Reporte',
  'reports.schedule.column.when': 'Cuándo sale',
  'reports.schedule.column.recipients': 'Destinatarios',
  'reports.schedule.column.next': 'Próximo envío',
  'reports.schedule.column.actions': 'Acciones',
  'reports.schedule.runNow': 'Correr ahora',
  'reports.schedule.pause': 'Pausar',
  'reports.schedule.resume': 'Reanudar',
  'reports.schedule.delete': 'Borrar',
  'reports.schedule.runs.title': 'Envíos',
  'reports.schedule.runs.description':
    'Cuándo salió, a quiénes y con qué resultado. Borrar una programación no borra este registro.',
  'reports.schedule.runs.column.period': 'Período',
  'reports.schedule.runs.column.status': 'Estado',
  'reports.schedule.runs.column.recipients': 'Destinatarios',
  'reports.schedule.runs.column.trigger': 'Origen',
  'reports.schedule.runs.column.when': 'Cuándo',
  'reports.schedule.runs.refresh': 'Actualizar',
  'reports.schedule.runs.manual': 'A mano',
  'reports.schedule.runs.scheduled': 'Por calendario',
  'reports.schedule.run.queued': 'En cola',
  'reports.schedule.run.running': 'Generando',
  'reports.schedule.run.sent': 'Enviado',
  'reports.schedule.run.failed': 'Falló',

  // ───────────────────────────── verificación · mensajes al consumidor (#90)
  'module.verification': 'Verificación',
  'module.verification.description':
    'Los mensajes que ve quien escanea tu producto, en español, inglés y portugués.',

  'verification.title': 'Mensajes de la página pública',
  'verification.description':
    'Lo que lee alguien que acaba de escanear tu producto. Lo que dejes vacío usa el texto por defecto de XYVONA.',
  'verification.readOnly': 'Sólo lectura',
  'verification.save': 'Publicar',
  'verification.saving': 'Publicando…',
  'verification.saved': 'Publicado. Los escaneos nuevos ya lo ven.',

  'verification.locale.es': 'Español',
  'verification.locale.en': 'English',
  'verification.locale.pt': 'Português',
  'verification.locale.hint':
    'Cada idioma se guarda por separado: lo que no escribas en uno usa el texto por defecto EN ESE idioma, nunca el de otro.',

  'verification.field.title': 'Título',
  'verification.field.body': 'Texto',
  'verification.field.bodyHint': 'Vacío = el texto por defecto.',
  'verification.field.bodyVars':
    'Podés usar {date} y {place}: se reemplazan por la fecha y la ciudad de la primera verificación.',
  'verification.field.defaultPlaceholder': 'Vacío: se usa el texto por defecto',

  'verification.slot.seeIt': 'Verlo',
  'verification.slot.genuine': 'Producto verificado',
  'verification.slot.genuine.hint':
    'Primera verificación de una unidad activada. Es el único estado que afirma autenticidad.',
  'verification.slot.already': 'Ya verificado antes',
  'verification.slot.already.hint':
    'El código es válido pero alguien ya lo escaneó. No es una acusación: puede haberlo escaneado la misma persona.',
  'verification.slot.unverified': 'No pudimos verificar',
  'verification.slot.unverified.hint':
    'Código ilegible, etiqueta dañada, unidad todavía no activada o rollo denunciado. Los cuatro casos comparten este mensaje a propósito.',
  'verification.slot.recalled': 'Lote retirado (alerta sanitaria)',
  'verification.slot.recalled.hint':
    'El único mensaje que sí afirma algo. Tiene que decir qué hacer con el producto y a quién llamar.',
  'verification.slot.throttled': 'Demasiados intentos',
  'verification.slot.throttled.hint':
    'Se ve al enviar varios reportes seguidos desde el mismo dispositivo. No tiene vista previa propia.',
  'verification.slot.contact': 'Pie: cómo contactarte',
  'verification.slot.contact.hint':
    'El encabezado del canal de reporte. Aparece en los estados que ofrecen contactarte; el teléfono y el correo salen de la configuración de tu marca.',

  'verification.preview.title': 'Vista previa',
  'verification.preview.description': 'La página real, con datos de muestra.',
  'verification.preview.states': 'Estado a previsualizar',
  'verification.preview.frameTitle': 'Página del consumidor, vista previa',
  'verification.preview.note':
    'Es la página pública en modo demostración: no consulta ninguna unidad ni lote real, y el texto sin publicar sólo se ve acá.',
  'verification.state.genuine': 'Verificado',
  'verification.state.already_seen': 'Ya verificado',
  'verification.state.unverified': 'No verificado',
  'verification.state.recalled': 'Lote retirado',

  'verification.rule.title': 'Por qué no hay un mensaje de «producto no genuino»',
  'verification.rule.what':
    'A quien escanea nunca se le afirma que su producto sea ilegítimo. El peor mensaje posible es «no pudimos verificar este código», acompañado de cómo contactarte.',
  'verification.rule.why':
    'Un aviso equivocado con ese texto le dice a un comprador legítimo que lo que tiene en la mano no lo es, en base a una inferencia. Una sola vez que se equivoque es un problema legal para tu marca y para XYVONA. Y avisar de un robo a quien tiene el producto encima o bien acusa a quien lo compró de buena fe, o bien le avisa a quien no debería.',
  'verification.rule.where':
    'La certeza sí te llega a vos: la unidad, el lote, dónde y cuándo se escaneó, y la evidencia completa aparecen en tu bandeja de alertas.',
  'verification.rule.recall':
    'La única excepción es el retiro de lote: ahí el riesgo de no avisar supera al de avisar, y el mensaje se muestra como alerta sanitaria.',

  'verification.rejected.field': 'No se puede publicar: {terms}.',
  'verification.rejected.item': '{locale} · {slot} · {field}: {terms}',


  // ───────────────────────────────── back-office (#109)
  'module.clients.description': 'Alta de marcas, suspensión y métricas por cliente.',
  'module.requests.description': 'Lo que los clientes piden: rollos de etiquetas.',
  'clients.title': 'Clientes',
  'clients.description': 'Las marcas que operan sobre XYVONA.',
  'clients.new': 'Dar de alta un cliente',
  'clients.count': '{count} clientes',
  'clients.empty': 'Todavía no hay ningún cliente.',
  'clients.col.client': 'Cliente',
  'clients.col.industry': 'Rubro',
  'clients.col.units': 'Unidades',
  'clients.col.activated': 'Activadas',
  'clients.col.users': 'Usuarios',
  'clients.col.lastScan': 'Último escaneo',
  'clients.col.state': 'Estado',
  'clients.state.active': 'Activo',
  'clients.state.suspended': 'Suspendido',
  'clients.suspend': 'Suspender',
  'clients.reactivate': 'Reactivar',
  'clients.suspend.confirm': 'Suspender corta el acceso de TODOS los usuarios de esta marca en el acto. Las etiquetas ya impresas se siguen verificando como genuinas.',
  'clients.suspend.reason': 'Motivo (queda en la auditoría)',
  'clients.suspend.reasonHint': 'Al menos {min} caracteres: lo va a leer quien tenga que explicar esto.',
  'clients.suspend.failed': 'No se pudo cambiar el estado. Volvé a intentar.',
  'clients.reactivate.confirm': 'Reactivar devuelve el acceso a TODOS los usuarios de esta marca en el próximo login.',
  'clients.form.slug': 'Identificador',
  'clients.form.slugHint': 'Con esto entra al dashboard. No se puede cambiar después.',
  'clients.form.legalName': 'Razón social',
  'clients.form.country': 'País',
  'clients.form.industry': 'Rubro',
  'clients.form.portal': 'Portal del consumidor',
  'clients.form.portalPharma': 'Prohibido en pharma: qué medicamentos escaneó una persona identificada es un dato de salud.',
  'clients.form.adminEmail': 'Correo del primer administrador',
  'clients.form.adminName': 'Nombre',
  'clients.form.submit': 'Crear cliente',
  'clients.created.title': 'Cliente creado',
  'clients.created.password': 'Contraseña temporal',
  'clients.created.once': 'Se muestra UNA sola vez. Si se pierde, se resetea — no se puede volver a consultar.',
  'clients.created.copy': 'Copiar',
  'clients.created.copied': 'Copiado',
  'requests.title': 'Pedidos de rollos',
  'requests.description': 'Lo que los clientes piden. Los pendientes, primero los más viejos.',
  'requests.empty': 'No hay pedidos.',
  'requests.filter.all': 'Todos',
  'requests.filter.pending': 'Pendientes',
  'requests.col.client': 'Cliente',
  'requests.col.quantity': 'Unidades',
  'requests.col.neededBy': 'Para',
  'requests.col.notes': 'Notas',
  'requests.col.state': 'Estado',
  'requests.state.pending': 'Pendiente',
  'requests.state.approved': 'Aprobado',
  'requests.state.fulfilled': 'Entregado',
  'requests.state.rejected': 'Rechazado',
  'requests.approve': 'Aprobar',
  'requests.reject': 'Rechazar',
  'requests.fulfill': 'Dar por entregado',
  'requests.resolution': 'Respuesta para el cliente',
  'stock.title': 'Stock de etiquetas',
  'stock.description': 'Rollos a nombre de XYVONA, todavía sin cliente.',
  'stock.empty': 'No hay rollos en stock. Generá una orden en Etiquetas.',
  'stock.showAll': 'Ver también los entregados',
  'stock.col.roll': 'Rollo',
  'stock.col.order': 'Orden',
  'stock.col.supplier': 'Proveedor',
  'stock.col.units': 'Unidades',
  'stock.col.range': 'Series',
  'stock.col.state': 'Custodia',
  'stock.col.owner': 'Asignado a',
  'stock.assign': 'Entregar a un cliente',
  'stock.assign.title': 'Entregar el rollo',
  'stock.assign.client': 'Cliente',
  'stock.assign.warning': 'Mueve el rollo y sus unidades al cliente. Queda en la auditoría de las dos partes y no se puede deshacer desde acá.',
  'stock.assign.submit': 'Entregar',
  'stock.assigned': '{count} unidades entregadas.',
  'hq.free': 'En stock',
  'hq.close': 'Cerrar',
  'labels.verify.request': 'Verificar el archivo',
  'labels.verify.request.hint': 'Lo descifra en memoria y lo compara contra la base: cada código y cada secreto. Es lo que hay que mirar antes de mandárselo al proveedor.',
  'labels.verify.ok': 'El archivo corresponde a las unidades de la base.',
  'labels.verify.fail': 'El archivo NO corresponde. No lo mandes.',
  'labels.verify.records': 'Registros',
  'labels.verify.checked': 'Comprobados',
  'labels.verify.sampled': 'muestra',
  'labels.verify.codes': 'Códigos inválidos',
  'labels.verify.secrets': 'Secretos que no coinciden',
  'labels.verify.sha256': 'Integridad del objeto',
  'labels.verify.sha256.ok': 'coincide con lo registrado',
  'labels.verify.sha256.fail': 'NO coincide: el archivo cambió',
  'labels.verify.preview': 'Primeras líneas del archivo',
  'labels.verify.col.roll': 'Rollo',
  'labels.verify.col.code': 'Código',
  'labels.verify.col.secret': 'Secreto',
  'labels.verify.masked': 'El secreto va enmascarado: en claro sólo existe en el archivo cifrado y bajo la capa de raspado.',
  'labels.verify.full': 'Comprobar el archivo entero',

  // ── pedidos de rollos del cliente (#142)
  'labels.tab.requests': 'Pedidos',
  'rollRequests.title': 'Pedidos de rollos',
  'rollRequests.description':
    'Lo que le pedís a XYVONA. Nosotros generamos los códigos, mandamos a imprimir y te entregamos los rollos.',
  'rollRequests.new': 'Pedir rollos',
  'rollRequests.new.description':
    'La cantidad va en etiquetas. Cuántas entran en cada rollo lo definimos con el proveedor según la tirada.',
  'rollRequests.send': 'Enviar pedido',
  'rollRequests.empty': 'Todavía no pediste rollos',
  'rollRequests.empty.detail':
    'Cuando pidas, el pedido queda acá con su estado hasta que se entregue.',
  'rollRequests.col.quantity': 'Etiquetas',
  'rollRequests.col.neededBy': 'Para cuándo',
  'rollRequests.col.notes': 'Notas',
  'rollRequests.col.state': 'Estado',
  'rollRequests.col.resolution': 'Respuesta',
  'rollRequests.field.quantity': 'Cantidad de etiquetas',
  'rollRequests.field.quantity.hint': 'En etiquetas, no en rollos.',
  'rollRequests.field.quantity.invalid': 'Un número entero entre 1 y {max}.',
  'rollRequests.field.product': 'Producto',
  'rollRequests.field.product.hint': 'Opcional. Ayuda a preparar la tirada.',
  'rollRequests.field.product.none': 'Sin producto asignado',
  'rollRequests.field.neededBy': 'Fecha en que las necesitás',
  'rollRequests.field.neededBy.hint': 'Opcional. Es una referencia, no un compromiso.',
  'rollRequests.field.notes': 'Notas',
  'rollRequests.field.notes.hint': 'Nivel de seguridad, destino, lo que haga falta aclarar.',

  // ── #147 · clave del pedido y herramientas del back-office
  'module.tooling': 'Herramientas',
  'module.tooling.description': 'La página del impresor y los permisos del equipo.',
  'labels.orderKey.request': 'Ver la clave de este pedido',
  'labels.orderKey.request.hint': 'Se muestra sólo cuando la pedís, y queda registrado quién la miró y cuándo.',
  'labels.orderKey.working': 'Pidiendo…',
  'labels.orderKey.value': 'Clave del pedido {order}',
  'labels.orderKey.copy': 'Copiar la clave',
  'labels.orderKey.copied': 'Copiada',
  'labels.orderKey.hide': 'Ocultar',
  'labels.orderKey.warning.title': 'Mandala por un canal distinto del enlace de descarga',
  'labels.orderKey.warning.detail': 'Si el archivo y la clave viajan por el mismo medio, las dos capas se vuelven una sola. Teléfono, WhatsApp o en persona — nunca el mismo correo que el enlace.',
  'labels.orderKey.audited': 'Este pedido quedó en la auditoría. La clave no: sólo que vos la miraste.',
  'tooling.supplier.title': 'Página para el impresor',
  'tooling.supplier.description': 'El archivo con el que el proveedor abre el .xyvc1. Se le manda una vez y lo guarda.',
  'tooling.supplier.version': 'Versión',
  'tooling.supplier.bytes': 'Tamaño (bytes)',
  'tooling.supplier.sha256': 'SHA-256',
  'tooling.supplier.download': 'Descargar la página',
  'tooling.supplier.downloading': 'Bajando…',
  'tooling.supplier.sendOnce': 'No hace falta reenviarla en cada pedido: sólo si sube la versión.',
  'tooling.permissions.title': 'Permisos del equipo de XYVONA',
  'tooling.permissions.description': 'Quién puede ver la clave de descifrado de un pedido. Se otorga de a uno y queda auditado.',
  'tooling.permissions.empty': 'No hay usuarios de XYVONA.',
  'tooling.permissions.col.person': 'Persona',
  'tooling.permissions.col.permission': 'Ver la clave',
  'tooling.permissions.col.action': 'Acción',
  'tooling.permissions.granted': 'Otorgado',
  'tooling.permissions.none': 'Sin permiso',
  'tooling.permissions.grant': 'Otorgar',
  'tooling.permissions.revoke': 'Revocar',
  'tooling.permissions.note': 'Motivo (queda en la auditoría)',
  'tooling.permissions.noteRequired': 'Detalle (obligatorio con «otro»)',
  'tooling.permissions.reason': 'Motivo',
  'tooling.permissions.reason.reprint': 'Reimpresión',
  'tooling.permissions.reason.printer_claim': 'Reclamo del impresor',
  'tooling.permissions.reason.audit': 'Auditoría',
  'tooling.permissions.reason.other': 'Otro',
  'tooling.permissions.duration': 'Vence en',
  'tooling.permissions.duration.4': '4 horas',
  'tooling.permissions.duration.24': '24 horas',
  'tooling.permissions.duration.72': '3 días',
  'tooling.permissions.duration.168': '7 días',
  'tooling.permissions.duration.720': '30 días',
  'tooling.permissions.expires': 'Vence el {when}',
  'tooling.permissions.expired': 'Venció el {when}',

  // ── secciones de la barra lateral
  'nav.section.catalog': 'Catálogo',
  'nav.section.plant': 'Planta',
  'nav.section.activity': 'Actividad',
  'nav.section.protection': 'Protección de marca',
  'nav.section.config': 'Configuración',
  'nav.section.hq': 'XYVONA',

  // ── ficha de etiqueta, encabezado
  'labels.units.detail.label': 'Etiqueta ·',
  'labels.units.detail.batchInline': 'Lote {lot}',
  'labels.units.detail.rollInline': 'Rollo {roll}',
  'labels.units.detail.openAlertsBadge': '{count} alertas abiertas',

  // ── listado de etiquetas
  'labels.units.countTotal': '{count} en total',
  'labels.units.quickFind.placeholder': 'Buscar por código impreso, o poné el id del lote o del rollo abajo',
  'labels.units.quickFind.submit': 'Buscar',
  'labels.units.column.batch': 'Lote',
  'labels.units.column.created': 'Creada',
  'labels.units.column.actions': 'Acciones',
  'labels.units.column.qr': 'QR',

  // ── ámbito activo del listado de etiquetas
  'labels.units.scoped.all': 'Todos los códigos de la marca, del más nuevo al más viejo.',
  'labels.units.scoped.batch': 'Etiquetas del lote {value}. Cambiá el ámbito abajo para ver otro.',
  'labels.units.scoped.roll': 'Etiquetas del rollo {value}. Cambiá el ámbito abajo para ver otro.',
  'labels.units.scoped.code': 'Búsqueda del código {value}.',
} as const;

export type MessageKey = keyof typeof es;

type Catalog = Record<MessageKey, string>;

const en: Catalog = {
  'app.name': 'XYVONA',
  'app.subtitle': 'Product authentication',
  'app.skipToContent': 'Skip to content',

  'auth.login.title': 'Sign in to the dashboard',
  'auth.login.intro': 'Use your brand credentials.',
  'auth.login.tenant': 'Brand',
  'auth.login.tenantHint': 'Your laboratory short identifier.',
  'auth.login.email': 'Email',
  'auth.login.password': 'Password',
  'auth.login.submit': 'Sign in',
  'auth.login.submitting': 'Checking…',
  'auth.login.required': 'Fill in all three fields.',
  'auth.logout': 'Sign out',
  'auth.loggingOut': 'Signing out…',
  'auth.session.restoring': 'Restoring your session…',
  'auth.session.expired': 'Your session expired. Sign in again.',
  'auth.session.reused':
    'All your sessions were closed for security: this session token was used twice. If this was not you, tell your brand administrator.',
  'auth.session.revoked': 'An administrator closed your session.',
  'auth.role.label': 'Role',

  // ───────────────────────────────────────────────────────────── my account
  'account.title': 'My account',
  'account.link': 'My account',
  'account.identity.title': 'Who I am',
  'account.identity.name': 'Name',
  'account.identity.email': 'Email',
  'account.password.title': 'Change password',
  'account.password.intro':
    'All your sessions on other devices will be signed out. You stay signed in here.',
  'account.password.current': 'Current password',
  'account.password.new': 'New password',
  'account.password.newHint': 'At least 12 characters. A phrase beats a word.',
  'account.password.confirm': 'Repeat the new password',
  'account.password.submit': 'Change password',
  'account.password.submitting': 'Changing…',
  'account.password.required': 'Fill in all three fields.',
  'account.password.tooShort': 'The new password needs at least 12 characters.',
  'account.password.mismatch': 'The two new passwords do not match.',
  'account.password.same': 'The new password is the same as the current one.',
  'account.password.wrongCurrent': 'The current password is not correct.',
  'account.password.federated':
    'Your account signs in through your organization directory. Change the password there.',
  'account.password.changed': 'Password changed.',
  'account.password.changedOthers':
    'Password changed. {n} sessions on other devices were signed out.',
  'account.password.changedRelogin':
    'Password changed. We could not renew this session: sign in again with the new one.',

  'nav.title': 'Modules',
  'nav.pending': 'Coming soon',
  'nav.pendingHint': 'This module is not implemented yet.',
  'nav.open': 'Open navigation',
  'nav.close': 'Close navigation',
  'nav.language': 'Interface language',

  'module.overview': 'Overview',
  'module.map': 'Heat map',
  'module.products': 'Products',
  'module.batches': 'Batches',
  'module.alerts': 'Alerts and cases',
  'module.labels': 'Labels',
  'module.analytics': 'Analytics',
  'module.integrations': 'Integrations',
  'module.admin': 'Administration',
  'module.clients': 'Clients',
  'module.requests': 'Requests',

  'module.overview.description': 'Scans, authentication rate and open alerts.',
  'module.map.description': 'Where product is scanned and where anomalies appear.',
  'module.products.description': 'Catalogue, GTIN and security tier.',
  'module.batches.description': 'Batch status, activated vs. scanned, recall.',
  'module.alerts.description': 'Inbox, assignment, investigation and closure.',
  'module.labels.description': 'Orders, rolls, stock and codes.',
  'module.analytics.description': 'Trends, regional penetration and export.',
  'module.integrations.description': 'API keys, webhooks and delivery logs.',
  'module.admin.description': 'Users, roles, SSO and audit trail.',

  'state.loading': 'Loading…',
  'state.empty.title': 'Nothing to show',
  'state.empty.detail': 'Data will appear here once there is any.',
  'state.error.title': 'Could not load',
  'state.error.retry': 'Retry',
  'state.error.trace': 'Support reference',
  'state.loadMore': 'Load more',
  'state.loadingMore': 'Loading more…',
  'state.allLoaded': 'No more results.',
  'state.refreshing': 'Refreshing…',

  'guard.forbidden.title': 'You do not have access to this',
  'guard.forbidden.detail':
    'This section requires the {required} role. Yours is {actual}. Ask your brand administrator for it.',
  'guard.forbidden.back': 'Back to overview',

  'error.network': 'No connection to the API.',
  'error.network.hint': 'Check your network. The dashboard retries on its own.',
  'error.timeout': 'The request took too long.',
  'error.timeout.hint': 'Try again or narrow the date range.',
  'error.unauthorized': 'Your session is not valid.',
  'error.invalidCredentials': 'Wrong user, password or brand.',
  'error.forbidden': 'Your role does not allow this operation.',
  'error.backofficeOnly': 'Buying and generating labels is done by XYVONA.',
  'error.backofficeOnly.hint': 'Your brand requests rolls from Labels › Requests and receives them already generated.',
  'error.notFound': 'We could not find what you were looking for.',
  'error.conflict': 'The record changed while you were editing it.',
  'error.validation': 'Some fields have invalid values.',
  'error.rateLimited': 'Too many requests. Wait a few seconds.',
  'error.internal': 'Internal platform error.',
  'error.internal.hint': 'Give the reference to support so they can find it in the log.',
  'error.invalidCursor': 'Pagination went stale. Reload the list.',
  'error.unknown': 'Something unexpected happened.',

  'users.title': 'Users',
  'users.description': 'Who has access to this brand dashboard.',
  'users.column.name': 'Name',
  'users.column.email': 'Email',
  'users.column.role': 'Role',
  'users.column.status': 'Status',
  'users.column.lastLogin': 'Last sign-in',
  'users.status.active': 'Active',
  'users.status.inactive': 'Inactive',
  'users.never': 'Never',
  'users.empty': 'This brand has no users yet.',
  'users.count': '{count} users loaded',

  'users.column.authMode': 'Authentication',
  'users.column.actions': 'Actions',
  'users.authMode.password': 'Password',
  'users.authMode.oidc': 'Directory (OIDC)',
  'users.new': 'New user',

  'users.filter.title': 'Search and filter',
  'users.filter.description':
    'With two hundred users, "load more" is not navigation. The three filters are resolved by the API.',
  'users.filter.search': 'Search',
  'users.filter.searchHint': 'Searches email and name. Press Enter to apply.',
  'users.filter.searchPlaceholder': 'maria, @labfarma…',
  'users.filter.searchAction': 'Search',
  'users.filter.role': 'Role',
  'users.filter.allRoles': 'All roles',
  'users.filter.status': 'Status',
  'users.filter.allStatuses': 'Active and inactive',
  'users.filter.empty': 'No user matches the filter.',

  'users.form.title': 'New user',
  'users.form.description':
    'The server generates the password and shows it once. The email is unique within this brand.',
  'users.form.email': 'Email',
  'users.form.emailHint': 'They will sign in with this email plus the brand name.',
  'users.form.emailInvalid': 'Enter a valid email address.',
  'users.form.name': 'Name',
  'users.form.nameHint': 'How they appear in the table and in alert assignments.',
  'users.form.role': 'Role',
  'users.form.submit': 'Create user',
  'users.form.submitting': 'Creating…',

  'users.action.self': 'You',
  'users.action.federated': 'Password in the directory',
  'users.action.changeRole': 'Role for {email}',
  'users.action.deactivate': 'Deactivate',
  'users.action.reactivate': 'Reactivate',
  'users.action.reset': 'Reset password',

  'users.confirm.role.title': 'Change the role',
  'users.confirm.role.detail':
    '{who} becomes {role}. Their open sessions are closed and the change is recorded in the audit trail under your name.',
  'users.confirm.role.accept': 'Yes, change the role',
  'users.confirm.deactivate.title': 'Deactivate',
  'users.confirm.deactivate.detail':
    '{who} loses access now and their sessions are closed. The account is not deleted: the audit trail still has to say who did what.',
  'users.confirm.deactivate.accept': 'Yes, deactivate',
  'users.confirm.reactivate.title': 'Reactivate',
  'users.confirm.reactivate.detail': '{who} can sign in again with their previous password.',
  'users.confirm.reactivate.accept': 'Yes, reactivate',
  'users.confirm.reset.title': 'Reset the password',
  'users.confirm.reset.detail':
    'A temporary password is generated for {who} and all their sessions are closed. The temporary password is shown once.',
  'users.confirm.reset.accept': 'Yes, reset',

  'users.temp.title': 'Temporary password',
  'users.temp.detail':
    'Hand it to {email} over a secure channel. It is not shown again: if lost, reset it once more.',
  'users.temp.sessions': '{count} open sessions were closed.',
  'users.temp.dismiss': 'Done, copied',

  'module.activation': 'Activation',
  'module.activation.description': 'Packaging line: activate units and see what gets rejected.',

  'activation.title': 'Plant activation',
  'activation.description':
    'Simulates the packaging line: read codes, activate them against a production batch and see what gets rejected.',
  'activation.rule':
    'A unit does not authenticate until it is activated. Labels arrive from the factory already registered: if they were born valid, a roll stolen from the warehouse would authenticate counterfeit product. Activation is only allowed from RECEIVED, and always with the printed code — never with an internal identifier.',

  'activation.console.title': 'Line console',
  'activation.console.description':
    'One code per line, the way a scanner leaves them. Comma, semicolon and space separators also work.',

  'activation.field.batch': 'Production batch',
  'activation.field.batchHint': 'Units get bound to this batch: GTIN, lot and expiry.',
  'activation.field.batchPlaceholder': 'Pick a batch',
  'activation.field.batchBlocked': 'does not accept activations',
  'activation.field.line': 'Line',
  'activation.field.lineHint': 'Recorded on the activation run.',
  'activation.field.key': 'Idempotency key',
  'activation.field.keyHint':
    'Resending the same run under this key does not activate twice. Reusing the key with different content is an error and the API rejects it.',
  'activation.field.codes': 'Scanned codes',
  'activation.field.codesPlaceholder': '07VZFEF9G08Y8B57K165',
  'activation.field.codesHint': 'Up to 50,000 labels per run.',

  'activation.scan.ready': '{count} ready',
  'activation.scan.duplicates': '{count} repeated',
  'activation.scan.unreadable': '{count} unreadable',
  'activation.scan.unreadableHint': 'These do not fit the code format and are not sent:',

  'activation.submit': 'Activate run',
  'activation.missing.batch': 'To enable, pick the batch first.',
  'activation.missing.codes': 'To enable, paste the scanned codes in the field above.',
  'activation.missing.key': 'To enable, the idempotency key is missing.',
  'activation.submitting': 'Activating…',
  'activation.submit.offline': 'Store in the queue',

  'activation.offline.on': 'Offline',
  'activation.offline.off': 'Online',
  'activation.offline.disconnect': 'Simulate a network outage',
  'activation.offline.reconnect': 'Restore the network',
  'activation.offline.explain':
    'The line does not stop when the network drops: it buffers and resends. The same run resent does not activate twice.',
  'activation.offline.stored': 'The run is in the local queue. It goes out when the network is back.',
  'activation.offline.replaced': 'A run with that key was already queued and this one replaced it.',
  'activation.offline.queueTitle': 'Line queue',
  'activation.offline.queueDescription': '{runs} runs · {codes} labels waiting for network.',
  'activation.offline.queuedAt': 'Closed',
  'activation.offline.sync': 'Sync',
  'activation.offline.syncing': 'Syncing…',
  'activation.offline.discard': 'Discard',

  'activation.result.replayed': 'Already received',
  'activation.result.fresh': 'Activated now',
  'activation.result.replayedHint':
    'This run had already been received: the response is the stored one and nothing was activated again. That is how a resend after an outage does not duplicate.',
  'activation.result.requested': 'Sent',
  'activation.result.activated': 'Activated',
  'activation.result.already': 'Already active',
  'activation.result.rejected': 'Rejected',
  'activation.result.elapsed': 'Resolved in {ms} ms.',
  'activation.result.cacheStale':
    'The verification cache could not be invalidated. The units ARE activated; consumers may see the previous state for a few minutes. Resending the run retries the invalidation.',
  'activation.result.verifyHint':
    'Verify one of these labels as a consumer: from now on it must say genuine.',
  'activation.result.resend': 'Resend as is',
  'activation.result.open': 'See rejections',

  'activation.rejects.title': 'Rejected labels',
  'activation.rejects.description': 'Take them out of the box before it leaves the plant.',
  'activation.rejects.sample':
    'Showing {shown} of {total}. The full detail is audited on the run.',
  'activation.rejects.column.code': 'Code',
  'activation.rejects.column.outcome': 'Reason',
  'activation.rejects.column.existingBatch': 'Current batch',
  'activation.rejects.column.existingStatus': 'Status',

  'activation.outcome.activated': 'Activated',
  'activation.outcome.alreadyActivated': 'Already activated',
  'activation.outcome.duplicate': 'Repeated in the run',
  'activation.outcome.wrongState': 'Invalid state',
  'activation.outcome.otherBatch': 'From another batch',
  'activation.outcome.terminal': 'Voided, stolen or recalled',
  'activation.outcome.unknown': 'No such unit',
  'activation.outcome.invalidCode': 'Invalid code',

  'activation.why.activated': 'Moved from RECEIVED to ACTIVATED: it authenticates from now on.',
  'activation.why.alreadyActivated':
    'It was already activated against this same batch. That is what makes a resend idempotent.',
  'activation.why.duplicate': 'The same code came twice in the run.',
  'activation.why.wrongState': 'It never reached RECEIVED: the label did not go through the warehouse.',
  'activation.why.otherBatch': 'It was already activated against a different production batch.',
  'activation.why.terminal': 'Voided, stolen, recalled or destroyed: it does not return to the chain.',
  'activation.why.unknown': 'The code verifies, but no unit of this brand has that id.',
  'activation.why.invalidCode': 'The HMAC does not verify: forged or misread code.',

  'activation.runs.title': 'Received runs',
  'activation.runs.description': 'Every submission from the line, with what it activated and rejected.',
  'activation.runs.empty': 'No run has been activated yet.',
  'activation.runs.emptyHint': 'Pick a production batch, paste the scanned codes and activate.',
  'activation.runs.column.received': 'Received',
  'activation.runs.column.requested': 'Sent',

  'activation.detail.back': '← Back to activation',
  'activation.detail.description': 'Production batch #{batch} · line {line}',
  'activation.detail.rejected': '{count} rejected',
  'activation.detail.clean': 'No rejections',
  'activation.detail.clientSentAt': 'Line clock',
  'activation.detail.duration': 'Duration',
  'activation.detail.durationValue': '{ms} ms',

  // ───────────────── activation modes A and C (#79) and exceptions (#80)
  'activation.modes.link': 'Modes A and C · range and inline',
  'activation.modes.title': 'Range and line',
  'activation.modes.description':
    'The other two activation mechanisms, for customers that do not use a batch scanner.',
  'activation.modes.back': '← Back to batch activation',
  'activation.modes.rule':
    'Three modes, one result for the consumer: a unit activated by any of them authenticates the same, and all three leave the same audit log entry. What changes is who can use which.',
  'activation.modes.badge.range': 'Mode A',
  'activation.modes.badge.line': 'Mode C',
  'activation.modes.badge.exceptions': 'Terminal',

  'activation.range.title': 'Mode A · activate by range',
  'activation.range.description':
    'For producers without automation: “roll 47, serials 1,000,000 to 1,010,000 → batch L2026-0801”.',
  'activation.range.from': 'Serial from',
  'activation.range.to': 'Serial to',
  'activation.range.width': '{count} units in the range. The range must come from an issued roll, or the API rejects it.',
  'activation.range.submit': 'Activate the range',
  'activation.range.order':
    'Declared exceptions are voided first and the range is activated after, in the same transaction. The other way round is impossible: an activated unit cannot be voided.',
  'activation.range.inRange': 'In range',
  'activation.range.exceptionRef': 'Exception record #{id}.',

  'activation.exceptions.title': 'Exception record',
  'activation.exceptions.description':
    'Mark labels as voided: damaged, misapplied, skipped or discarded during printing.',
  'activation.exceptions.legend': 'Exceptions for this range',
  'activation.exceptions.rule':
    'Required. Activating a range without declaring discards leaves units activated with no real product behind them: they authenticate as genuine and correspond to nothing. If the roll was applied in full, that gets signed too.',
  'activation.exceptions.terminal':
    'Voiding is terminal and cannot be undone. Only a label that has not been activated yet can be voided; for one already in the market it is a batch recall or a documented destruction.',
  'activation.exceptions.mode.none': 'No discards (I sign for it)',
  'activation.exceptions.mode.ranges': 'By serial ranges',
  'activation.exceptions.mode.codes': 'By scanned codes',
  'activation.exceptions.reason': 'Reason',
  'activation.exceptions.note': 'Detail',
  'activation.exceptions.noteHint': 'Required when the reason is “other”.',
  'activation.exceptions.ranges': 'Discarded ranges',
  'activation.exceptions.rangesPlaceholder': '1000010-1000024',
  'activation.exceptions.rangesHint': '{tramos} ranges · {count} units. One per line: “1000-1010” or “1000”.',
  'activation.exceptions.codes': 'Discarded codes',
  'activation.exceptions.invalidRanges': 'Some lines are not ranges and will not be sent: {list}',
  'activation.exceptions.outside':
    'Some ranges fall outside the range being activated. They would void labels from another roll.',
  'activation.exceptions.byRange': 'Void by serial range instead of by codes',
  'activation.exceptions.voided': 'Voided',
  'activation.exceptions.submit': 'Record and void',
  'activation.exceptions.rejects': 'Could not be voided',

  'activation.reason.damaged': 'Damaged label',
  'activation.reason.misapplied': 'Misapplied',
  'activation.reason.skipped': 'Skipped by the applicator',
  'activation.reason.printDiscard': 'Discarded during printing',
  'activation.reason.orderCancelled': 'Order cancelled',
  'activation.reason.other': 'Other (explain)',

  'activation.voidOutcome.voided': 'Voided',
  'activation.voidOutcome.alreadyVoided': 'Already voided',
  'activation.voidOutcome.activated': 'Already activated: cannot be voided',
  'activation.voidOutcome.terminal': 'Stolen, recalled or destroyed',
  'activation.voidOutcome.wrongState': 'State that does not allow voiding',
  'activation.voidOutcome.unknown': 'No unit',
  'activation.voidOutcome.invalidCode': 'Invalid code',

  'activation.line.title': 'Mode C · activate inline',
  'activation.line.description':
    'The line vision system, one unit or one pack per call.',
  'activation.line.code': 'Scanned code',
  'activation.line.codeHint': 'Enter activates. Several codes separated by spaces are a pack.',
  'activation.line.submit': 'Activate',
  'activation.line.p50': 'p50 {ms} ms',
  'activation.line.samples': '{count} scans measured',
  'activation.line.budget':
    'A line running at 300 units per minute is 5 per second. If the API takes longer than that, the line slows down — and the customer will not accept that.',
  'activation.line.failSafe':
    'If XYVONA does not answer, the vision system queues locally and the line keeps running. Never the other way round.',
  'activation.detail.notFound': 'That activation run does not exist.',

  'activation.conflicts.title': 'Audited rejections',
  'activation.conflicts.description':
    'The trail an auditor asks for: what was activated, against which batch, and what state the unit was really in.',
  'activation.conflicts.empty': 'This run had no rejections.',
  'activation.conflicts.emptyHint': 'Every label sent was in RECEIVED state and got activated.',
  'activation.conflicts.column.case': 'Case',
  'activation.conflicts.column.reason': 'Reason',
  'activation.conflicts.column.attempted': 'Attempted batch',
  'activation.conflicts.column.existing': 'Current batch',
  'activation.conflicts.column.status': 'Unit status',
  'activation.conflicts.column.when': 'Recorded',

  'activation.unitStatus.created': 'Created',
  'activation.unitStatus.printed': 'Printed',
  'activation.unitStatus.shipped': 'Shipped',
  'activation.unitStatus.received': 'Received',
  'activation.unitStatus.activated': 'Activated',
  'activation.unitStatus.inMarket': 'In market',
  'activation.unitStatus.verified': 'Verified',
  'activation.unitStatus.voided': 'Voided',
  'activation.unitStatus.stolen': 'Stolen',
  'activation.unitStatus.recalled': 'Recalled',
  'activation.unitStatus.destroyed': 'Destroyed',

  'alerts.inbox.title': 'Alert inbox',
  'alerts.inbox.description':
    'Every alert shows the evidence that fired it. Keyboard driven: press ? for help.',
  'alerts.case': 'Case #{id}',
  'alerts.unassigned': 'Unassigned',
  'alerts.yes': 'yes',
  'alerts.no': 'no',
  'alerts.column.batch': 'Batch',

  'alerts.summary.open': 'Open',
  'alerts.summary.unassigned': 'Unassigned',
  'alerts.summary.mine': 'Mine',

  'alerts.filter.status': 'Status',
  'alerts.filter.severity': 'Severity',
  'alerts.filter.severityFrom': '{severity} or above',
  'alerts.filter.type': 'Rule',
  'alerts.filter.assignment': 'Assignment',
  'alerts.filter.sort': 'Order',
  'alerts.filter.sortSeverity': 'Most severe first',
  'alerts.filter.sortRecent': 'Most recent first',
  'alerts.filter.window': 'Period',
  'alerts.filter.window1': 'Last 24 h',
  'alerts.filter.window7': 'Last 7 days',
  'alerts.filter.window30': 'Last 30 days',
  'alerts.filter.window90': 'Last 90 days',
  'alerts.filter.windowAll': 'All time',
  'alerts.filter.any': 'All',
  'alerts.filter.mine': 'Assigned to me',
  'alerts.filter.unassigned': 'Unassigned',
  'alerts.filter.reset': 'Clear filters',

  'alerts.list.title': 'Cases',
  'alerts.list.description': 'Sorted by severity: what needs attention comes first.',
  'alerts.list.count': '{count} loaded',
  'alerts.list.empty': 'No alerts match these filters.',
  'alerts.list.emptyHint': 'Widen the period or drop the status filter.',

  'alerts.severity.critical': 'Critical',
  'alerts.severity.high': 'High',
  'alerts.severity.medium': 'Medium',
  'alerts.severity.low': 'Low',
  'alerts.severity.info': 'Info',

  'alerts.status.open': 'Open',
  'alerts.status.investigating': 'Investigating',
  'alerts.status.confirmed': 'Confirmed',
  'alerts.status.dismissed': 'Dismissed',

  'alerts.type.notActivated': 'Code not activated',
  'alerts.type.stolenRoll': 'Stolen roll',
  'alerts.type.recalled': 'Recalled batch',
  'alerts.type.secretReuse': 'Hidden code reuse',
  'alerts.type.impossibleTravel': 'Impossible travel',
  'alerts.type.territoryDiversion': 'Territory diversion',
  'alerts.type.geoCluster': 'Out-of-zone cluster',
  'alerts.type.duplicateScan': 'Duplicate scan',
  'alerts.type.velocitySpike': 'Scan velocity spike',
  'alerts.type.lowDeviceDiversity': 'Low device diversity',
  'alerts.type.datacenterAsn': 'Datacenter ASN',

  'alerts.resolution.counterfeit': 'Counterfeit confirmed',
  'alerts.resolution.diversion': 'Diversion confirmed',
  'alerts.resolution.theft': 'Theft confirmed',
  'alerts.resolution.falsePositive': 'False positive',
  'alerts.resolution.consumerError': 'Consumer error',
  'alerts.resolution.testScan': 'Test scan',
  'alerts.resolution.noAction': 'No action',

  'alerts.evidence.title': 'Evidence',
  'alerts.evidence.empty': 'The rule left no evidence fields.',
  'alerts.evidence.hidden': 'hidden: internal identifier',
  'alerts.evidence.unknownPlace': 'unknown place',

  'alerts.travel.first': 'First scan',
  'alerts.travel.second': 'Second scan',
  'alerts.travel.distance': 'Distance',
  'alerts.travel.elapsed': 'Time between scans',
  'alerts.travel.speed': 'Implied speed',
  'alerts.travel.limit': 'Rule threshold',
  'alerts.travel.km': '{value} km',
  'alerts.travel.kmh': '{value} km/h',
  'alerts.travel.minutes': '{value} min',
  'alerts.travel.plotLabel': 'The two scans: {from} and {to}',
  'alerts.travel.approximate': 'Approximate location, city level.',

  'alerts.detail.none': 'No case selected',
  'alerts.detail.noneHint': 'Pick one from the list or press j to start with the most severe.',
  'alerts.detail.missing': 'That case does not exist or belongs to another brand.',
  'alerts.detail.opened': 'Opened on {when}',
  'alerts.detail.context': 'Context',
  'alerts.detail.territory': 'Territory',
  'alerts.detail.relatedUnit': 'Other alerts on the unit',
  'alerts.detail.relatedBatch': 'Other alerts on the batch',
  'alerts.detail.assignee': 'Assigned to',
  'alerts.detail.resolution': 'Closing reason',
  'alerts.detail.actions': 'Case management',
  'alerts.detail.readOnly':
    'Your role reads the inbox but does not operate it. Assigning, closing and annotating need the analyst role.',

  'alerts.action.assignSelf': 'Assign to me',
  'alerts.action.unassign': 'Release',
  'alerts.action.assignTo': 'Assign to',
  'alerts.action.moveTo': 'Move to {status}',
  'alerts.action.note': 'Add note',

  'alerts.notes.title': 'Investigation notes',
  'alerts.notes.empty': 'No notes yet.',
  'alerts.history.title': 'Case history',
  'alerts.history.hint': 'Comes from the hash-chained audit log, the same one an auditor reads.',
  'alerts.history.empty': 'No recorded moves.',

  'alerts.dialog.statusTitle': 'Move the case to {status}',
  'alerts.dialog.noteTitle': 'Add a note',
  'alerts.dialog.resolution': 'Closing reason',
  'alerts.dialog.note': 'Note',
  'alerts.dialog.noteOptional': 'Note (optional)',
  'alerts.dialog.submit': 'Save',
  'alerts.dialog.saving': 'Saving…',
  'alerts.dialog.cancel': 'Cancel',
  'alerts.dialog.submitHint': 'Ctrl+Enter saves · Esc cancels. Notes cannot be edited or deleted.',

  'alerts.shortcuts.title': 'Keyboard shortcuts',
  'alerts.shortcuts.open': 'Shortcuts (?)',
  'alerts.shortcut.next': 'Next case',
  'alerts.shortcut.previous': 'Previous case',
  'alerts.shortcut.first': 'First / last',
  'alerts.shortcut.last': 'Last case',
  'alerts.shortcut.investigate': 'Mark as investigating',
  'alerts.shortcut.confirm': 'Confirm the case',
  'alerts.shortcut.dismiss': 'Dismiss the case',
  'alerts.shortcut.reopen': 'Reopen the case',
  'alerts.shortcut.refresh': 'Refresh the inbox',
  'alerts.shortcut.help': 'This help',
  'alerts.shortcut.close': 'Close the dialog',

  'placeholder.title': 'Module under construction',
  'placeholder.detail':
    'This section is built in issue #{issue}. The scaffold — session, navigation, API client and states — is already in place.',
  'placeholder.issue': 'Issue',

  'notFound.title': 'That page does not exist',
  'notFound.detail': 'The link may be out of date.',

  'confirm.cancel': 'Cancel',
  'confirm.accept': 'Confirm',
  'confirm.typeToConfirm': 'Type {word} to confirm.',

  'admin.sections': 'Administration sections',
  'admin.tab.users': 'Users',
  'admin.tab.audit': 'Audit trail',
  'admin.tab.portal': 'Consumer portal',

  'users.roles.title': 'What each role can do',
  'users.roles.admin': 'Manages users and keys, and reads the audit trail.',
  'users.roles.analyst': 'Operates batches, alerts and cases; requests exports and reports.',
  'users.roles.viewer': 'Read only: dashboards, batches and the alert inbox.',
  'users.roles.audited':
    'Every user added, removed or re-roled lands in the audit trail, with who did it and when.',
  'users.roles.seeAudit': 'See access changes',

  'audit.title': 'Audit trail',
  'audit.description': 'Who did what, when, and whether the record is intact.',
  'audit.chain.title': 'Chain integrity',
  'audit.chain.checking': 'Recomputing the chain…',
  'audit.chain.ok': 'Chain intact',
  'audit.chain.broken': 'Chain broken',
  'audit.chain.okDetail': '{entries} entries recomputed and every one links to the previous.',
  'audit.chain.brokenDetail': '{broken} of {entries} entries do not link. The first is #{seq}.',
  'audit.chain.reason': 'Reason',
  'audit.chain.reason.contenido_alterado': 'The entry content changed after it was written.',
  'audit.chain.reason.enlace_roto': 'The entry does not link to the previous hash.',
  'audit.chain.reason.falta_una_fila': 'An entry is missing from the sequence.',
  'audit.chain.reason.genesis_incorrecto': 'The first entry of the chain is not the expected one.',
  'audit.chain.reason.hash_version_desconocida':
    'The entry uses a hash version this server does not know.',
  'audit.chain.reason.other': 'The server reports it as: {reason}',
  'audit.chain.anchor': 'Anchor',
  'audit.chain.anchorHint':
    'Write down the entry and its hash. If that pair is unchanged on your next visit, nobody rewrote the past.',
  'audit.chain.entries': 'Entries',
  'audit.chain.elapsed': 'Took',
  'audit.chain.recheck': 'Verify again',
  'audit.chain.rechecking': 'Verifying…',
  'audit.chain.formula': 'Hash formula',
  'audit.chain.empty': 'This brand has no audit entries yet.',

  'audit.filter.entityType': 'Entity type',
  'audit.filter.entityTypeHint': 'For example: batch, alert, api_key, user.',
  'audit.filter.action': 'Action',
  'audit.filter.apply': 'Apply',
  'audit.filter.clear': 'Clear',
  'audit.column.seq': 'Entry',
  'audit.column.when': 'When',
  'audit.column.action': 'Action',
  'audit.column.entity': 'Entity',
  'audit.column.actor': 'Who',
  'audit.entry.system': 'System',
  'audit.entry.details': 'Details of entry {seq}',
  'audit.entry.payload': 'Signed content',
  'audit.entry.prevHash': 'Previous hash',
  'audit.entry.rowHash': 'Entry hash',
  'audit.entry.hashVersion': 'Hash version',
  'audit.entry.broken': 'The chain breaks here',
  'audit.empty': 'No entries match those filters.',

  'portal.title': 'Consumer portal',
  'portal.description': 'The account where a buyer keeps the codes they verified.',
  'portal.state.enabled': 'Enabled',
  'portal.state.disabled': 'Not available',
  'portal.brand': 'Brand',
  'portal.unavailable.title': 'This brand has no consumer portal',
  'portal.reason.industry_restricted':
    'This is a pharmaceutical brand. Storing which medicines an identified person scanned is health data — a special category under GDPR, LGPD and Law 6534/2020 — so the portal does not exist for this kind of brand (ADR-011). It is not switched off: the endpoints answer that it does not exist.',
  'portal.reason.disabled_by_tenant': 'The brand has not enabled the portal. It is off by default.',
  'portal.reason.unknown': 'The server does not offer the portal for this brand.',
  'portal.preview.title': 'What the buyer sees',
  'portal.preview.detail':
    'This is the portal screen against the real API. The session belongs to the buyer, not to you: it is not kept across reloads and shares nothing with your dashboard session.',
  'portal.login.title': 'Sign in',
  'portal.login.submit': 'Sign in',
  'portal.register.title': 'Create account',
  'portal.register.submit': 'Create the account',
  'portal.field.email': 'Email',
  'portal.field.password': 'Password',
  'portal.field.passwordHint': 'Ten characters minimum.',
  'portal.field.name': 'Name (optional)',
  'portal.notice':
    'Creating the account stores the consent together with the privacy notice version ({version}).',
  'portal.session.logout': 'Close the portal session',
  'portal.account.title': 'My account',
  'portal.account.email': 'Email',
  'portal.account.name': 'Name',
  'portal.account.created': 'Account created',
  'portal.codes.title': 'My codes',
  'portal.codes.description': 'The codes you verified, with the date you saved them.',
  'portal.codes.empty': 'You have not saved any code yet.',
  'portal.codes.column.ref': 'Reference',
  'portal.codes.column.label': 'Label',
  'portal.codes.column.result': 'Result',
  'portal.codes.column.saved': 'Saved',
  'portal.codes.column.actions': 'Actions',
  'portal.codes.add': 'Save a code',
  'portal.codes.field.code': 'QR code',
  'portal.codes.field.label': 'Label (optional)',
  'portal.codes.save': 'Save',
  'portal.codes.saving': 'Saving…',
  'portal.codes.delete': 'Delete',
  'portal.codes.delete.title': 'Delete this code',
  'portal.codes.delete.detail':
    'It leaves your list. The verification itself is not deleted: what disappears is its link to your account.',
  'portal.result.genuine': 'Verified',
  'portal.result.already_seen': 'Verified before',
  'portal.result.recalled': 'Recalled batch',
  'portal.rights.title': 'Your data',
  'portal.rights.export': 'Download my data',
  'portal.rights.exportHint':
    'A JSON with the account, the saved codes and the consents. That is the right of access.',
  'portal.rights.delete': 'Delete my account',
  'portal.rights.deleteHint':
    'Deletes the account, its sessions, its consents and the saved codes. That is the right to erasure.',
  'portal.delete.title': 'Delete the account',
  'portal.delete.detail':
    'The account is deleted and with it the link between that person and the codes they verified. It cannot be undone.',
  'portal.delete.confirmWord': 'DELETE',
  'portal.delete.done':
    'The account is gone. No link remains between that person and the codes they had saved.',

  'module.reports': 'Reports',
  'module.reports.description': 'Formal reports for a period, signed and exportable.',
  'reports.title': 'Formal reports',
  'reports.description':
    'A document for a period, with who generated it and when. Unlike interactive analytics, this is what gets archived.',
  'reports.form.period': 'Period',
  'reports.form.from': 'From',
  'reports.form.to': 'To',
  'reports.form.generate': 'Generate the report',
  'reports.form.regenerate': 'Generate again',
  'reports.period.last7': 'Last 7 days',
  'reports.period.last30': 'Last 30 days',
  'reports.period.last90': 'Last 90 days',
  'reports.period.custom': 'Custom',
  'reports.form.invalidRange': 'The start date must come before the end date.',
  'reports.doc.title': 'Product authentication report',
  'reports.doc.brand': 'Brand',
  'reports.doc.period': 'Period',
  'reports.doc.generatedBy': 'Generated by',
  'reports.doc.generatedAt': 'Generated on',
  'reports.doc.reference': 'Reference',
  'reports.doc.kpis': 'Period summary',
  'reports.doc.totalScans': 'Scans',
  'reports.doc.genuine': 'Verified',
  'reports.doc.suspicious': 'Suspicious',
  'reports.doc.suspiciousRate': 'Suspicion rate',
  'reports.doc.openAlerts': 'Open alerts',
  'reports.doc.criticalAlerts': 'Open critical alerts',
  'reports.doc.byResult': 'Scans by result',
  'reports.doc.result': 'Result',
  'reports.doc.scans': 'Scans',
  'reports.doc.share': 'Share',
  'reports.doc.countries': 'Countries with most scans',
  'reports.doc.country': 'Country',
  'reports.doc.daily': 'Daily activity',
  'reports.doc.day': 'Day',
  'reports.doc.units': 'Units',
  'reports.doc.refreshed': 'Aggregates as of {when}.',
  'reports.doc.privacy':
    'The data in this report is aggregated. It contains no personal data of consumers.',
  'reports.doc.footer':
    'Document generated by XYVONA. Its content can be checked against the audit trail for the period.',
  'reports.doc.empty': 'There were no scans in the selected period.',
  'reports.print': 'Print or save as PDF',
  'reports.result.genuine': 'Verified',
  'reports.result.already_seen': 'Already scanned',
  'reports.result.not_activated': 'Not activated',
  'reports.result.invalid': 'Not verifiable',
  'reports.result.stolen': 'Stolen roll',
  'reports.result.recalled': 'Recalled batch',
  'reports.result.out_of_territory': 'Out of territory',
  'reports.export.title': 'Exports',
  'reports.export.description':
    'The detail as CSV, to archive or cross with another system. Every request and every download lands in the audit trail.',
  'reports.export.kind': 'Content',
  'reports.export.format': 'Format',
  'reports.export.format.csv': 'CSV (UTF-8)',
  'reports.export.format.csv_excel': 'CSV for Excel',
  'reports.export.kind.alerts': 'Alerts',
  'reports.export.kind.batches': 'Batches',
  'reports.export.kind.units': 'Units',
  'reports.export.kind.scans_daily': 'Scans per day (aggregate)',
  'reports.export.kind.geo_cells': 'Geographic cells (aggregate)',
  'reports.export.request': 'Request the export',
  'reports.export.requesting': 'Requesting…',
  'reports.export.column.kind': 'Content',
  'reports.export.column.status': 'Status',
  'reports.export.column.rows': 'Rows',
  'reports.export.column.requestedBy': 'Requested by',
  'reports.export.column.created': 'Requested on',
  'reports.export.column.actions': 'Actions',
  'reports.export.status.queued': 'Queued',
  'reports.export.status.running': 'Generating',
  'reports.export.status.done': 'Ready',
  'reports.export.status.failed': 'Failed',
  'reports.export.status.expired': 'Expired',
  'reports.export.download': 'Download',
  'reports.export.downloading': 'Downloading…',
  'reports.export.empty': 'No export has been requested yet.',

  'map.title': 'Heat map',
  'map.description': 'Geographic cells aggregated by the server. Never individual scans.',
  'map.loading': 'Loading the map…',
  'map.cells': '{count} cells',
  'map.canvas.label':
    'Heat map of scans per geographic cell. The same information is in the table below.',
  'map.recenter': 'Recenter',
  'map.empty': 'No cells for these filters',
  'map.empty.detail':
    'Try a longer period, a coarser detail level, or a lower per-cell scan threshold.',
  'map.truncated':
    'The response hit the cell limit. Lower the detail level to see the whole period.',

  'map.filter.period': 'Period',
  'map.filter.days': '{days} days',
  'map.filter.layer': 'Layer',
  'map.filter.precision': 'Detail',
  'map.filter.minScans': 'Minimum per cell',
  'map.filter.alertOverlay': 'Overlay alerts',

  'map.layer.all': 'All scans',
  'map.layer.genuine': 'Genuine',
  'map.layer.suspicious': 'Suspicious',
  'map.layer.notActivated': 'Not activated',
  'map.layer.stolen': 'Stolen roll',
  'map.layer.recalled': 'Recalled batch',
  'map.layer.outOfTerritory': 'Out of territory',

  'map.precision.3': 'Country (~156 km)',
  'map.precision.4': 'Region (~39 km)',
  'map.precision.5': 'City (~4.9 km)',
  'map.precision.6': 'Neighbourhood (~1.2 km)',

  'map.legend.title': 'Scans per cell',
  'map.legend.alerts': 'Cells with suspicious scans',

  'map.stat.scans': 'Scans in period',
  'map.stat.cells': 'Cells drawn',
  'map.stat.suspicious': 'Suspicious scans',
  'map.stat.cellSize': 'Cell size',
  'map.stat.cellSizeValue': '{width} × {height} km',

  'map.footer.window': 'From {from} to {to}',
  'map.footer.refreshed': 'Aggregates as of {when}',
  'map.footer.queryMs': 'Query: {ms} ms',

  'map.table.title': 'Cells in period',
  'map.table.description':
    'The same information as the map, keyboard navigable. Each row focuses its cell.',
  'map.table.caption': 'Geographic cells ordered by scan count.',
  'map.table.cell': 'Cell',
  'map.table.country': 'Country',
  'map.table.scans': 'Scans',
  'map.table.share': 'Of total',
  'map.table.suspicious': 'Suspicious',
  'map.table.peakUnits': 'Units (peak day)',
  'map.table.focus': 'Focus the cell at {lat}, {lon}',
  'map.table.more': 'Show more ({shown} of {total})',
  'map.table.clearSelection': 'Clear selection',

  'analytics.title': 'Analytics',
  'analytics.description':
    'Trends, authentication rate and regional penetration for the selected period.',
  'analytics.period': 'Period',
  'analytics.days': '{days} days',
  'analytics.refresh': 'Recompute',
  'analytics.refreshing': 'Recomputing…',
  'analytics.refreshed': 'Recomputed: {rows} rows in {ms} ms.',
  'analytics.window': 'From {from} to {to}',
  'analytics.refreshedAt': 'Aggregates as of {when}',

  'analytics.kpi.scans': 'Scans',
  'analytics.kpi.authRate': 'Authentication rate',
  'analytics.kpi.authRateHint': '{genuine} genuine scans',
  'analytics.kpi.suspiciousRate': 'Suspicion rate',
  'analytics.kpi.suspiciousHint': '{scans} suspicious scans',
  'analytics.kpi.openAlerts': 'Open alerts',
  'analytics.kpi.criticalHint': '{count} critical',

  'analytics.chart.peak': 'max. {value}',
  'analytics.chart.empty': 'No scans in this period.',
  'analytics.chart.emptySuspicious': 'No suspicious scans in the period.',
  'analytics.trend.title': 'Trend',
  'analytics.trend.description':
    'Scans per bucket. The bucket widens on its own for long periods, so the chart always draws the same number of points.',
  'analytics.trend.scans': 'Scans',
  'analytics.trend.scansSummary': 'Scans per bucket: {points} points, {total} in total.',
  'analytics.trend.suspicious': 'Suspicious scans',
  'analytics.trend.suspiciousSummary':
    'Suspicious scans per bucket: {points} points, {total} in total.',
  'analytics.trend.showData': 'Show the numbers',
  'analytics.trend.tableCaption': 'Scans per bucket of the period.',
  'analytics.trend.column.period': 'Bucket',
  'analytics.trend.column.scans': 'Scans',
  'analytics.trend.column.genuine': 'Genuine',
  'analytics.trend.column.suspicious': 'Suspicious',
  'analytics.trend.column.units': 'Units',
  'analytics.trend.grain': 'Each point groups {days} day(s) · {points} points drawn.',

  'analytics.regions.title': 'Regional penetration',
  'analytics.regions.description': 'Where the product is scanned, by country.',
  'analytics.regions.country': 'Country',
  'analytics.regions.scans': 'Scans',
  'analytics.regions.share': 'Of total',
  'analytics.regions.suspicious': 'Suspicious',

  'analytics.results.title': 'Verification results',
  'analytics.results.description': 'How each scan of the period ended.',
  'analytics.results.result': 'Result',
  'analytics.results.scans': 'Scans',
  'analytics.results.share': 'Of total',

  'analytics.result.genuine': 'Genuine',
  'analytics.result.alreadySeen': 'Already scanned',
  'analytics.result.notActivated': 'Not activated',
  'analytics.result.invalid': 'Invalid code',
  'analytics.result.stolen': 'Stolen roll',
  'analytics.result.recalled': 'Recalled batch',
  'analytics.result.outOfTerritory': 'Out of territory',

  'analytics.tab.label': 'Analytics views',
  'analytics.tab.trends': 'Trends',
  'analytics.tab.exports': 'Exports',

  'analytics.export.new': 'New export',
  'analytics.export.newDescription':
    'The file is generated in the background. Once ready it shows up below with a download button.',
  'analytics.export.kind': 'Content',
  'analytics.export.format': 'Format',
  'analytics.export.format.csv': 'CSV (UTF-8)',
  'analytics.export.format.excel': 'CSV for Excel',
  'analytics.export.since': 'Since',
  'analytics.export.batch': 'Batch',
  'analytics.export.create': 'Generate',
  'analytics.export.creating': 'Requesting…',
  'analytics.export.privacy':
    'No export carries consumer data: they are aggregates and the brand inventory. Who requested and who downloaded are recorded in the audit trail.',
  'analytics.export.title': 'Exports',
  'analytics.export.description': 'The latest exports of this brand and who requested them.',
  'analytics.export.working': 'Generating…',
  'analytics.export.empty': 'No exports yet',
  'analytics.export.emptyHint': 'Generate one from the form above.',
  'analytics.export.download': 'Download',
  'analytics.export.downloaded': '{name} downloaded.',

  'analytics.export.kind.scansDaily': 'Scans per day',
  'analytics.export.kind.geoCells': 'Geographic cells',
  'analytics.export.kind.alerts': 'Alerts',
  'analytics.export.kind.batches': 'Batches',
  'analytics.export.kind.units': 'Units',
  'analytics.export.kind.labels': 'Labels (filtered list)',

  'analytics.export.status.queued': 'Queued',
  'analytics.export.status.running': 'Generating',
  'analytics.export.status.done': 'Ready',
  'analytics.export.status.failed': 'Failed',
  'analytics.export.status.expired': 'Expired',

  'analytics.export.column.kind': 'Content',
  'analytics.export.column.status': 'Status',
  'analytics.export.column.rows': 'Rows',
  'analytics.export.column.requestedBy': 'Requested by',
  'analytics.export.column.created': 'Requested on',
  'analytics.export.column.expires': 'Expires',

  'form.required': 'This field is required.',
  'form.save': 'Save',
  'form.saving': 'Saving…',
  'form.cancel': 'Cancel',

  'products.title': 'Product catalogue',
  'products.description': 'SKU, GTIN and security tier of every presentation.',
  'products.count': '{count} products loaded',
  'products.empty': 'This brand has no products yet.',
  'products.empty.detail': 'The catalogue comes first: batches and label orders hang off a product.',
  'products.column.sku': 'SKU',
  'products.column.name': 'Name',
  'products.column.gtin': 'GTIN',
  'products.column.tier': 'Security tier',
  'products.column.risk': 'Risk',
  'products.column.status': 'Status',
  'products.column.created': 'Created',
  'products.column.updated': 'Last change',
  'products.active': 'Active',
  'products.inactive': 'Inactive',
  'products.back': '← Back to the catalogue',
  'products.new': 'New product',
  'products.new.title': 'New product',
  'products.new.description': 'SKU and name are required. The GTIN is validated against GS1.',
  'products.edit': 'Edit',
  'products.edit.title': 'Edit product',
  'products.deactivate': 'Deactivate',
  'products.deactivate.title': 'Deactivate the product',
  'products.deactivate.description': 'The product goes out of circulation. It is not deleted.',
  'products.deactivate.detail': '{sku} — {name} goes out of circulation: it will no longer be selectable for new batches or label orders. Existing batches and units are untouched — an auditor has to be able to see the catalogue as it was when that lot was serialised. It can be reactivated from Edit.',
  'products.deactivate.confirm': 'Deactivate the product',
  'products.reactivate': 'Reactivate',
  'products.column.deactivated': 'Deactivated',
  'products.deactivated.unknown': 'Before the date was recorded',
  'products.detail.badId': 'That product identifier is not valid.',
  'products.detail.gs1': 'GS1 element string (01)',
  'products.detail.batches': 'See this product batches →',
  'products.field.sku': 'SKU',
  'products.field.sku.hint': 'Unique within the brand. The one your ERP uses.',
  'products.field.name': 'Commercial name',
  'products.field.gtin': 'GTIN',
  'products.field.gtin.hint': 'GTIN-8, 12, 13 or 14. Normalised to 14 digits, check digit validated.',
  'products.field.tier': 'Security tier',
  'products.field.tier.hint': 'Calibrates the code-reuse rule for this product.',
  'products.field.active': 'Status',
  'products.field.active.hint': 'An inactive product accepts no new batches.',
  'products.gtin.none': 'no GTIN',
  'products.gtin.normalized': 'Will be stored as {gtin}.',
  'products.gtin.notDigits': 'A GTIN can only contain digits.',
  'products.gtin.length': 'A GTIN must have 8, 12, 13 or 14 digits (you typed {length}).',
  'products.gtin.checkDigit': 'The check digit is not valid: it should end in {expected}.',
  'products.tier.qr_only': 'QR only',
  'products.tier.qr_scratch': 'QR + scratch-off code',
  'products.tier.qr_nfc': 'QR + NFC',
  'products.tier.nfc': 'NFC only',

  'batches.title': 'Production batches',
  'batches.description': 'Status, territory, expiry and activated units of each batch.',
  'batches.count': '{count} batches loaded',
  'batches.empty': 'No batches match these filters.',
  'batches.empty.detail': 'Try clearing the product or status filter.',
  'batches.back': '← Back to batches',
  'batches.column.lot': 'Lot',
  'batches.column.product': 'Product',
  'batches.column.expiry': 'Expiry',
  'batches.column.territory': 'Territory',
  'batches.column.activated': 'Activated units',
  'batches.column.risk': 'Risk',
  'batches.column.status': 'Status',
  'batches.column.actions': 'Actions',
  'batches.column.created': 'Created',
  'batches.column.updated': 'Last change',
  'batches.territory.none': 'unassigned',
  'batches.activated.hint': 'Activated units are the ones that went through the packaging line. That is the real scope of a recall.',
  'batches.recalledAt': 'Recalled on',
  'batches.filter.title': 'Filters',
  'batches.filter.description': 'They live in the URL: the link shows the same list on the other end.',
  'batches.filter.product': 'Product',
  'batches.filter.status': 'Status',
  'batches.filter.allProducts': 'All products',
  'batches.filter.allStatuses': 'All statuses',
  'batches.status.open': 'Packaging',
  'batches.status.released': 'In market',
  'batches.status.recalled': 'Recalled',
  'batches.status.closed': 'Closed',
  'batches.new': 'New batch',
  'batches.new.title': 'New production batch',
  'batches.new.description': 'Product, lot code and expiry are the GS1 identity of the batch.',
  'batches.edit': 'Edit',
  'batches.edit.title': 'Edit batch',
  'batches.detail.badId': 'That batch identifier is not valid.',
  'batches.field.product': 'Product',
  'batches.field.product.hint': 'It cannot be changed later: it would rewrite the identity of already activated units.',
  'batches.field.lot': 'Lot code',
  'batches.field.lot.hint': 'This is GS1 (10). Unique per product.',
  'batches.field.expiry': 'Expiry date',
  'batches.field.expiry.hint': 'This is GS1 (17). Required: without it there is no expiry-driven recall.',
  'batches.field.territory': 'Territory',
  'batches.field.territory.hint': 'A registered territory code. Empty = no zone restriction.',
  'batches.field.status': 'Status',
  'batches.field.status.hint': 'Recall is not here: it has its own action, with confirmation.',
  'batches.recall': 'Recall',
  'batches.recall.title': 'Recall the batch from the market',
  'batches.recall.step1.description': 'Review the scope before continuing. This recall cannot be undone.',
  'batches.recall.step2.description': 'Type the lot code to confirm which batch is being recalled.',
  'batches.recall.scope.title': 'Recall scope',
  'batches.recall.scope.units': 'Activated units affected',
  'batches.recall.effects.title': 'What happens on confirm',
  'batches.recall.effects.consumer': 'Anyone scanning a unit of this batch will see an explicit health alert telling them not to consume the product.',
  'batches.recall.effects.alerts': 'Every later scan of this batch raises a critical alert in the inbox.',
  'batches.recall.effects.irreversible': 'There is no way to undo it, neither from the dashboard nor from the API.',
  'batches.recall.reason': 'Reason for the recall',
  'batches.recall.reason.hint': 'Recorded with your user and the time. Someone who was not part of this decision will read it.',
  'batches.recall.reason.tooShort': 'Write at least {min} characters explaining the reason.',
  'batches.recall.continue': 'Continue',
  'batches.recall.back': 'Back',
  'batches.recall.confirm': 'Recall from the market',
  'batches.recall.recalling': 'Recalling…',
  'batches.recall.confirm.summary': 'You are about to recall lot {lot} of {product}, with {units} activated units in the market.',
  'batches.recall.typeLot': 'Type {lot} to confirm',
  'batches.recall.typeLot.hint': 'Exactly as it is, including case and dashes.',
  'batches.recall.typeLot.mismatch': 'It does not match the lot code.',
  'batches.recall.done.title': 'Batch recalled',
  'batches.recall.done.close': 'Close',
  'batches.recall.done.detail': 'Lot {lot} has been recalled from the market.',
  'batches.recall.done.consumer': 'The consumer page already shows the health alert for this batch.',
  'batches.recall.done.audit': 'The action was recorded with your user, the reason and the time.',
  'batches.recalled.banner': 'Batch recalled from the market',
  'batches.recalled.detail': 'Lot {lot} was recalled on {when}. Consumers scanning it see a health alert.',

  'labels.title': 'Labels',
  'labels.tab.orders': 'Orders',
  'labels.tab.rolls': 'Rolls',
  'labels.tab.stock': 'Stock',
  'labels.tab.units': 'Codes',
  'labels.journey':
    'Order → Roll → Code (no batch yet) → Activation at the plant → Code with batch and product → Consumer scan.',

  // ────────────────── single label management (#89)
  'labels.units.title': 'Codes',
  'labels.units.description':
    'One label at a time: verdict, QR, status flags and actions.',
  'labels.units.count': '{count} labels',
  'labels.units.scope': 'Search by',
  'labels.units.scope.hint': 'The whole brand, or from a batch, a roll or a code.',
  'labels.units.scope.all': 'Whole brand',
  'labels.units.scope.batch': 'Batch',
  'labels.units.scope.roll': 'Roll',
  'labels.units.scope.code': 'Printed code',
  'labels.units.scope.value.id': 'Number',
  'labels.units.scope.value.code': 'Code',
  'labels.units.scope.apply': 'Search',
  'labels.units.filter.state': 'Status',
  'labels.units.filter.all': 'All',
  'labels.units.filter.verdict': 'Verdict',
  'labels.units.filter.product': 'Product',
  'labels.units.filter.scanned': 'Scanned',
  'labels.units.filter.scanned.yes': 'At least once',
  'labels.units.filter.scanned.no': 'Never',
  'labels.units.filter.alerts': 'Alerts',
  'labels.units.filter.alerts.open': 'With open alerts',
  'labels.units.filter.expires': 'Batch expires by',
  'labels.units.filter.expires.hint': 'Activated ones only: the rest have no batch yet.',
  'labels.units.export': 'Export this list',
  'labels.units.export.sending': 'Queuing…',
  'labels.units.export.queued': 'Export queued: see it in Analytics › Exports',
  'labels.units.export.failed': 'The export could not be queued.',
  'labels.units.noScope': 'Missing the number',
  'labels.units.noScope.detail':
    'You chose to search by batch, roll or code but did not say which. Fill it in, or go back to “Whole brand”.',
  'labels.units.batchRef': 'Batch #{id}',
  'labels.units.empty': 'No labels in that scope',
  'labels.units.empty.detail':
    'The batch may have no activated units yet, or the status filter may be letting none through.',
  'labels.units.column.code': 'Code',
  'labels.units.column.state': 'Status',
  'labels.units.column.verdict': 'Verdict',
  'labels.units.column.scans': 'Scans',
  'labels.units.column.lastScan': 'Last scan',
  'labels.units.column.activated': 'Activated',

  'labels.units.state.created': 'Created',
  'labels.units.state.printed': 'Printed',
  'labels.units.state.shipped': 'Shipped',
  'labels.units.state.received': 'Received',
  'labels.units.state.activated': 'Activated',
  'labels.units.state.inMarket': 'In market',
  'labels.units.state.verified': 'Verified',
  'labels.units.state.voided': 'Voided',
  'labels.units.state.stolen': 'Stolen',
  'labels.units.state.recalled': 'Recalled',
  'labels.units.state.destroyed': 'Destroyed',
  'labels.units.state.compromised': 'Counterfeit',

  'labels.units.verdict.genuine': 'Genuine',
  'labels.units.verdict.alreadySeen': 'Already verified',
  'labels.units.verdict.unverified': 'Does not verify',
  'labels.units.verdict.stolen': 'Stolen',
  'labels.units.verdict.compromised': 'Cloned code',
  'labels.units.verdict.recalled': 'Batch recalled',

  'labels.units.detail.description':
    'What the system answers today to anyone scanning this label.',
  'labels.units.detail.consumerUrl': 'QR URL',
  'labels.units.detail.scans': 'Scans',
  'labels.units.detail.openAlerts': 'Open alerts',
  'labels.units.detail.riskScore': 'Risk',
  'labels.units.detail.batch': 'Batch',
  'labels.units.detail.expiry': 'Expiry',
  'labels.units.detail.product': 'Product',
  'labels.units.detail.territory': 'Territory',
  'labels.units.detail.roll': 'Roll',
  'labels.units.detail.created': 'Created',
  'labels.units.detail.activated': 'Activated',
  'labels.units.detail.firstScan': 'First scan',
  'labels.units.detail.lastScan': 'Last scan',
  'labels.units.detail.from': 'Current status',
  'labels.units.identity.title': 'Identity and traceability',

  'labels.units.qr.alt': 'QR code for label {code}',
  'labels.units.qr.download': 'Download QR (SVG)',
  'labels.units.qr.downloading': 'Downloading…',
  'labels.units.qr.hint':
    'The QR carries that URL. Scanning it with a phone shows exactly what a consumer sees.',

  'labels.units.flags.title': 'Status flags',
  'labels.units.flags.description':
    'Every flag comes from a field in the model. The right column says which one.',
  'labels.units.flags.column.flag': 'Flag',
  'labels.units.flags.column.value': 'Value',
  'labels.units.flags.column.source': 'Where it comes from',
  'labels.units.flags.yes': 'Yes',
  'labels.units.flags.no': 'No',
  'labels.units.flag.scanned': 'Scanned',
  'labels.units.flag.scanned.source': 'It has at least one recorded scan.',
  'labels.units.flag.expired': 'Expired',
  'labels.units.flag.expired.source': 'The batch expiry date has passed.',
  'labels.units.flag.authenticates': 'Active',
  'labels.units.flag.authenticates.source':
    'The status authenticates: the only thing that makes a consumer read "genuine".',
  'labels.units.flag.suspicious': 'Suspicious',
  'labels.units.flag.suspicious.source': 'It has open, unresolved alerts.',
  'labels.units.flag.compromised': 'Counterfeit',
  'labels.units.flag.compromised.source':
    'The code was confirmed cloned or found on counterfeit product.',
  'labels.units.flag.stolen': 'Stolen',
  'labels.units.flag.stolen.source': 'This label or its roll was reported stolen.',
  'labels.units.flag.recalled': 'Recalled',
  'labels.units.flag.recalled.source': 'The unit or its batch is recalled from the market.',
  'labels.units.flag.voided': 'Voided',
  'labels.units.flag.voided.source': 'It was voided: wrong activation or discarded label.',
  'labels.units.flag.terminal': 'No way out',
  'labels.units.flag.terminal.source': 'The status is terminal: no action moves it.',
  'labels.units.flag.reappeared': 'Found',
  'labels.units.flag.reappeared.source':
    'It is out of circulation and was scanned again after being taken out.',

  'labels.units.actions.title': 'Actions',
  'labels.units.actions.description':
    'Only the ones the state machine allows from the current status are shown.',
  'labels.units.actions.terminal':
    'This label is in a terminal state: no action can move it.',
  'labels.units.action.stolen': 'Mark as stolen',
  'labels.units.action.stolen.effect':
    'The label becomes stolen and every later scan raises a critical alert.',
  'labels.units.action.compromised': 'Mark as counterfeit',
  'labels.units.action.compromised.effect':
    'The code is declared cloned and stops authenticating. If the legitimate product is still on the market, it stops verifying too.',
  'labels.units.action.void': 'Deactivate',
  'labels.units.action.void.effect':
    'The label is voided: it stops authenticating and does not return to circulation.',
  'labels.units.action.effect.audit':
    'It is recorded in the audit log with your user, the time and the reason.',
  'labels.units.action.effect.irreversible': 'This cannot be undone.',
  'labels.units.action.step1': 'What happens to the label and why you are doing it.',
  'labels.units.action.step2': 'Type the printed code to confirm.',
  'labels.units.action.typeCode': 'Label code',
  'labels.units.action.confirm': 'Apply',
  'labels.units.action.applying': 'Applying…',
  'labels.units.action.confirm.summary':
    'You are about to change the status of label {code}. This cannot be undone.',
  'labels.units.action.done.title': 'Done',
  'labels.units.action.done.close': 'Close',
  'labels.units.action.done.detail': 'Label {code} is now {state}.',
  'labels.units.action.done.audit': 'Audit entry #{seq}.',
  'labels.units.action.done.cacheWarning':
    'The verification service could not be notified. The change is already applied and will propagate within minutes.',
  'labels.units.reason': 'Reason',
  'labels.units.reason.hint':
    'What you verified. Someone who was not here will read it, maybe three years from now.',
  'labels.units.reason.tooShort': 'Write at least one sentence.',
  'labels.units.reference': 'Reference',
  'labels.units.reference.hint': 'Police report, file number or case id.',

  'labels.units.evidence.download': 'Download evidence package',
  'labels.units.evidence.downloading': 'Preparing…',
  'labels.units.evidence.privacy':
    'Includes the label record, scans with city-level geo, alerts and the roll custody chain. No personal data: no IP or device hashes, no exact coordinates. Who exported it is recorded.',

  'labels.units.history.title': 'Status history',
  'labels.units.history.description':
    'Append-only: the manual actions on this label, with their reason.',
  'labels.units.history.empty': 'This label had no manual actions.',
  'labels.units.history.column.when': 'When',
  'labels.units.history.column.change': 'Change',
  'labels.units.history.column.reason': 'Reason',
  'labels.units.history.column.actor': 'Who',
  'labels.units.history.column.audit': 'Audit',
  'labels.orders.title': 'Label orders',
  'labels.orders.description': 'What was ordered from the printer and where each order stands.',
  'labels.orders.count': '{count} orders loaded',
  'labels.orders.empty': 'No orders match this filter.',
  'labels.orders.empty.detail': 'An order starts as a draft and reserves no codes until it is generated.',
  'labels.orders.back': '← Back to orders',
  'labels.orders.new': 'New order',
  'labels.orders.new.title': 'New label order',
  'labels.orders.new.description': 'The order starts as a draft: no codes reserved yet and it can still be cancelled.',
  'labels.orders.create': 'Create order',
  'labels.orders.filter.status': 'Status',
  'labels.orders.filter.all': 'All statuses',
  'labels.orders.column.code': 'Order',
  'labels.orders.column.supplier': 'Supplier',
  'labels.orders.column.quantity': 'Labels',
  'labels.orders.column.rolls': 'Rolls',
  'labels.orders.column.serials': 'Serial range',
  'labels.orders.column.status': 'Status',
  'labels.orders.column.generated': 'Generated',
  'labels.orders.column.created': 'Created',
  'labels.order.status.draft': 'Draft',
  'labels.order.status.generating': 'Generating',
  'labels.order.status.generated': 'Generated',
  'labels.order.status.cancelled': 'Cancelled',
  'labels.order.badId': 'That order identifier is not valid.',
  'labels.order.serials.pending': 'assigned on generation',
  'labels.order.field.code': 'Order code',
  'labels.order.field.code.hint': 'The purchase order number sent to the supplier.',
  'labels.order.field.supplier': 'Supplier',
  'labels.order.field.quantity': 'Labels to manufacture',
  'labels.order.field.quantity.invalid': 'A whole number between 1 and {max}.',
  'labels.order.field.unitsPerRoll': 'Labels per roll',
  'labels.order.field.unitsPerRoll.invalid': 'A whole number between 1 and {max}.',
  'labels.order.field.product': 'Product',
  'labels.order.field.product.hint': 'Optional. Labels can be made before knowing which SKU they go to.',
  'labels.order.field.product.none': 'No product assigned',
  'labels.order.field.keyVersion': 'Key version',
  'labels.order.field.notes': 'Notes',
  'labels.order.rollPreview': 'This order produces {rolls} rolls for {units} labels.',
  'labels.order.rollPreview.unknown': 'Fill in quantity and labels per roll to see how many rolls come out.',
  'labels.order.generate': 'Generate codes',
  'labels.order.generate.section': 'Code generation',
  'labels.order.generate.section.hint': 'Reserves the serial range, creates the rolls and produces the encrypted file for the printer.',
  'labels.order.generate.title': 'Generate the codes for this order',
  'labels.order.generate.description': 'This is the operation that permanently reserves a range of codes.',
  'labels.order.generate.confirm': 'Generate now',
  'labels.order.generate.effect.units': 'Units are born in CREATED: a freshly made label authenticates nothing until it is activated on the packaging line.',
  'labels.order.generate.effect.range': 'The serial range is reserved and cannot be reused, not even by cancelling the order.',
  'labels.order.generate.effect.key': 'The printer file is encrypted before it leaves the process. The key is handed over outside the platform.',
  'labels.order.progress.title': 'Generating {units} labels…',
  'labels.order.progress.elapsed': 'Elapsed',
  'labels.order.progress.serverStatus': 'Server status',
  'labels.order.progress.seconds': '{seconds} s',
  'labels.order.progress.note': 'Generation runs inside a transaction, so there is no percentage to read: the server status is re-read every second and the real per-phase timings are shown when it finishes.',
  'labels.order.generated.title': 'Codes generated',
  'labels.order.generated.units': 'Units',
  'labels.order.generated.rolls': 'Rolls',
  'labels.order.generated.elapsed': 'Duration',
  'labels.order.generated.rate': 'Throughput',
  'labels.order.generated.rate.value': '{rate} units/s',
  'labels.order.cancel': 'Cancel order',
  'labels.order.cancel.title': 'Cancel the order',
  'labels.order.cancel.description': 'It can only be cancelled while it is a draft.',
  'labels.order.cancel.detail': 'Order {code} will be cancelled. A generated order is not cancelled: what gets voided there are the rolls, one by one.',
  'labels.order.cancel.confirm': 'Cancel the order',
  'labels.order.rolls.title': 'Rolls of this order',
  'labels.order.rolls.description': 'Each roll carries a contiguous serial range and no range overlaps another.',
  'labels.order.rolls.empty': 'This order has not produced rolls yet.',
  'labels.printFile.title': 'Printer file',
  'labels.printFile.description': 'Signed, temporary link to the encrypted variable-data file.',
  'labels.printFile.request': 'Request the download link',
  'labels.printFile.request.hint': 'The link is minted on the spot and expires: it is not cached.',
  'labels.printFile.notGenerated': 'This order has no file yet.',
  'labels.printFile.notGenerated.detail': 'The file is produced when the codes are generated.',
  'labels.printFile.key.title': 'The decryption key does not travel here',
  'labels.printFile.key.detail': 'It is handed to the supplier over a channel outside the platform. Do not send it the same way as this link: kept apart, an intercepted link gives access to no secret at all.',
  'labels.printFile.records': 'Records',
  'labels.printFile.bytes': 'Size (bytes)',
  'labels.printFile.expires': 'Expires in',
  'labels.printFile.expires.value': '{minutes} minutes',
  'labels.printFile.cipher': 'Cipher',
  'labels.printFile.sha256': 'File SHA-256',
  'labels.printFile.nonce': 'Nonce',
  'labels.printFile.download': 'Download the encrypted file',
  'labels.printFile.renew': 'Renew the link',
  'labels.rolls.title': 'Rolls',
  'labels.rolls.description': 'Physical label inventory and its chain of custody.',
  'labels.rolls.count': '{count} rolls loaded',
  'labels.rolls.empty': 'No rolls match this filter.',
  'labels.rolls.empty.detail': 'Rolls are created when the codes of an order are generated.',
  'labels.rolls.back': '← Back to rolls',
  'labels.rolls.filter.state': 'Custody state',
  'labels.rolls.filter.state.hint': 'Filter by "stolen" to see the reported rolls.',
  'labels.rolls.filter.all': 'All states',
  'labels.rolls.column.code': 'Roll',
  'labels.rolls.column.order': 'Order',
  'labels.rolls.column.serials': 'Serial range',
  'labels.rolls.column.units': 'Labels',
  'labels.rolls.column.state': 'State',
  'labels.rolls.column.supplier': 'Supplier',
  'labels.rolls.column.updated': 'Last event',
  'labels.roll.badId': 'That roll identifier is not valid.',
  'labels.roll.noSupplier': 'No supplier on record',
  'labels.roll.index': 'Roll number within the order',
  'labels.roll.carrier': 'Carrier',
  'labels.roll.tracking': 'Tracking reference',
  'labels.roll.printedAt': 'Printed',
  'labels.roll.shippedAt': 'Shipped',
  'labels.roll.receivedAt': 'Received',
  'labels.roll.workerStatus': 'Projected status (rules engine)',
  'labels.roll.state.ordered': 'Ordered',
  'labels.roll.state.printed': 'Printed',
  'labels.roll.state.shipped': 'Shipped',
  'labels.roll.state.received': 'Received',
  'labels.roll.state.in_use': 'In use',
  'labels.roll.state.consumed': 'Consumed',
  'labels.roll.state.stolen': 'Stolen',
  'labels.roll.state.destroyed': 'Destroyed',
  'labels.roll.state.voided': 'Voided',
  'labels.chain.title': 'Chain of custody',
  'labels.chain.interrupted': 'The chain was interrupted: the roll is "{state}".',
  'labels.transition.title': 'Move the roll',
  'labels.transition.description': 'Only the legitimate destinations from the current state are offered. Nothing points backwards: a correction is recorded as a new event.',
  'labels.transition.terminal': 'The roll is "{state}" and has no possible moves.',
  'labels.transition.to': 'New state',
  'labels.transition.to.hint': 'Theft is not here: it has its own action, with confirmation.',
  'labels.transition.location': 'Location',
  'labels.transition.reference': 'Reference',
  'labels.transition.reference.hint': 'Delivery note, tracking number or destruction certificate.',
  'labels.transition.carrier': 'Carrier',
  'labels.transition.tracking': 'Tracking reference',
  'labels.transition.note': 'Note',
  'labels.transition.submit': 'Record the movement',
  'labels.transition.done': 'Movement recorded: the roll is now "{to}" and it affected {units} labels.',
  'labels.custody.title': 'Custody history',
  'labels.custody.description': 'Append-only: events are neither edited nor deleted. An editable chain of custody answers nothing.',
  'labels.custody.empty': 'This roll has no custody events yet.',
  'labels.custody.empty.detail': 'The first event is recorded when the roll moves out of "Ordered".',
  'labels.custody.from': 'from {from}',
  'labels.custody.units': 'Labels affected',
  'labels.custody.location': 'Location',
  'labels.custody.reference': 'Reference',
  'labels.custody.actor': 'Recorded by',
  'labels.custody.actor.value': 'user #{id}',
  'labels.custody.complete': 'Start of the chain.',
  'labels.stolen.action': 'Report theft',
  'labels.stolen.title': 'Report the roll as stolen',
  'labels.stolen.step1.description': 'Review the scope and tell what happened. This cannot be undone.',
  'labels.stolen.step2.description': 'Type the roll code to confirm which one is being reported.',
  'labels.stolen.scope.units': 'Labels in the roll',
  'labels.stolen.effects.units': 'Every label of the roll that has not been activated yet becomes "stolen" and can no longer authenticate.',
  'labels.stolen.effects.alerts': 'Every later scan of any of those labels raises a critical alert, with the evidence of where it showed up.',
  'labels.stolen.effects.activated': 'Already activated units are left alone: legitimate product out in the market is not deactivated.',
  'labels.stolen.effects.irreversible': '"Stolen" is a dead end: the roll can never be moved again.',
  'labels.stolen.note': 'What happened',
  'labels.stolen.note.hint': 'Required. The critical alert this raises will be read by an investigator who was not here.',
  'labels.stolen.note.tooShort': 'Tell what happened, even in one line.',
  'labels.stolen.policeReport': 'Police report number',
  'labels.stolen.policeReport.hint': 'Optional, but it is what holds the case up in front of an auditor.',
  'labels.stolen.location': 'Where it was stolen',
  'labels.stolen.confirm': 'Report the theft',
  'labels.stolen.reporting': 'Recording the report…',
  'labels.stolen.confirm.summary': 'You are about to report roll {code} as stolen, with {units} labels.',
  'labels.stolen.typeCode': 'Type {code} to confirm',
  'labels.stolen.done.title': 'Theft recorded',
  'labels.stolen.done.detail': 'Roll {code} is marked as stolen and {units} labels can no longer authenticate.',
  'labels.stolen.done.next': 'From now on, any scan of those labels raises a critical alert in the inbox.',
  'labels.stolen.banner': 'Roll reported as stolen',
  'labels.stolen.banner.detail': 'The report was filed on {when}. Every scan of its labels raises a critical alert.',
  // programación de reportes (#70)
  'reports.schedule.title': 'Scheduled delivery',
  'reports.schedule.description':
    'The report is generated on the server and sent by email, with nobody logged in. Every delivery is recorded in the audit log.',
  'reports.schedule.adminOnly':
    'Scheduling a delivery is an admin action: it decides which brand data reaches other people, every week, without anyone looking again.',
  'reports.schedule.form.period': 'Period covered',
  'reports.schedule.form.cadence': 'How often',
  'reports.schedule.form.weekday': 'Day of the week',
  'reports.schedule.form.monthday': 'Day of the month',
  'reports.schedule.form.hour': 'Hour',
  'reports.schedule.form.recipients': 'Who receives it',
  'reports.schedule.form.recipientsHint':
    'Users of your brand only. An automated delivery to an outside mailbox would take brand data out every week with nobody reviewing it.',
  'reports.schedule.form.submit': 'Schedule',
  'reports.schedule.form.saving': 'Scheduling…',
  'reports.schedule.period.lastWeek': 'Last week',
  'reports.schedule.period.lastMonth': 'Last month',
  'reports.schedule.period.last30d': 'Last 30 days',
  'reports.schedule.cadence.weekly': 'Weekly',
  'reports.schedule.cadence.monthly': 'Monthly',
  'reports.schedule.weekday.1': 'Monday',
  'reports.schedule.weekday.2': 'Tuesday',
  'reports.schedule.weekday.3': 'Wednesday',
  'reports.schedule.weekday.4': 'Thursday',
  'reports.schedule.weekday.5': 'Friday',
  'reports.schedule.weekday.6': 'Saturday',
  'reports.schedule.weekday.7': 'Sunday',
  'reports.schedule.monthlyOn': 'Day',
  'reports.schedule.empty': 'No scheduled deliveries',
  'reports.schedule.emptyHint': 'Schedule one and the report goes out on its own, with the period already computed.',
  'reports.schedule.paused': 'Paused',
  'reports.schedule.column.what': 'Report',
  'reports.schedule.column.when': 'When it goes out',
  'reports.schedule.column.recipients': 'Recipients',
  'reports.schedule.column.next': 'Next delivery',
  'reports.schedule.column.actions': 'Actions',
  'reports.schedule.runNow': 'Run now',
  'reports.schedule.pause': 'Pause',
  'reports.schedule.resume': 'Resume',
  'reports.schedule.delete': 'Delete',
  'reports.schedule.runs.title': 'Deliveries',
  'reports.schedule.runs.description':
    'When it went out, to whom, and with what result. Deleting a schedule does not delete this record.',
  'reports.schedule.runs.column.period': 'Period',
  'reports.schedule.runs.column.status': 'Status',
  'reports.schedule.runs.column.recipients': 'Recipients',
  'reports.schedule.runs.column.trigger': 'Source',
  'reports.schedule.runs.column.when': 'When',
  'reports.schedule.runs.refresh': 'Refresh',
  'reports.schedule.runs.manual': 'Manual',
  'reports.schedule.runs.scheduled': 'Scheduled',
  'reports.schedule.run.queued': 'Queued',
  'reports.schedule.run.running': 'Generating',
  'reports.schedule.run.sent': 'Sent',
  'reports.schedule.run.failed': 'Failed',

  // ───────────────────────────── verification · consumer-facing copy (#90)
  'module.verification': 'Verification',
  'module.verification.description':
    'What someone who scans your product reads, in Spanish, English and Portuguese.',

  'verification.title': 'Public page messages',
  'verification.description':
    'What someone who just scanned your product reads. Anything you leave blank uses the XYVONA default text.',
  'verification.readOnly': 'Read only',
  'verification.save': 'Publish',
  'verification.saving': 'Publishing…',
  'verification.saved': 'Published. New scans already see it.',

  'verification.locale.es': 'Español',
  'verification.locale.en': 'English',
  'verification.locale.pt': 'Português',
  'verification.locale.hint':
    'Each language is stored separately: whatever you leave blank in one uses the default text IN THAT language, never another one.',

  'verification.field.title': 'Title',
  'verification.field.body': 'Text',
  'verification.field.bodyHint': 'Blank = the default text.',
  'verification.field.bodyVars':
    'You can use {date} and {place}: they are replaced with the date and city of the first check.',
  'verification.field.defaultPlaceholder': 'Blank: the default text is used',

  'verification.slot.seeIt': 'See it',
  'verification.slot.genuine': 'Product verified',
  'verification.slot.genuine.hint':
    'First check of an activated unit. It is the only state that asserts authenticity.',
  'verification.slot.already': 'Already checked',
  'verification.slot.already.hint':
    'The code is valid but someone already scanned it. It is not an accusation: it may have been the same person.',
  'verification.slot.unverified': 'We could not verify it',
  'verification.slot.unverified.hint':
    'Unreadable code, damaged label, unit not activated yet, or a roll reported missing. All four share this message on purpose.',
  'verification.slot.recalled': 'Batch recalled (health alert)',
  'verification.slot.recalled.hint':
    'The only message that does assert something. It must say what to do with the product and who to call.',
  'verification.slot.throttled': 'Too many attempts',
  'verification.slot.throttled.hint':
    'Shown when several reports are sent in a row from the same device. It has no preview state of its own.',
  'verification.slot.contact': 'Footer: how to reach you',
  'verification.slot.contact.hint':
    'The heading of the reporting channel. It appears in the states that offer to contact you; phone and email come from your brand settings.',

  'verification.preview.title': 'Preview',
  'verification.preview.description': 'The real page, with sample data.',
  'verification.preview.states': 'State to preview',
  'verification.preview.frameTitle': 'Consumer page, preview',
  'verification.preview.note':
    'This is the public page in demo mode: it queries no real unit or batch, and unpublished text is only visible here.',
  'verification.state.genuine': 'Verified',
  'verification.state.already_seen': 'Already checked',
  'verification.state.unverified': 'Not verified',
  'verification.state.recalled': 'Batch recalled',

  'verification.rule.title': 'Why there is no "not genuine" message',
  'verification.rule.what':
    'Whoever scans is never told their product is illegitimate. The worst possible message is "we could not verify this code", together with how to reach you.',
  'verification.rule.why':
    'A wrong notice with that wording tells a legitimate buyer that what they are holding is not. Getting it wrong once is a legal problem for your brand and for XYVONA. And warning about a theft to whoever is holding the product either accuses a good-faith buyer, or tips off the person who should not be tipped off.',
  'verification.rule.where':
    'You do get the certainty: the unit, the batch, where and when it was scanned and the full evidence show up in your alert inbox.',
  'verification.rule.recall':
    'The only exception is a batch recall: there the risk of staying silent outweighs the risk of speaking, and the message is shown as a health alert.',

  'verification.rejected.field': 'Cannot be published: {terms}.',
  'verification.rejected.item': '{locale} · {slot} · {field}: {terms}',


  // ───────────────────────────────── back-office (#109)
  'module.clients.description': 'Onboarding, suspension and per-client metrics.',
  'module.requests.description': 'What clients ask for: label rolls.',
  'clients.title': 'Clients',
  'clients.description': 'The brands operating on XYVONA.',
  'clients.new': 'Onboard a client',
  'clients.count': '{count} clients',
  'clients.empty': 'No clients yet.',
  'clients.col.client': 'Client',
  'clients.col.industry': 'Industry',
  'clients.col.units': 'Units',
  'clients.col.activated': 'Activated',
  'clients.col.users': 'Users',
  'clients.col.lastScan': 'Last scan',
  'clients.col.state': 'State',
  'clients.state.active': 'Active',
  'clients.state.suspended': 'Suspended',
  'clients.suspend': 'Suspend',
  'clients.reactivate': 'Reactivate',
  'clients.suspend.confirm': 'Suspending cuts access for ALL users of this brand immediately. Labels already printed keep verifying as genuine.',
  'clients.suspend.reason': 'Reason (goes into the audit log)',
  'clients.suspend.reasonHint': 'At least {min} characters: whoever has to explain this will read it.',
  'clients.suspend.failed': 'The status could not be changed. Try again.',
  'clients.reactivate.confirm': 'Reactivating gives access back to ALL users of this brand on their next login.',
  'clients.form.slug': 'Identifier',
  'clients.form.slugHint': 'What they log in with. Cannot be changed later.',
  'clients.form.legalName': 'Legal name',
  'clients.form.country': 'Country',
  'clients.form.industry': 'Industry',
  'clients.form.portal': 'Consumer portal',
  'clients.form.portalPharma': 'Forbidden in pharma: which medicines an identified person scanned is health data.',
  'clients.form.adminEmail': 'First administrator email',
  'clients.form.adminName': 'Name',
  'clients.form.submit': 'Create client',
  'clients.created.title': 'Client created',
  'clients.created.password': 'Temporary password',
  'clients.created.once': 'Shown ONCE. If lost, reset it — it cannot be retrieved.',
  'clients.created.copy': 'Copy',
  'clients.created.copied': 'Copied',
  'requests.title': 'Roll requests',
  'requests.description': 'What clients asked for. Pending first, oldest on top.',
  'requests.empty': 'No requests.',
  'requests.filter.all': 'All',
  'requests.filter.pending': 'Pending',
  'requests.col.client': 'Client',
  'requests.col.quantity': 'Units',
  'requests.col.neededBy': 'Needed by',
  'requests.col.notes': 'Notes',
  'requests.col.state': 'State',
  'requests.state.pending': 'Pending',
  'requests.state.approved': 'Approved',
  'requests.state.fulfilled': 'Fulfilled',
  'requests.state.rejected': 'Rejected',
  'requests.approve': 'Approve',
  'requests.reject': 'Reject',
  'requests.fulfill': 'Mark fulfilled',
  'requests.resolution': 'Reply to the client',
  'stock.title': 'Label stock',
  'stock.description': 'Rolls owned by XYVONA, not assigned yet.',
  'stock.empty': 'No rolls in stock. Generate an order under Labels.',
  'stock.showAll': 'Show delivered too',
  'stock.col.roll': 'Roll',
  'stock.col.order': 'Order',
  'stock.col.supplier': 'Supplier',
  'stock.col.units': 'Units',
  'stock.col.range': 'Serials',
  'stock.col.state': 'Custody',
  'stock.col.owner': 'Assigned to',
  'stock.assign': 'Deliver to a client',
  'stock.assign.title': 'Deliver roll',
  'stock.assign.client': 'Client',
  'stock.assign.warning': 'Moves the roll and its units to the client. Recorded in both audit chains and not undoable from here.',
  'stock.assign.submit': 'Deliver',
  'stock.assigned': '{count} units delivered.',
  'hq.free': 'In stock',
  'hq.close': 'Close',
  'labels.verify.request': 'Verify the file',
  'labels.verify.request.hint': 'Decrypts it in memory and compares it against the database: every code and every secret. This is what to check before sending it to the supplier.',
  'labels.verify.ok': 'The file matches the units in the database.',
  'labels.verify.fail': 'The file does NOT match. Do not send it.',
  'labels.verify.records': 'Records',
  'labels.verify.checked': 'Checked',
  'labels.verify.sampled': 'sample',
  'labels.verify.codes': 'Invalid codes',
  'labels.verify.secrets': 'Mismatched secrets',
  'labels.verify.sha256': 'Object integrity',
  'labels.verify.sha256.ok': 'matches what was recorded',
  'labels.verify.sha256.fail': 'does NOT match: the file changed',
  'labels.verify.preview': 'First lines of the file',
  'labels.verify.col.roll': 'Roll',
  'labels.verify.col.code': 'Code',
  'labels.verify.col.secret': 'Secret',
  'labels.verify.masked': 'The secret is masked: in the clear it only exists in the encrypted file and under the scratch layer.',
  'labels.verify.full': 'Check the whole file',

  // ── client roll requests (#142)
  'labels.tab.requests': 'Requests',
  'rollRequests.title': 'Roll requests',
  'rollRequests.description':
    'What you ask XYVONA for. We generate the codes, have them printed and deliver the rolls.',
  'rollRequests.new': 'Request rolls',
  'rollRequests.new.description':
    'Quantity is in labels. How many fit on each roll is set with the supplier for each print run.',
  'rollRequests.send': 'Send request',
  'rollRequests.empty': 'No roll requests yet',
  'rollRequests.empty.detail':
    'Once you request rolls, they stay here with their status until delivery.',
  'rollRequests.col.quantity': 'Labels',
  'rollRequests.col.neededBy': 'Needed by',
  'rollRequests.col.notes': 'Notes',
  'rollRequests.col.state': 'Status',
  'rollRequests.col.resolution': 'Response',
  'rollRequests.field.quantity': 'Number of labels',
  'rollRequests.field.quantity.hint': 'In labels, not rolls.',
  'rollRequests.field.quantity.invalid': 'A whole number between 1 and {max}.',
  'rollRequests.field.product': 'Product',
  'rollRequests.field.product.hint': 'Optional. Helps us prepare the run.',
  'rollRequests.field.product.none': 'No product assigned',
  'rollRequests.field.neededBy': 'Date you need them',
  'rollRequests.field.neededBy.hint': 'Optional. A reference, not a commitment.',
  'rollRequests.field.notes': 'Notes',
  'rollRequests.field.notes.hint': 'Security tier, destination, anything worth noting.',

  // ── #147 · clave del pedido y herramientas del back-office
  'module.tooling': 'Tooling',
  'module.tooling.description': 'The printer page and the team permissions.',
  'labels.orderKey.request': 'Show this order key',
  'labels.orderKey.request.hint': 'Shown only when you ask for it, and who looked is recorded.',
  'labels.orderKey.working': 'Requesting…',
  'labels.orderKey.value': 'Key for order {order}',
  'labels.orderKey.copy': 'Copy the key',
  'labels.orderKey.copied': 'Copied',
  'labels.orderKey.hide': 'Hide',
  'labels.orderKey.warning.title': 'Send it over a different channel than the download link',
  'labels.orderKey.warning.detail': 'If the file and the key travel the same way, the two layers become one. Phone, WhatsApp or in person — never the same email as the link.',
  'labels.orderKey.audited': 'This request is in the audit log. The key is not: only that you looked at it.',
  'tooling.supplier.title': 'Printer page',
  'tooling.supplier.description': 'The file the supplier uses to open the .xyvc1. Sent once, kept by them.',
  'tooling.supplier.version': 'Version',
  'tooling.supplier.bytes': 'Size (bytes)',
  'tooling.supplier.sha256': 'SHA-256',
  'tooling.supplier.download': 'Download the page',
  'tooling.supplier.downloading': 'Downloading…',
  'tooling.supplier.sendOnce': 'No need to resend it per order — only when the version goes up.',
  'tooling.permissions.title': 'XYVONA team permissions',
  'tooling.permissions.description': 'Who can see an order decryption key. Granted one by one, and audited.',
  'tooling.permissions.empty': 'No XYVONA users.',
  'tooling.permissions.col.person': 'Person',
  'tooling.permissions.col.permission': 'See the key',
  'tooling.permissions.col.action': 'Action',
  'tooling.permissions.granted': 'Granted',
  'tooling.permissions.none': 'No permission',
  'tooling.permissions.grant': 'Grant',
  'tooling.permissions.revoke': 'Revoke',
  'tooling.permissions.note': 'Reason (kept in the audit log)',
  'tooling.permissions.noteRequired': 'Detail (required with “other”)',
  'tooling.permissions.reason': 'Reason',
  'tooling.permissions.reason.reprint': 'Reprint',
  'tooling.permissions.reason.printer_claim': 'Printer claim',
  'tooling.permissions.reason.audit': 'Audit',
  'tooling.permissions.reason.other': 'Other',
  'tooling.permissions.duration': 'Expires in',
  'tooling.permissions.duration.4': '4 hours',
  'tooling.permissions.duration.24': '24 hours',
  'tooling.permissions.duration.72': '3 days',
  'tooling.permissions.duration.168': '7 days',
  'tooling.permissions.duration.720': '30 days',
  'tooling.permissions.expires': 'Expires {when}',
  'tooling.permissions.expired': 'Expired {when}',

  // ── secciones de la barra lateral
  'nav.section.catalog': 'Catalog',
  'nav.section.plant': 'Plant',
  'nav.section.activity': 'Activity',
  'nav.section.protection': 'Brand protection',
  'nav.section.config': 'Configuration',
  'nav.section.hq': 'XYVONA',

  // ── ficha de etiqueta, encabezado
  'labels.units.detail.label': 'Label ·',
  'labels.units.detail.batchInline': 'Batch {lot}',
  'labels.units.detail.rollInline': 'Roll {roll}',
  'labels.units.detail.openAlertsBadge': '{count} open alerts',

  // ── listado de etiquetas
  'labels.units.countTotal': '{count} in total',
  'labels.units.quickFind.placeholder': 'Search by printed code, or enter a batch or roll id below',
  'labels.units.quickFind.submit': 'Search',
  'labels.units.column.batch': 'Batch',
  'labels.units.column.created': 'Created',
  'labels.units.column.actions': 'Actions',
  'labels.units.column.qr': 'QR',

  // ── ámbito activo del listado de etiquetas
  'labels.units.scoped.all': 'Every code of the brand, newest first.',
  'labels.units.scoped.batch': 'Labels in batch {value}. Change the scope below to see another.',
  'labels.units.scoped.roll': 'Labels in roll {value}. Change the scope below to see another.',
  'labels.units.scoped.code': 'Lookup for code {value}.',
};

const pt: Catalog = {
  'app.name': 'XYVONA',
  'app.subtitle': 'Autenticação de produto',
  'app.skipToContent': 'Ir para o conteúdo',

  'auth.login.title': 'Entrar no painel',
  'auth.login.intro': 'Use as credenciais da sua marca.',
  'auth.login.tenant': 'Marca',
  'auth.login.tenantHint': 'O identificador curto do seu laboratório.',
  'auth.login.email': 'E-mail',
  'auth.login.password': 'Senha',
  'auth.login.submit': 'Entrar',
  'auth.login.submitting': 'Verificando…',
  'auth.login.required': 'Preencha os três campos.',
  'auth.logout': 'Sair',
  'auth.loggingOut': 'Saindo…',
  'auth.session.restoring': 'Restaurando a sessão…',
  'auth.session.expired': 'A sessão expirou. Entre novamente.',
  'auth.session.reused':
    'Todas as suas sessões foram encerradas por segurança: o token desta sessão foi usado duas vezes. Se não foi você, avise o administrador da sua marca.',
  'auth.session.revoked': 'Um administrador encerrou a sua sessão.',
  'auth.role.label': 'Papel',

  // ───────────────────────────────────────────────────────────── minha conta
  'account.title': 'Minha conta',
  'account.link': 'Minha conta',
  'account.identity.title': 'Quem sou',
  'account.identity.name': 'Nome',
  'account.identity.email': 'E-mail',
  'account.password.title': 'Alterar a senha',
  'account.password.intro':
    'Todas as suas sessões em outros dispositivos serão encerradas. Aqui você continua conectado.',
  'account.password.current': 'Senha atual',
  'account.password.new': 'Nova senha',
  'account.password.newHint': 'Pelo menos 12 caracteres. Uma frase é melhor que uma palavra.',
  'account.password.confirm': 'Repita a nova senha',
  'account.password.submit': 'Alterar a senha',
  'account.password.submitting': 'Alterando…',
  'account.password.required': 'Preencha os três campos.',
  'account.password.tooShort': 'A nova senha precisa de pelo menos 12 caracteres.',
  'account.password.mismatch': 'As duas novas senhas não coincidem.',
  'account.password.same': 'A nova senha é igual à atual.',
  'account.password.wrongCurrent': 'A senha atual não está correta.',
  'account.password.federated':
    'Sua conta autentica pelo diretório da sua organização. A senha é alterada lá.',
  'account.password.changed': 'Senha alterada.',
  'account.password.changedOthers':
    'Senha alterada. {n} sessões em outros dispositivos foram encerradas.',
  'account.password.changedRelogin':
    'Senha alterada. Não foi possível renovar esta sessão: entre de novo com a nova.',

  'nav.title': 'Módulos',
  'nav.pending': 'Em construção',
  'nav.pendingHint': 'Este módulo ainda não foi implementado.',
  'nav.open': 'Abrir navegação',
  'nav.close': 'Fechar navegação',
  'nav.language': 'Idioma da interface',

  'module.overview': 'Visão geral',
  'module.map': 'Mapa de calor',
  'module.products': 'Produtos',
  'module.batches': 'Lotes',
  'module.alerts': 'Alertas e casos',
  'module.labels': 'Etiquetas',
  'module.analytics': 'Analítica',
  'module.integrations': 'Integrações',
  'module.admin': 'Administração',
  'module.clients': 'Clientes',
  'module.requests': 'Pedidos',

  'module.overview.description': 'Leituras, taxa de autenticação e alertas abertos.',
  'module.map.description': 'Onde o produto é lido e onde aparece anomalia.',
  'module.products.description': 'Catálogo, GTIN e nível de segurança.',
  'module.batches.description': 'Status do lote, ativadas vs. lidas, recall.',
  'module.alerts.description': 'Caixa de entrada, atribuição, investigação e fechamento.',
  'module.labels.description': 'Pedidos, rolos, estoque e códigos.',
  'module.analytics.description': 'Tendências, penetração por região e exportação.',
  'module.integrations.description': 'API keys, webhooks e logs de entrega.',
  'module.admin.description': 'Usuários, papéis, SSO e auditoria.',

  'state.loading': 'Carregando…',
  'state.empty.title': 'Nada para mostrar',
  'state.empty.detail': 'Os dados aparecem aqui assim que existirem.',
  'state.error.title': 'Não foi possível carregar',
  'state.error.retry': 'Tentar de novo',
  'state.error.trace': 'Referência para o suporte',
  'state.loadMore': 'Carregar mais',
  'state.loadingMore': 'Carregando mais…',
  'state.allLoaded': 'Não há mais resultados.',
  'state.refreshing': 'Atualizando…',

  'guard.forbidden.title': 'Você não tem acesso a isto',
  'guard.forbidden.detail':
    'Esta seção exige o papel {required}. O seu é {actual}. Peça ao administrador da sua marca.',
  'guard.forbidden.back': 'Voltar à visão geral',

  'error.network': 'Sem conexão com a API.',
  'error.network.hint': 'Verifique a rede. O painel tenta de novo sozinho.',
  'error.timeout': 'A consulta demorou demais.',
  'error.timeout.hint': 'Tente de novo ou reduza o intervalo de datas.',
  'error.unauthorized': 'Sua sessão não é válida.',
  'error.invalidCredentials': 'Usuário, senha ou marca incorretos.',
  'error.forbidden': 'Seu papel não permite esta operação.',
  'error.backofficeOnly': 'Comprar e gerar etiquetas é tarefa da XYVONA.',
  'error.backofficeOnly.hint': 'Sua marca solicita rolos em Etiquetas › Solicitações e os recebe já gerados.',
  'error.notFound': 'Não encontramos o que você procurava.',
  'error.conflict': 'O registro mudou enquanto você editava.',
  'error.validation': 'Há campos com valores inválidos.',
  'error.rateLimited': 'Muitas requisições. Espere alguns segundos.',
  'error.internal': 'Erro interno da plataforma.',
  'error.internal.hint': 'Passe a referência ao suporte para procurarem no log.',
  'error.invalidCursor': 'A paginação ficou desatualizada. Recarregue a lista.',
  'error.unknown': 'Ocorreu um erro inesperado.',

  'users.title': 'Usuários',
  'users.description': 'Quem tem acesso ao painel desta marca.',
  'users.column.name': 'Nome',
  'users.column.email': 'E-mail',
  'users.column.role': 'Papel',
  'users.column.status': 'Status',
  'users.column.lastLogin': 'Último acesso',
  'users.status.active': 'Ativo',
  'users.status.inactive': 'Inativo',
  'users.never': 'Nunca',
  'users.empty': 'Esta marca ainda não tem usuários.',
  'users.count': '{count} usuários carregados',

  'users.column.authMode': 'Autenticação',
  'users.column.actions': 'Ações',
  'users.authMode.password': 'Senha',
  'users.authMode.oidc': 'Diretório (OIDC)',
  'users.new': 'Novo usuário',

  'users.filter.title': 'Buscar e filtrar',
  'users.filter.description':
    'Com duzentos usuários, "carregar mais" não é navegação. Os três filtros são resolvidos pela API.',
  'users.filter.search': 'Buscar',
  'users.filter.searchHint': 'Busca no e-mail e no nome. Enter para aplicar.',
  'users.filter.searchPlaceholder': 'maria, @labfarma…',
  'users.filter.searchAction': 'Buscar',
  'users.filter.role': 'Papel',
  'users.filter.allRoles': 'Todos os papéis',
  'users.filter.status': 'Status',
  'users.filter.allStatuses': 'Ativos e inativos',
  'users.filter.empty': 'Nenhum usuário corresponde ao filtro.',

  'users.form.title': 'Novo usuário',
  'users.form.description':
    'A senha é gerada pelo servidor e mostrada uma única vez. O e-mail é único dentro desta marca.',
  'users.form.email': 'E-mail',
  'users.form.emailHint': 'Vai entrar com este e-mail junto com o nome da marca.',
  'users.form.emailInvalid': 'Digite um e-mail válido.',
  'users.form.name': 'Nome',
  'users.form.nameHint': 'Como aparece na tabela e nas atribuições de alertas.',
  'users.form.role': 'Papel',
  'users.form.submit': 'Criar usuário',
  'users.form.submitting': 'Criando…',

  'users.action.self': 'Você',
  'users.action.federated': 'Senha no diretório',
  'users.action.changeRole': 'Papel de {email}',
  'users.action.deactivate': 'Desativar',
  'users.action.reactivate': 'Reativar',
  'users.action.reset': 'Redefinir senha',

  'users.confirm.role.title': 'Mudar o papel',
  'users.confirm.role.detail':
    '{who} passa a {role}. As sessões abertas são encerradas e a mudança fica na auditoria com o seu nome.',
  'users.confirm.role.accept': 'Sim, mudar o papel',
  'users.confirm.deactivate.title': 'Desativar',
  'users.confirm.deactivate.detail':
    '{who} perde o acesso agora e as sessões são encerradas. A conta não é apagada: a auditoria precisa continuar dizendo quem fez o quê.',
  'users.confirm.deactivate.accept': 'Sim, desativar',
  'users.confirm.reactivate.title': 'Reativar',
  'users.confirm.reactivate.detail': '{who} volta a entrar com a senha anterior.',
  'users.confirm.reactivate.accept': 'Sim, reativar',
  'users.confirm.reset.title': 'Redefinir a senha',
  'users.confirm.reset.detail':
    'Uma senha temporária é gerada para {who} e todas as sessões são encerradas. A temporária é mostrada uma única vez.',
  'users.confirm.reset.accept': 'Sim, redefinir',

  'users.temp.title': 'Senha temporária',
  'users.temp.detail':
    'Entregue a {email} por um canal seguro. Não é mostrada de novo: se perder, redefina outra vez.',
  'users.temp.sessions': '{count} sessões abertas foram encerradas.',
  'users.temp.dismiss': 'Pronto, copiei',

  'module.activation': 'Ativação',
  'module.activation.description': 'Linha de embalagem: ativar unidades e ver o que é rejeitado.',

  'activation.title': 'Ativação na fábrica',
  'activation.description':
    'Simula a linha de embalagem: ler códigos, ativá-los contra um lote de produção e ver o que é rejeitado.',
  'activation.rule':
    'Uma unidade não autentica até estar ativada. As etiquetas chegam da fábrica já registradas: se nascessem válidas, um rolo roubado do depósito autenticaria produto falsificado. Só se ativa a partir de RECEBIDA, e sempre com o código impresso — nunca com um identificador interno.',

  'activation.console.title': 'Console da linha',
  'activation.console.description':
    'Um código por linha, como um leitor os deixa. Também valem vírgula, ponto e vírgula ou espaço.',

  'activation.field.batch': 'Lote de produção',
  'activation.field.batchHint': 'As unidades ficam vinculadas a este lote: GTIN, lote e validade.',
  'activation.field.batchPlaceholder': 'Escolha um lote',
  'activation.field.batchBlocked': 'não aceita ativações',
  'activation.field.line': 'Linha',
  'activation.field.lineHint': 'Fica registrada na remessa de ativação.',
  'activation.field.key': 'Chave de idempotência',
  'activation.field.keyHint':
    'Reenviar a mesma remessa com esta chave não ativa duas vezes. Reusar a chave com outro conteúdo é um erro e a API rejeita.',
  'activation.field.codes': 'Códigos lidos',
  'activation.field.codesPlaceholder': '07VZFEF9G08Y8B57K165',
  'activation.field.codesHint': 'Até 50.000 etiquetas por remessa.',

  'activation.scan.ready': '{count} prontos',
  'activation.scan.duplicates': '{count} repetidos',
  'activation.scan.unreadable': '{count} ilegíveis',
  'activation.scan.unreadableHint': 'Não entram no formato do código e não são enviados:',

  'activation.submit': 'Ativar remessa',
  'activation.missing.batch': 'Para habilitar, escolha primeiro o lote.',
  'activation.missing.codes': 'Para habilitar, cole os códigos lidos no campo acima.',
  'activation.missing.key': 'Para habilitar, falta a chave de idempotência.',
  'activation.submitting': 'Ativando…',
  'activation.submit.offline': 'Guardar na fila',

  'activation.offline.on': 'Sem rede',
  'activation.offline.off': 'Online',
  'activation.offline.disconnect': 'Simular queda de rede',
  'activation.offline.reconnect': 'Restabelecer a rede',
  'activation.offline.explain':
    'A linha não para quando a rede cai: acumula e reenvia. A mesma remessa reenviada não ativa duas vezes.',
  'activation.offline.stored': 'A remessa ficou na fila local. Sai quando a rede voltar.',
  'activation.offline.replaced': 'Já havia uma remessa com essa chave na fila e esta a substituiu.',
  'activation.offline.queueTitle': 'Fila da linha',
  'activation.offline.queueDescription': '{runs} remessas · {codes} etiquetas esperando rede.',
  'activation.offline.queuedAt': 'Fechada',
  'activation.offline.sync': 'Sincronizar',
  'activation.offline.syncing': 'Sincronizando…',
  'activation.offline.discard': 'Descartar',

  'activation.result.replayed': 'Já recebida',
  'activation.result.fresh': 'Ativada agora',
  'activation.result.replayedHint':
    'Esta remessa já havia sido recebida: a resposta é a guardada e nada foi ativado de novo. É assim que um reenvio depois de uma queda não duplica.',
  'activation.result.requested': 'Enviadas',
  'activation.result.activated': 'Ativadas',
  'activation.result.already': 'Já ativas',
  'activation.result.rejected': 'Rejeitadas',
  'activation.result.elapsed': 'Resolvido em {ms} ms.',
  'activation.result.cacheStale':
    'Não foi possível invalidar o cache de verificação. As unidades ESTÃO ativadas; o consumidor pode ver o estado anterior por alguns minutos. Reenviar a remessa tenta de novo.',
  'activation.result.verifyHint':
    'Verifique uma destas etiquetas como consumidor: a partir de agora tem que dizer genuíno.',
  'activation.result.resend': 'Reenviar igual',
  'activation.result.open': 'Ver rejeições',

  'activation.rejects.title': 'Etiquetas rejeitadas',
  'activation.rejects.description': 'Tire-as da caixa antes que saiam da fábrica.',
  'activation.rejects.sample':
    'Mostrando {shown} de {total}. O detalhe completo fica auditado na remessa.',
  'activation.rejects.column.code': 'Código',
  'activation.rejects.column.outcome': 'Motivo',
  'activation.rejects.column.existingBatch': 'Lote atual',
  'activation.rejects.column.existingStatus': 'Estado',

  'activation.outcome.activated': 'Ativada',
  'activation.outcome.alreadyActivated': 'Já estava ativada',
  'activation.outcome.duplicate': 'Repetida na remessa',
  'activation.outcome.wrongState': 'Estado inválido',
  'activation.outcome.otherBatch': 'De outro lote',
  'activation.outcome.terminal': 'Anulada, roubada ou recolhida',
  'activation.outcome.unknown': 'Sem unidade',
  'activation.outcome.invalidCode': 'Código inválido',

  'activation.why.activated': 'Passou de RECEBIDA a ATIVADA: a partir de agora autentica.',
  'activation.why.alreadyActivated':
    'Já estava ativada contra este mesmo lote. É o que torna o reenvio idempotente.',
  'activation.why.duplicate': 'O mesmo código veio duas vezes na remessa.',
  'activation.why.wrongState': 'Não chegou a RECEBIDA: a etiqueta não passou pelo depósito.',
  'activation.why.otherBatch': 'Já estava ativada contra outro lote de produção.',
  'activation.why.terminal': 'Anulada, roubada, recolhida ou destruída: não volta ao circuito.',
  'activation.why.unknown': 'O código verifica, mas não há unidade desta marca com esse id.',
  'activation.why.invalidCode': 'O HMAC não confere: código forjado ou mal lido.',

  'activation.runs.title': 'Remessas recebidas',
  'activation.runs.description': 'Cada envio da linha, com o que ativou e o que rejeitou.',
  'activation.runs.empty': 'Nenhuma remessa foi ativada ainda.',
  'activation.runs.emptyHint': 'Escolha um lote de produção, cole os códigos lidos e ative.',
  'activation.runs.column.received': 'Recebida',
  'activation.runs.column.requested': 'Enviadas',

  'activation.detail.back': '← Voltar à ativação',
  'activation.detail.description': 'Lote de produção #{batch} · linha {line}',
  'activation.detail.rejected': '{count} rejeitadas',
  'activation.detail.clean': 'Sem rejeições',
  'activation.detail.clientSentAt': 'Relógio da linha',
  'activation.detail.duration': 'Duração',
  'activation.detail.durationValue': '{ms} ms',

  // ───────────────── modos A e C de ativação (#79) e exceções (#80)
  'activation.modes.link': 'Modos A e C · intervalo e em linha',
  'activation.modes.title': 'Intervalo e linha',
  'activation.modes.description':
    'Os outros dois mecanismos de ativação, para clientes que não usam leitor de lote.',
  'activation.modes.back': '← Voltar ao lote',
  'activation.modes.rule':
    'Três modos, um só resultado para o consumidor: uma unidade ativada por qualquer um deles autentica igual, e os três deixam a mesma entrada no registro de auditoria. O que muda é quem pode usar cada um.',
  'activation.modes.badge.range': 'Modo A',
  'activation.modes.badge.line': 'Modo C',
  'activation.modes.badge.exceptions': 'Terminal',

  'activation.range.title': 'Modo A · ativar por intervalo',
  'activation.range.description':
    'Para o produtor sem automação: «rolo 47, séries 1.000.000 a 1.010.000 → lote L2026-0801».',
  'activation.range.from': 'Série de',
  'activation.range.to': 'Série até',
  'activation.range.width': '{count} unidades no intervalo. O intervalo tem que sair de um rolo emitido: senão, a API rejeita.',
  'activation.range.submit': 'Ativar o intervalo',
  'activation.range.order':
    'Primeiro anulam-se as exceções declaradas e depois ativa-se o intervalo, na mesma transação. Ao contrário não daria: uma unidade já ativada não se anula.',
  'activation.range.inRange': 'No intervalo',
  'activation.range.exceptionRef': 'Registro de exceções #{id}.',

  'activation.exceptions.title': 'Registro de exceções',
  'activation.exceptions.description':
    'Marcar etiquetas como anuladas: danificadas, mal aplicadas, puladas ou descartadas na impressão.',
  'activation.exceptions.legend': 'Exceções deste intervalo',
  'activation.exceptions.rule':
    'Obrigatório. Ativar um intervalo sem declarar os descartes deixa unidades ativadas sem produto real: autenticam como genuínas e não correspondem a nada. Se o rolo foi aplicado inteiro, isso também se assina.',
  'activation.exceptions.terminal':
    'Anular é terminal e não se desfaz. Só se pode anular uma etiqueta que ainda não foi ativada; para uma que já está na rua vale o recolhimento do lote ou a destruição documentada.',
  'activation.exceptions.mode.none': 'Sem descartes (eu assino)',
  'activation.exceptions.mode.ranges': 'Por faixas de séries',
  'activation.exceptions.mode.codes': 'Por códigos lidos',
  'activation.exceptions.reason': 'Motivo',
  'activation.exceptions.note': 'Detalhe',
  'activation.exceptions.noteHint': 'Obrigatório se o motivo for «outro».',
  'activation.exceptions.ranges': 'Faixas descartadas',
  'activation.exceptions.rangesPlaceholder': '1000010-1000024',
  'activation.exceptions.rangesHint': '{tramos} faixas · {count} unidades. Uma por linha: «1000-1010» ou «1000».',
  'activation.exceptions.codes': 'Códigos descartados',
  'activation.exceptions.invalidRanges': 'Há linhas que não são faixas e não serão enviadas: {list}',
  'activation.exceptions.outside':
    'Há faixas fora do intervalo que vai ser ativado. Anulariam etiquetas de outro rolo.',
  'activation.exceptions.byRange': 'Anular por faixa de séries em vez de por códigos',
  'activation.exceptions.voided': 'Anuladas',
  'activation.exceptions.submit': 'Registrar e anular',
  'activation.exceptions.rejects': 'Não puderam ser anuladas',

  'activation.reason.damaged': 'Etiqueta danificada',
  'activation.reason.misapplied': 'Mal aplicada',
  'activation.reason.skipped': 'Pulada pelo aplicador',
  'activation.reason.printDiscard': 'Descartada na impressão',
  'activation.reason.orderCancelled': 'Ordem cancelada',
  'activation.reason.other': 'Outro (explicar)',

  'activation.voidOutcome.voided': 'Anulada',
  'activation.voidOutcome.alreadyVoided': 'Já estava anulada',
  'activation.voidOutcome.activated': 'Já ativada: não se anula',
  'activation.voidOutcome.terminal': 'Roubada, recolhida ou destruída',
  'activation.voidOutcome.wrongState': 'Estado que não admite anulação',
  'activation.voidOutcome.unknown': 'Sem unidade',
  'activation.voidOutcome.invalidCode': 'Código inválido',

  'activation.line.title': 'Modo C · ativar em linha',
  'activation.line.description':
    'O sistema de visão da linha, unidade por unidade ou por pacote.',
  'activation.line.code': 'Código lido',
  'activation.line.codeHint': 'Enter ativa. Vários códigos separados por espaço são um pacote.',
  'activation.line.submit': 'Ativar',
  'activation.line.p50': 'p50 {ms} ms',
  'activation.line.samples': '{count} leituras medidas',
  'activation.line.budget':
    'Uma linha a 300 unidades por minuto são 5 por segundo. Se a API demorar mais que isso, a linha reduz a velocidade — e isso o cliente não aceita.',
  'activation.line.failSafe':
    'Se a XYVONA não responder, o sistema de visão enfileira e a linha segue. Nunca ao contrário.',
  'activation.detail.notFound': 'Essa remessa de ativação não existe.',

  'activation.conflicts.title': 'Rejeições auditadas',
  'activation.conflicts.description':
    'O rastro que um auditor pede: o que se tentou ativar, contra qual lote e em que estado a unidade realmente estava.',
  'activation.conflicts.empty': 'Esta remessa não teve rejeições.',
  'activation.conflicts.emptyHint':
    'Todas as etiquetas enviadas estavam em RECEBIDA e foram ativadas.',
  'activation.conflicts.column.case': 'Caso',
  'activation.conflicts.column.reason': 'Motivo',
  'activation.conflicts.column.attempted': 'Lote tentado',
  'activation.conflicts.column.existing': 'Lote atual',
  'activation.conflicts.column.status': 'Estado da unidade',
  'activation.conflicts.column.when': 'Registrado',

  'activation.unitStatus.created': 'Criada',
  'activation.unitStatus.printed': 'Impressa',
  'activation.unitStatus.shipped': 'Despachada',
  'activation.unitStatus.received': 'Recebida',
  'activation.unitStatus.activated': 'Ativada',
  'activation.unitStatus.inMarket': 'No mercado',
  'activation.unitStatus.verified': 'Verificada',
  'activation.unitStatus.voided': 'Anulada',
  'activation.unitStatus.stolen': 'Roubada',
  'activation.unitStatus.recalled': 'Recolhida',
  'activation.unitStatus.destroyed': 'Destruída',

  'alerts.inbox.title': 'Caixa de alertas',
  'alerts.inbox.description':
    'Cada alerta mostra a evidência que o disparou. Operada pelo teclado: ? para a ajuda.',
  'alerts.case': 'Caso #{id}',
  'alerts.unassigned': 'Sem responsável',
  'alerts.yes': 'sim',
  'alerts.no': 'não',
  'alerts.column.batch': 'Lote',

  'alerts.summary.open': 'Abertos',
  'alerts.summary.unassigned': 'Sem dono',
  'alerts.summary.mine': 'Meus',

  'alerts.filter.status': 'Estado',
  'alerts.filter.severity': 'Severidade',
  'alerts.filter.severityFrom': '{severity} ou acima',
  'alerts.filter.type': 'Regra',
  'alerts.filter.assignment': 'Atribuição',
  'alerts.filter.sort': 'Ordem',
  'alerts.filter.sortSeverity': 'Mais graves primeiro',
  'alerts.filter.sortRecent': 'Mais recentes primeiro',
  'alerts.filter.window': 'Período',
  'alerts.filter.window1': 'Últimas 24 h',
  'alerts.filter.window7': 'Últimos 7 dias',
  'alerts.filter.window30': 'Últimos 30 dias',
  'alerts.filter.window90': 'Últimos 90 dias',
  'alerts.filter.windowAll': 'Tudo',
  'alerts.filter.any': 'Todas',
  'alerts.filter.mine': 'Atribuídos a mim',
  'alerts.filter.unassigned': 'Sem dono',
  'alerts.filter.reset': 'Limpar filtros',

  'alerts.list.title': 'Casos',
  'alerts.list.description': 'Ordenados por severidade: primeiro o que precisa de atenção.',
  'alerts.list.count': '{count} carregados',
  'alerts.list.empty': 'Não há alertas com estes filtros.',
  'alerts.list.emptyHint': 'Amplie o período ou tire o filtro de estado.',

  'alerts.severity.critical': 'Crítica',
  'alerts.severity.high': 'Alta',
  'alerts.severity.medium': 'Média',
  'alerts.severity.low': 'Baixa',
  'alerts.severity.info': 'Informativa',

  'alerts.status.open': 'Aberto',
  'alerts.status.investigating': 'Em investigação',
  'alerts.status.confirmed': 'Confirmado',
  'alerts.status.dismissed': 'Descartado',

  'alerts.type.notActivated': 'Código não ativado',
  'alerts.type.stolenRoll': 'Rolo roubado',
  'alerts.type.recalled': 'Lote recolhido',
  'alerts.type.secretReuse': 'Reúso do código oculto',
  'alerts.type.impossibleTravel': 'Viagem impossível',
  'alerts.type.territoryDiversion': 'Desvio de território',
  'alerts.type.geoCluster': 'Cluster fora da zona',
  'alerts.type.duplicateScan': 'Leitura duplicada',
  'alerts.type.velocitySpike': 'Pico de leituras',
  'alerts.type.lowDeviceDiversity': 'Baixa diversidade de dispositivos',
  'alerts.type.datacenterAsn': 'ASN de datacenter',

  'alerts.resolution.counterfeit': 'Falsificação confirmada',
  'alerts.resolution.diversion': 'Desvio confirmado',
  'alerts.resolution.theft': 'Roubo confirmado',
  'alerts.resolution.falsePositive': 'Falso positivo',
  'alerts.resolution.consumerError': 'Erro do consumidor',
  'alerts.resolution.testScan': 'Leitura de teste',
  'alerts.resolution.noAction': 'Sem ação',

  'alerts.evidence.title': 'Evidência',
  'alerts.evidence.empty': 'A regra não deixou campos de evidência.',
  'alerts.evidence.hidden': 'oculto: identificador interno',
  'alerts.evidence.unknownPlace': 'lugar desconhecido',

  'alerts.travel.first': 'Primeira leitura',
  'alerts.travel.second': 'Segunda leitura',
  'alerts.travel.distance': 'Distância',
  'alerts.travel.elapsed': 'Tempo entre leituras',
  'alerts.travel.speed': 'Velocidade implícita',
  'alerts.travel.limit': 'Limite da regra',
  'alerts.travel.km': '{value} km',
  'alerts.travel.kmh': '{value} km/h',
  'alerts.travel.minutes': '{value} min',
  'alerts.travel.plotLabel': 'As duas leituras: {from} e {to}',
  'alerts.travel.approximate': 'Localização aproximada, a nível de cidade.',

  'alerts.detail.none': 'Nenhum caso selecionado',
  'alerts.detail.noneHint': 'Escolha um da lista ou aperte j para começar pelo mais grave.',
  'alerts.detail.missing': 'Esse caso não existe ou é de outra marca.',
  'alerts.detail.opened': 'Aberto em {when}',
  'alerts.detail.context': 'Contexto',
  'alerts.detail.territory': 'Território',
  'alerts.detail.relatedUnit': 'Outros alertas da unidade',
  'alerts.detail.relatedBatch': 'Outros alertas do lote',
  'alerts.detail.assignee': 'Atribuído a',
  'alerts.detail.resolution': 'Motivo de fechamento',
  'alerts.detail.actions': 'Gestão do caso',
  'alerts.detail.readOnly':
    'Seu papel lê a caixa mas não a opera. Atribuir, fechar e anotar exigem papel de analista.',

  'alerts.action.assignSelf': 'Atribuir a mim',
  'alerts.action.unassign': 'Liberar',
  'alerts.action.assignTo': 'Atribuir a',
  'alerts.action.moveTo': 'Passar para {status}',
  'alerts.action.note': 'Adicionar nota',

  'alerts.notes.title': 'Notas de investigação',
  'alerts.notes.empty': 'Ainda não há notas.',
  'alerts.history.title': 'Histórico do caso',
  'alerts.history.hint': 'Vem da auditoria encadeada por hash, a mesma que um auditor lê.',
  'alerts.history.empty': 'Sem movimentos registrados.',

  'alerts.dialog.statusTitle': 'Passar o caso para {status}',
  'alerts.dialog.noteTitle': 'Adicionar uma nota',
  'alerts.dialog.resolution': 'Motivo de fechamento',
  'alerts.dialog.note': 'Nota',
  'alerts.dialog.noteOptional': 'Nota (opcional)',
  'alerts.dialog.submit': 'Salvar',
  'alerts.dialog.saving': 'Salvando…',
  'alerts.dialog.cancel': 'Cancelar',
  'alerts.dialog.submitHint': 'Ctrl+Enter salva · Esc cancela. As notas não se editam nem se apagam.',

  'alerts.shortcuts.title': 'Atalhos de teclado',
  'alerts.shortcuts.open': 'Atalhos (?)',
  'alerts.shortcut.next': 'Próximo caso',
  'alerts.shortcut.previous': 'Caso anterior',
  'alerts.shortcut.first': 'Primeiro / último',
  'alerts.shortcut.last': 'Último caso',
  'alerts.shortcut.investigate': 'Marcar em investigação',
  'alerts.shortcut.confirm': 'Confirmar o caso',
  'alerts.shortcut.dismiss': 'Descartar o caso',
  'alerts.shortcut.reopen': 'Reabrir o caso',
  'alerts.shortcut.refresh': 'Atualizar a caixa',
  'alerts.shortcut.help': 'Esta ajuda',
  'alerts.shortcut.close': 'Fechar o diálogo',

  'placeholder.title': 'Módulo em construção',
  'placeholder.detail':
    'Esta seção é implementada na issue #{issue}. O andaime — sessão, navegação, cliente de API e estados — já está pronto.',
  'placeholder.issue': 'Issue',

  'notFound.title': 'Essa página não existe',
  'notFound.detail': 'O link pode estar desatualizado.',

  'confirm.cancel': 'Cancelar',
  'confirm.accept': 'Confirmar',
  'confirm.typeToConfirm': 'Escreva {word} para confirmar.',

  'admin.sections': 'Seções de administração',
  'admin.tab.users': 'Usuários',
  'admin.tab.audit': 'Auditoria',
  'admin.tab.portal': 'Portal do consumidor',

  'users.roles.title': 'O que cada papel pode fazer',
  'users.roles.admin': 'Administra usuários e chaves, e lê a auditoria.',
  'users.roles.analyst': 'Opera lotes, alertas e casos; pede exportações e relatórios.',
  'users.roles.viewer': 'Somente leitura: painéis, lotes e caixa de alertas.',
  'users.roles.audited':
    'Toda inclusão, exclusão ou troca de papel fica na auditoria, com quem fez e quando.',
  'users.roles.seeAudit': 'Ver as mudanças de acesso',

  'audit.title': 'Auditoria',
  'audit.description': 'Quem fez o quê, quando, e se o registro está intacto.',
  'audit.chain.title': 'Integridade da cadeia',
  'audit.chain.checking': 'Recalculando a cadeia…',
  'audit.chain.ok': 'Cadeia íntegra',
  'audit.chain.broken': 'Cadeia quebrada',
  'audit.chain.okDetail': '{entries} entradas recalculadas e todas fecham com a anterior.',
  'audit.chain.brokenDetail': '{broken} de {entries} entradas não fecham. A primeira é a #{seq}.',
  'audit.chain.reason': 'Motivo',
  'audit.chain.reason.contenido_alterado':
    'O conteúdo da entrada mudou depois de ter sido escrito.',
  'audit.chain.reason.enlace_roto': 'A entrada não engata no hash da anterior.',
  'audit.chain.reason.falta_una_fila': 'Falta uma entrada na sequência.',
  'audit.chain.reason.genesis_incorrecto': 'A primeira entrada da cadeia não é a esperada.',
  'audit.chain.reason.hash_version_desconocida':
    'A entrada usa uma versão de hash que este servidor não conhece.',
  'audit.chain.reason.other': 'O servidor informa como: {reason}',
  'audit.chain.anchor': 'Âncora',
  'audit.chain.anchorHint':
    'Anote a entrada e o hash dela. Se o par continuar igual na próxima visita, ninguém reescreveu o passado.',
  'audit.chain.entries': 'Entradas',
  'audit.chain.elapsed': 'Levou',
  'audit.chain.recheck': 'Verificar de novo',
  'audit.chain.rechecking': 'Verificando…',
  'audit.chain.formula': 'Fórmula do hash',
  'audit.chain.empty': 'Esta marca ainda não tem entradas de auditoria.',

  'audit.filter.entityType': 'Tipo de entidade',
  'audit.filter.entityTypeHint': 'Por exemplo: batch, alert, api_key, user.',
  'audit.filter.action': 'Ação',
  'audit.filter.apply': 'Aplicar',
  'audit.filter.clear': 'Limpar',
  'audit.column.seq': 'Entrada',
  'audit.column.when': 'Quando',
  'audit.column.action': 'Ação',
  'audit.column.entity': 'Entidade',
  'audit.column.actor': 'Quem',
  'audit.entry.system': 'Sistema',
  'audit.entry.details': 'Detalhe da entrada {seq}',
  'audit.entry.payload': 'Conteúdo assinado',
  'audit.entry.prevHash': 'Hash anterior',
  'audit.entry.rowHash': 'Hash da entrada',
  'audit.entry.hashVersion': 'Versão do hash',
  'audit.entry.broken': 'Aqui a cadeia se quebra',
  'audit.empty': 'Não há entradas com esses filtros.',

  'portal.title': 'Portal do consumidor',
  'portal.description': 'A conta em que o comprador guarda os códigos que verificou.',
  'portal.state.enabled': 'Habilitado',
  'portal.state.disabled': 'Indisponível',
  'portal.brand': 'Marca',
  'portal.unavailable.title': 'Esta marca não tem portal do consumidor',
  'portal.reason.industry_restricted':
    'É uma marca farmacêutica. Guardar quais medicamentos uma pessoa identificada escaneou é dado de saúde — categoria especial sob GDPR, LGPD e a Lei 6534/2020 —, então o portal não existe para esse tipo de marca (ADR-011). Não está desligado: os endpoints respondem que ele não existe.',
  'portal.reason.disabled_by_tenant':
    'A marca ainda não habilitou o portal. Ele vem desligado por padrão.',
  'portal.reason.unknown': 'O servidor não oferece o portal para esta marca.',
  'portal.preview.title': 'O que o comprador vê',
  'portal.preview.detail':
    'Esta é a tela do portal contra a API real. A sessão é do comprador, não a sua: não sobrevive a um recarregamento e não compartilha nada com a sua sessão do painel.',
  'portal.login.title': 'Entrar',
  'portal.login.submit': 'Entrar',
  'portal.register.title': 'Criar conta',
  'portal.register.submit': 'Criar a conta',
  'portal.field.email': 'E-mail',
  'portal.field.password': 'Senha',
  'portal.field.passwordHint': 'Dez caracteres no mínimo.',
  'portal.field.name': 'Nome (opcional)',
  'portal.notice':
    'Ao criar a conta guarda-se o consentimento junto com a versão do texto de privacidade ({version}).',
  'portal.session.logout': 'Fechar a sessão do portal',
  'portal.account.title': 'Minha conta',
  'portal.account.email': 'E-mail',
  'portal.account.name': 'Nome',
  'portal.account.created': 'Conta criada',
  'portal.codes.title': 'Meus códigos',
  'portal.codes.description': 'Os códigos que você verificou, com a data em que os guardou.',
  'portal.codes.empty': 'Você ainda não guardou nenhum código.',
  'portal.codes.column.ref': 'Referência',
  'portal.codes.column.label': 'Etiqueta',
  'portal.codes.column.result': 'Resultado',
  'portal.codes.column.saved': 'Guardado',
  'portal.codes.column.actions': 'Ações',
  'portal.codes.add': 'Guardar um código',
  'portal.codes.field.code': 'Código do QR',
  'portal.codes.field.label': 'Etiqueta (opcional)',
  'portal.codes.save': 'Guardar',
  'portal.codes.saving': 'Guardando…',
  'portal.codes.delete': 'Excluir',
  'portal.codes.delete.title': 'Excluir este código',
  'portal.codes.delete.detail':
    'Sai da sua lista. A verificação em si não é apagada: o que desaparece é o vínculo com a sua conta.',
  'portal.result.genuine': 'Verificado',
  'portal.result.already_seen': 'Já verificado antes',
  'portal.result.recalled': 'Lote recolhido',
  'portal.rights.title': 'Seus dados',
  'portal.rights.export': 'Baixar meus dados',
  'portal.rights.exportHint':
    'Um JSON com a conta, os códigos guardados e os consentimentos. É o direito de acesso.',
  'portal.rights.delete': 'Excluir minha conta',
  'portal.rights.deleteHint':
    'Apaga a conta, as sessões, os consentimentos e os códigos guardados. É o direito de eliminação.',
  'portal.delete.title': 'Excluir a conta',
  'portal.delete.detail':
    'A conta é apagada e, com ela, o vínculo entre essa pessoa e os códigos que verificou. Não dá para desfazer.',
  'portal.delete.confirmWord': 'EXCLUIR',
  'portal.delete.done':
    'A conta foi apagada. Não resta nenhum vínculo entre essa pessoa e os códigos que tinha guardado.',

  'module.reports': 'Relatórios',
  'module.reports.description': 'Relatórios formais do período, assinados e exportáveis.',
  'reports.title': 'Relatórios formais',
  'reports.description':
    'Um documento do período, com quem o gerou e quando. Diferente da analítica interativa: isto é o que se arquiva.',
  'reports.form.period': 'Período',
  'reports.form.from': 'De',
  'reports.form.to': 'Até',
  'reports.form.generate': 'Gerar o relatório',
  'reports.form.regenerate': 'Gerar de novo',
  'reports.period.last7': 'Últimos 7 dias',
  'reports.period.last30': 'Últimos 30 dias',
  'reports.period.last90': 'Últimos 90 dias',
  'reports.period.custom': 'Personalizado',
  'reports.form.invalidRange': 'A data inicial precisa ser anterior à final.',
  'reports.doc.title': 'Relatório de autenticação de produto',
  'reports.doc.brand': 'Marca',
  'reports.doc.period': 'Período',
  'reports.doc.generatedBy': 'Gerado por',
  'reports.doc.generatedAt': 'Gerado em',
  'reports.doc.reference': 'Referência',
  'reports.doc.kpis': 'Resumo do período',
  'reports.doc.totalScans': 'Escaneamentos',
  'reports.doc.genuine': 'Verificados',
  'reports.doc.suspicious': 'Suspeitos',
  'reports.doc.suspiciousRate': 'Taxa de suspeita',
  'reports.doc.openAlerts': 'Alertas abertos',
  'reports.doc.criticalAlerts': 'Alertas críticos abertos',
  'reports.doc.byResult': 'Escaneamentos por resultado',
  'reports.doc.result': 'Resultado',
  'reports.doc.scans': 'Escaneamentos',
  'reports.doc.share': 'Participação',
  'reports.doc.countries': 'Países com mais escaneamentos',
  'reports.doc.country': 'País',
  'reports.doc.daily': 'Atividade diária',
  'reports.doc.day': 'Dia',
  'reports.doc.units': 'Unidades',
  'reports.doc.refreshed': 'Agregados até {when}.',
  'reports.doc.privacy':
    'Os dados deste relatório são agregados. Não contém dados pessoais de consumidores.',
  'reports.doc.footer':
    'Documento gerado pela XYVONA. O conteúdo pode ser conferido contra o registro de auditoria do período.',
  'reports.doc.empty': 'Não houve escaneamentos no período escolhido.',
  'reports.print': 'Imprimir ou salvar em PDF',
  'reports.result.genuine': 'Verificado',
  'reports.result.already_seen': 'Já escaneado',
  'reports.result.not_activated': 'Sem ativação',
  'reports.result.invalid': 'Não verificável',
  'reports.result.stolen': 'Rolo roubado',
  'reports.result.recalled': 'Lote recolhido',
  'reports.result.out_of_territory': 'Fora do território',
  'reports.export.title': 'Exportações',
  'reports.export.description':
    'O detalhe em CSV, para arquivar ou cruzar com outro sistema. Cada pedido e cada download fica na auditoria.',
  'reports.export.kind': 'Conteúdo',
  'reports.export.format': 'Formato',
  'reports.export.format.csv': 'CSV (UTF-8)',
  'reports.export.format.csv_excel': 'CSV para Excel',
  'reports.export.kind.alerts': 'Alertas',
  'reports.export.kind.batches': 'Lotes',
  'reports.export.kind.units': 'Unidades',
  'reports.export.kind.scans_daily': 'Escaneamentos por dia (agregado)',
  'reports.export.kind.geo_cells': 'Células geográficas (agregado)',
  'reports.export.request': 'Pedir a exportação',
  'reports.export.requesting': 'Pedindo…',
  'reports.export.column.kind': 'Conteúdo',
  'reports.export.column.status': 'Status',
  'reports.export.column.rows': 'Linhas',
  'reports.export.column.requestedBy': 'Pedido por',
  'reports.export.column.created': 'Pedido em',
  'reports.export.column.actions': 'Ações',
  'reports.export.status.queued': 'Na fila',
  'reports.export.status.running': 'Gerando',
  'reports.export.status.done': 'Pronto',
  'reports.export.status.failed': 'Falhou',
  'reports.export.status.expired': 'Vencido',
  'reports.export.download': 'Baixar',
  'reports.export.downloading': 'Baixando…',
  'reports.export.empty': 'Nenhuma exportação foi pedida ainda.',

  'map.title': 'Mapa de calor',
  'map.description': 'Células geográficas agregadas pelo servidor. Nunca leituras individuais.',
  'map.loading': 'Carregando o mapa…',
  'map.cells': '{count} células',
  'map.canvas.label':
    'Mapa de calor de leituras por célula geográfica. A mesma informação está na tabela abaixo.',
  'map.recenter': 'Reenquadrar',
  'map.empty': 'Nenhuma célula para estes filtros',
  'map.empty.detail':
    'Tente um período mais longo, um detalhe menor ou um limite de leituras por célula mais baixo.',
  'map.truncated':
    'A resposta chegou ao teto de células. Reduza o detalhe para ver o período inteiro.',

  'map.filter.period': 'Período',
  'map.filter.days': '{days} dias',
  'map.filter.layer': 'Camada',
  'map.filter.precision': 'Detalhe',
  'map.filter.minScans': 'Mínimo por célula',
  'map.filter.alertOverlay': 'Sobrepor alertas',

  'map.layer.all': 'Todas as leituras',
  'map.layer.genuine': 'Genuínas',
  'map.layer.suspicious': 'Suspeitas',
  'map.layer.notActivated': 'Sem ativação',
  'map.layer.stolen': 'Rolo roubado',
  'map.layer.recalled': 'Lote recolhido',
  'map.layer.outOfTerritory': 'Fora do território',

  'map.precision.3': 'País (~156 km)',
  'map.precision.4': 'Região (~39 km)',
  'map.precision.5': 'Cidade (~4,9 km)',
  'map.precision.6': 'Bairro (~1,2 km)',

  'map.legend.title': 'Leituras por célula',
  'map.legend.alerts': 'Células com leituras suspeitas',

  'map.stat.scans': 'Leituras do período',
  'map.stat.cells': 'Células desenhadas',
  'map.stat.suspicious': 'Leituras suspeitas',
  'map.stat.cellSize': 'Tamanho da célula',
  'map.stat.cellSizeValue': '{width} × {height} km',

  'map.footer.window': 'De {from} a {to}',
  'map.footer.refreshed': 'Agregados em {when}',
  'map.footer.queryMs': 'Consulta: {ms} ms',

  'map.table.title': 'Células do período',
  'map.table.description':
    'A mesma informação do mapa, navegável pelo teclado. Cada linha foca a sua célula.',
  'map.table.caption': 'Células geográficas ordenadas por quantidade de leituras.',
  'map.table.cell': 'Célula',
  'map.table.country': 'País',
  'map.table.scans': 'Leituras',
  'map.table.share': 'Do total',
  'map.table.suspicious': 'Suspeitas',
  'map.table.peakUnits': 'Unidades (dia de pico)',
  'map.table.focus': 'Focar a célula em {lat}, {lon}',
  'map.table.more': 'Mostrar mais ({shown} de {total})',
  'map.table.clearSelection': 'Limpar seleção',

  'analytics.title': 'Analítica',
  'analytics.description':
    'Tendências, taxa de autenticação e penetração por região do período escolhido.',
  'analytics.period': 'Período',
  'analytics.days': '{days} dias',
  'analytics.refresh': 'Recalcular',
  'analytics.refreshing': 'Recalculando…',
  'analytics.refreshed': 'Recalculado: {rows} linhas em {ms} ms.',
  'analytics.window': 'De {from} a {to}',
  'analytics.refreshedAt': 'Agregados em {when}',

  'analytics.kpi.scans': 'Leituras',
  'analytics.kpi.authRate': 'Taxa de autenticação',
  'analytics.kpi.authRateHint': '{genuine} leituras genuínas',
  'analytics.kpi.suspiciousRate': 'Taxa de suspeita',
  'analytics.kpi.suspiciousHint': '{scans} leituras suspeitas',
  'analytics.kpi.openAlerts': 'Alertas abertos',
  'analytics.kpi.criticalHint': '{count} críticos',

  'analytics.chart.peak': 'máx. {value}',
  'analytics.chart.empty': 'Sem leituras neste período.',
  'analytics.chart.emptySuspicious': 'Nenhuma leitura suspeita no período.',
  'analytics.trend.title': 'Tendência',
  'analytics.trend.description':
    'Leituras por faixa. A faixa aumenta sozinha quando o período é longo, então o gráfico desenha sempre a mesma quantidade de pontos.',
  'analytics.trend.scans': 'Leituras',
  'analytics.trend.scansSummary': 'Leituras por faixa: {points} pontos, {total} no total.',
  'analytics.trend.suspicious': 'Leituras suspeitas',
  'analytics.trend.suspiciousSummary':
    'Leituras suspeitas por faixa: {points} pontos, {total} no total.',
  'analytics.trend.showData': 'Ver os números',
  'analytics.trend.tableCaption': 'Leituras por faixa do período.',
  'analytics.trend.column.period': 'Faixa',
  'analytics.trend.column.scans': 'Leituras',
  'analytics.trend.column.genuine': 'Genuínas',
  'analytics.trend.column.suspicious': 'Suspeitas',
  'analytics.trend.column.units': 'Unidades',
  'analytics.trend.grain': 'Cada ponto agrupa {days} dia(s) · {points} pontos desenhados.',

  'analytics.regions.title': 'Penetração por região',
  'analytics.regions.description': 'Onde o produto é lido, por país.',
  'analytics.regions.country': 'País',
  'analytics.regions.scans': 'Leituras',
  'analytics.regions.share': 'Do total',
  'analytics.regions.suspicious': 'Suspeitas',

  'analytics.results.title': 'Resultados de verificação',
  'analytics.results.description': 'Como terminou cada leitura do período.',
  'analytics.results.result': 'Resultado',
  'analytics.results.scans': 'Leituras',
  'analytics.results.share': 'Do total',

  'analytics.result.genuine': 'Genuíno',
  'analytics.result.alreadySeen': 'Já lido',
  'analytics.result.notActivated': 'Sem ativação',
  'analytics.result.invalid': 'Código inválido',
  'analytics.result.stolen': 'Rolo roubado',
  'analytics.result.recalled': 'Lote recolhido',
  'analytics.result.outOfTerritory': 'Fora do território',

  'analytics.tab.label': 'Visões de analítica',
  'analytics.tab.trends': 'Tendências',
  'analytics.tab.exports': 'Exportações',

  'analytics.export.new': 'Nova exportação',
  'analytics.export.newDescription':
    'O arquivo é gerado em segundo plano. Quando ficar pronto aparece abaixo com um botão de download.',
  'analytics.export.kind': 'Conteúdo',
  'analytics.export.format': 'Formato',
  'analytics.export.format.csv': 'CSV (UTF-8)',
  'analytics.export.format.excel': 'CSV para Excel',
  'analytics.export.since': 'Desde',
  'analytics.export.batch': 'Lote',
  'analytics.export.create': 'Gerar',
  'analytics.export.creating': 'Pedindo…',
  'analytics.export.privacy':
    'Nenhuma exportação leva dados de consumidores: são agregados e o inventário da marca. Quem pediu e quem baixou ficam registrados na auditoria.',
  'analytics.export.title': 'Exportações',
  'analytics.export.description': 'As últimas exportações desta marca e quem as pediu.',
  'analytics.export.working': 'Gerando…',
  'analytics.export.empty': 'Ainda não há exportações',
  'analytics.export.emptyHint': 'Gere uma pelo formulário acima.',
  'analytics.export.download': 'Baixar',
  'analytics.export.downloaded': '{name} baixado.',

  'analytics.export.kind.scansDaily': 'Leituras por dia',
  'analytics.export.kind.geoCells': 'Células geográficas',
  'analytics.export.kind.alerts': 'Alertas',
  'analytics.export.kind.batches': 'Lotes',
  'analytics.export.kind.units': 'Unidades',
  'analytics.export.kind.labels': 'Etiquetas (lista filtrada)',

  'analytics.export.status.queued': 'Na fila',
  'analytics.export.status.running': 'Gerando',
  'analytics.export.status.done': 'Pronto',
  'analytics.export.status.failed': 'Falhou',
  'analytics.export.status.expired': 'Vencido',

  'analytics.export.column.kind': 'Conteúdo',
  'analytics.export.column.status': 'Status',
  'analytics.export.column.rows': 'Linhas',
  'analytics.export.column.requestedBy': 'Pedido por',
  'analytics.export.column.created': 'Pedido em',
  'analytics.export.column.expires': 'Vence',

  'form.required': 'Este campo é obrigatório.',
  'form.save': 'Salvar',
  'form.saving': 'Salvando…',
  'form.cancel': 'Cancelar',

  'products.title': 'Catálogo de produtos',
  'products.description': 'SKU, GTIN e nível de segurança de cada apresentação.',
  'products.count': '{count} produtos carregados',
  'products.empty': 'Esta marca ainda não tem produtos.',
  'products.empty.detail': 'O catálogo vem primeiro: lotes e pedidos de etiquetas dependem de um produto.',
  'products.column.sku': 'SKU',
  'products.column.name': 'Nome',
  'products.column.gtin': 'GTIN',
  'products.column.tier': 'Nível de segurança',
  'products.column.risk': 'Risco',
  'products.column.status': 'Status',
  'products.column.created': 'Cadastro',
  'products.column.updated': 'Última alteração',
  'products.active': 'Ativo',
  'products.inactive': 'Inativo',
  'products.back': '← Voltar ao catálogo',
  'products.new': 'Novo produto',
  'products.new.title': 'Novo produto',
  'products.new.description': 'SKU e nome são obrigatórios. O GTIN é validado contra GS1.',
  'products.edit': 'Editar',
  'products.edit.title': 'Editar produto',
  'products.deactivate': 'Desativar',
  'products.deactivate.title': 'Desativar o produto',
  'products.deactivate.description': 'O produto sai de circulação. Não é excluído.',
  'products.deactivate.detail': '{sku} — {name} sai de circulação: não vai poder ser escolhido para novos lotes nem para ordens de etiquetas. Os lotes e as unidades que já existem não são tocados — um auditor precisa poder ver o catálogo como estava quando aquele lote foi serializado. Dá para reativar em Editar.',
  'products.deactivate.confirm': 'Desativar o produto',
  'products.reactivate': 'Reativar',
  'products.column.deactivated': 'Baixa',
  'products.deactivated.unknown': 'Antes de a data ser registrada',
  'products.detail.badId': 'Esse identificador de produto não é válido.',
  'products.detail.gs1': 'Cadeia GS1 (01)',
  'products.detail.batches': 'Ver os lotes deste produto →',
  'products.field.sku': 'SKU',
  'products.field.sku.hint': 'Único dentro da marca. É o que o ERP usa.',
  'products.field.name': 'Nome comercial',
  'products.field.gtin': 'GTIN',
  'products.field.gtin.hint': 'GTIN-8, 12, 13 ou 14. Normalizado para 14 dígitos, com verificação do dígito.',
  'products.field.tier': 'Nível de segurança',
  'products.field.tier.hint': 'Calibra a regra de reuso de código para este produto.',
  'products.field.active': 'Status',
  'products.field.active.hint': 'Um produto inativo não aceita lotes novos.',
  'products.gtin.none': 'sem GTIN',
  'products.gtin.normalized': 'Será salvo como {gtin}.',
  'products.gtin.notDigits': 'O GTIN só pode conter dígitos.',
  'products.gtin.length': 'O GTIN deve ter 8, 12, 13 ou 14 dígitos (você digitou {length}).',
  'products.gtin.checkDigit': 'O dígito verificador não é válido: deveria terminar em {expected}.',
  'products.tier.qr_only': 'Somente QR',
  'products.tier.qr_scratch': 'QR + código raspadinha',
  'products.tier.qr_nfc': 'QR + NFC',
  'products.tier.nfc': 'Somente NFC',

  'batches.title': 'Lotes de produção',
  'batches.description': 'Status, território, validade e unidades ativadas de cada lote.',
  'batches.count': '{count} lotes carregados',
  'batches.empty': 'Nenhum lote com estes filtros.',
  'batches.empty.detail': 'Tente remover o filtro de produto ou de status.',
  'batches.back': '← Voltar aos lotes',
  'batches.column.lot': 'Lote',
  'batches.column.product': 'Produto',
  'batches.column.expiry': 'Validade',
  'batches.column.territory': 'Território',
  'batches.column.activated': 'Unidades ativadas',
  'batches.column.risk': 'Risco',
  'batches.column.status': 'Status',
  'batches.column.actions': 'Ações',
  'batches.column.created': 'Cadastro',
  'batches.column.updated': 'Última alteração',
  'batches.territory.none': 'sem atribuição',
  'batches.activated.hint': 'As unidades ativadas são as que passaram pela linha de envase. É o alcance real de um recall.',
  'batches.recalledAt': 'Recolhido em',
  'batches.filter.title': 'Filtros',
  'batches.filter.description': 'Ficam na URL: o link mostra a mesma lista do outro lado.',
  'batches.filter.product': 'Produto',
  'batches.filter.status': 'Status',
  'batches.filter.allProducts': 'Todos os produtos',
  'batches.filter.allStatuses': 'Todos os status',
  'batches.status.open': 'Em envase',
  'batches.status.released': 'No mercado',
  'batches.status.recalled': 'Recolhido',
  'batches.status.closed': 'Esgotado',
  'batches.new': 'Novo lote',
  'batches.new.title': 'Novo lote de produção',
  'batches.new.description': 'Produto, código de lote e validade são a identidade GS1 do lote.',
  'batches.edit': 'Editar',
  'batches.edit.title': 'Editar lote',
  'batches.detail.badId': 'Esse identificador de lote não é válido.',
  'batches.field.product': 'Produto',
  'batches.field.product.hint': 'Não pode ser alterado depois: reescreveria a identidade de unidades já ativadas.',
  'batches.field.lot': 'Código do lote',
  'batches.field.lot.hint': 'É o (10) do GS1. Único por produto.',
  'batches.field.expiry': 'Validade',
  'batches.field.expiry.hint': 'É o (17) do GS1. Obrigatório: sem ele não há recall por validade.',
  'batches.field.territory': 'Território',
  'batches.field.territory.hint': 'Código de território cadastrado. Vazio = sem restrição de zona.',
  'batches.field.status': 'Status',
  'batches.field.status.hint': 'O recall não está aqui: tem ação própria, com confirmação.',
  'batches.recall': 'Recolher do mercado',
  'batches.recall.title': 'Recolher o lote do mercado',
  'batches.recall.step1.description': 'Revise o alcance antes de continuar. Este recall não pode ser desfeito.',
  'batches.recall.step2.description': 'Digite o código do lote para confirmar qual será recolhido.',
  'batches.recall.scope.title': 'Alcance do recall',
  'batches.recall.scope.units': 'Unidades ativadas afetadas',
  'batches.recall.effects.title': 'O que acontece ao confirmar',
  'batches.recall.effects.consumer': 'Quem ler uma unidade deste lote verá um alerta sanitário explícito, orientando a não consumir o produto.',
  'batches.recall.effects.alerts': 'Cada leitura posterior deste lote gera um alerta crítico na caixa de entrada.',
  'batches.recall.effects.irreversible': 'Não há como desfazer, nem pelo painel nem pela API.',
  'batches.recall.reason': 'Motivo do recall',
  'batches.recall.reason.hint': 'Fica registrado com o seu usuário e o horário. Vai ser lido por alguém que não participou desta decisão.',
  'batches.recall.reason.tooShort': 'Escreva ao menos {min} caracteres explicando o motivo.',
  'batches.recall.continue': 'Continuar',
  'batches.recall.back': 'Voltar',
  'batches.recall.confirm': 'Recolher do mercado',
  'batches.recall.recalling': 'Recolhendo…',
  'batches.recall.confirm.summary': 'Você vai recolher o lote {lot} de {product}, com {units} unidades ativadas no mercado.',
  'batches.recall.typeLot': 'Digite {lot} para confirmar',
  'batches.recall.typeLot.hint': 'Exatamente assim, respeitando maiúsculas e hifens.',
  'batches.recall.typeLot.mismatch': 'Não confere com o código do lote.',
  'batches.recall.done.title': 'Lote recolhido',
  'batches.recall.done.close': 'Fechar',
  'batches.recall.done.detail': 'O lote {lot} foi recolhido do mercado.',
  'batches.recall.done.consumer': 'A página do consumidor já mostra o alerta sanitário deste lote.',
  'batches.recall.done.audit': 'A ação ficou registrada com o seu usuário, o motivo e o horário.',
  'batches.recalled.banner': 'Lote recolhido do mercado',
  'batches.recalled.detail': 'O lote {lot} foi recolhido em {when}. Consumidores que o lerem veem um alerta sanitário.',

  'labels.title': 'Etiquetas',
  'labels.tab.orders': 'Pedidos',
  'labels.tab.rolls': 'Rolos',
  'labels.tab.stock': 'Estoque',
  'labels.tab.units': 'Códigos',
  'labels.journey':
    'Pedido → Rolo → Código (sem lote) → Ativação na fábrica → Código com lote e produto → Leitura do consumidor.',

  // ────────────────── gestão da etiqueta individual (#89)
  'labels.units.title': 'Códigos',
  'labels.units.description':
    'Uma etiqueta por vez: veredicto, QR, sinalizadores de estado e ações.',
  'labels.units.count': '{count} etiquetas',
  'labels.units.scope': 'Buscar por',
  'labels.units.scope.hint': 'Toda a marca, ou a partir de um lote, um rolo ou um código.',
  'labels.units.scope.all': 'Toda a marca',
  'labels.units.scope.batch': 'Lote',
  'labels.units.scope.roll': 'Rolo',
  'labels.units.scope.code': 'Código impresso',
  'labels.units.scope.value.id': 'Número',
  'labels.units.scope.value.code': 'Código',
  'labels.units.scope.apply': 'Buscar',
  'labels.units.filter.state': 'Estado',
  'labels.units.filter.all': 'Todos',
  'labels.units.filter.verdict': 'Veredito',
  'labels.units.filter.product': 'Produto',
  'labels.units.filter.scanned': 'Lida',
  'labels.units.filter.scanned.yes': 'Pelo menos uma vez',
  'labels.units.filter.scanned.no': 'Nunca',
  'labels.units.filter.alerts': 'Alertas',
  'labels.units.filter.alerts.open': 'Com alertas abertos',
  'labels.units.filter.expires': 'Lote vence até',
  'labels.units.filter.expires.hint': 'Só as ativadas: as demais ainda não têm lote.',
  'labels.units.export': 'Exportar esta lista',
  'labels.units.export.sending': 'Enfileirando…',
  'labels.units.export.queued': 'Exportação enfileirada: veja em Analítica › Exportações',
  'labels.units.export.failed': 'Não foi possível enfileirar a exportação.',
  'labels.units.noScope': 'Falta o número',
  'labels.units.noScope.detail':
    'Você escolheu buscar por lote, rolo ou código mas não disse qual. Complete, ou volte para «Toda a marca».',
  'labels.units.batchRef': 'Lote #{id}',
  'labels.units.empty': 'Não há etiquetas nesse âmbito',
  'labels.units.empty.detail':
    'O lote pode ainda não ter unidades ativadas, ou o filtro de estado pode não deixar passar nenhuma.',
  'labels.units.column.code': 'Código',
  'labels.units.column.state': 'Estado',
  'labels.units.column.verdict': 'Veredicto',
  'labels.units.column.scans': 'Leituras',
  'labels.units.column.lastScan': 'Última leitura',
  'labels.units.column.activated': 'Ativada',

  'labels.units.state.created': 'Criada',
  'labels.units.state.printed': 'Impressa',
  'labels.units.state.shipped': 'Despachada',
  'labels.units.state.received': 'Recebida',
  'labels.units.state.activated': 'Ativada',
  'labels.units.state.inMarket': 'No mercado',
  'labels.units.state.verified': 'Verificada',
  'labels.units.state.voided': 'Desativada',
  'labels.units.state.stolen': 'Roubada',
  'labels.units.state.recalled': 'Recolhida',
  'labels.units.state.destroyed': 'Destruída',
  'labels.units.state.compromised': 'Falsificada',

  'labels.units.verdict.genuine': 'Genuíno',
  'labels.units.verdict.alreadySeen': 'Já verificada antes',
  'labels.units.verdict.unverified': 'Não verifica',
  'labels.units.verdict.stolen': 'Roubada',
  'labels.units.verdict.compromised': 'Código clonado',
  'labels.units.verdict.recalled': 'Lote recolhido',

  'labels.units.detail.description':
    'O que o sistema responde hoje a quem ler esta etiqueta.',
  'labels.units.detail.consumerUrl': 'URL do QR',
  'labels.units.detail.scans': 'Leituras',
  'labels.units.detail.openAlerts': 'Alertas abertos',
  'labels.units.detail.riskScore': 'Risco',
  'labels.units.detail.batch': 'Lote',
  'labels.units.detail.expiry': 'Validade',
  'labels.units.detail.product': 'Produto',
  'labels.units.detail.territory': 'Território',
  'labels.units.detail.roll': 'Rolo',
  'labels.units.detail.created': 'Criada',
  'labels.units.detail.activated': 'Ativada',
  'labels.units.detail.firstScan': 'Primeira leitura',
  'labels.units.detail.lastScan': 'Última leitura',
  'labels.units.detail.from': 'Estado atual',
  'labels.units.identity.title': 'Identidade e rastreabilidade',

  'labels.units.qr.alt': 'Código QR da etiqueta {code}',
  'labels.units.qr.download': 'Baixar QR (SVG)',
  'labels.units.qr.downloading': 'Baixando…',
  'labels.units.qr.hint':
    'O QR leva essa URL dentro. Lendo-o com um telefone vê-se exatamente o que o consumidor vê.',

  'labels.units.flags.title': 'Sinalizadores de estado',
  'labels.units.flags.description':
    'Cada sinalizador vem de um dado do modelo. A coluna da direita diz qual.',
  'labels.units.flags.column.flag': 'Sinalizador',
  'labels.units.flags.column.value': 'Valor',
  'labels.units.flags.column.source': 'De onde vem',
  'labels.units.flags.yes': 'Sim',
  'labels.units.flags.no': 'Não',
  'labels.units.flag.scanned': 'Lida',
  'labels.units.flag.scanned.source': 'Tem pelo menos uma leitura registrada.',
  'labels.units.flag.expired': 'Vencida',
  'labels.units.flag.expired.source': 'A data de validade do lote já passou.',
  'labels.units.flag.authenticates': 'Ativa',
  'labels.units.flag.authenticates.source':
    'O estado autentica: é a única coisa que faz o consumidor ler «genuíno».',
  'labels.units.flag.suspicious': 'Suspeita',
  'labels.units.flag.suspicious.source': 'Tem alertas abertos sem resolver.',
  'labels.units.flag.compromised': 'Falsificada',
  'labels.units.flag.compromised.source':
    'O código foi confirmado clonado ou apareceu em produto falsificado.',
  'labels.units.flag.stolen': 'Roubada',
  'labels.units.flag.stolen.source': 'Foi denunciado o roubo desta etiqueta ou do seu rolo.',
  'labels.units.flag.recalled': 'Recolhida',
  'labels.units.flag.recalled.source': 'A unidade ou o seu lote estão recolhidos do mercado.',
  'labels.units.flag.voided': 'Desativada',
  'labels.units.flag.voided.source': 'Foi anulada: ativação errada ou etiqueta descartada.',
  'labels.units.flag.terminal': 'Sem saída',
  'labels.units.flag.terminal.source': 'O estado é terminal: nenhuma ação a move.',
  'labels.units.flag.reappeared': 'Encontrada',
  'labels.units.flag.reappeared.source':
    'Está fora de circulação e voltou a ser lida depois de ter saído.',

  'labels.units.actions.title': 'Ações',
  'labels.units.actions.description':
    'Só aparecem as que a máquina de estados permite a partir do estado atual.',
  'labels.units.actions.terminal':
    'Esta etiqueta está num estado terminal: nenhuma ação a move.',
  'labels.units.action.stolen': 'Marcar como roubada',
  'labels.units.action.stolen.effect':
    'A etiqueta passa a roubada e cada leitura posterior gera um alerta crítico.',
  'labels.units.action.compromised': 'Marcar como falsificada',
  'labels.units.action.compromised.effect':
    'O código é declarado clonado e deixa de autenticar. Se o produto legítimo ainda estiver no mercado, também deixa de verificar.',
  'labels.units.action.void': 'Desativar',
  'labels.units.action.void.effect':
    'A etiqueta é anulada: deixa de autenticar e não volta ao circuito.',
  'labels.units.action.effect.audit':
    'Fica registrado na auditoria com o seu usuário, a hora e o motivo.',
  'labels.units.action.effect.irreversible': 'Não é possível desfazer.',
  'labels.units.action.step1': 'O que acontece com a etiqueta e por que você faz isso.',
  'labels.units.action.step2': 'Digite o código impresso para confirmar.',
  'labels.units.action.typeCode': 'Código da etiqueta',
  'labels.units.action.confirm': 'Aplicar',
  'labels.units.action.applying': 'Aplicando…',
  'labels.units.action.confirm.summary':
    'Você vai mudar o estado da etiqueta {code}. Esta ação não se desfaz.',
  'labels.units.action.done.title': 'Pronto',
  'labels.units.action.done.close': 'Fechar',
  'labels.units.action.done.detail': 'A etiqueta {code} ficou no estado {state}.',
  'labels.units.action.done.audit': 'Entrada de auditoria #{seq}.',
  'labels.units.action.done.cacheWarning':
    'Não foi possível avisar o serviço de verificação. A mudança já está aplicada e chega em poucos minutos.',
  'labels.units.reason': 'Motivo',
  'labels.units.reason.hint':
    'O que você comprovou. Vai ser lido por alguém que não estava aqui, talvez daqui a três anos.',
  'labels.units.reason.tooShort': 'Escreva ao menos uma frase.',
  'labels.units.reference': 'Referência',
  'labels.units.reference.hint': 'Número do boletim, processo ou id do caso.',

  'labels.units.evidence.download': 'Baixar pacote de evidências',
  'labels.units.evidence.downloading': 'Preparando…',
  'labels.units.evidence.privacy':
    'Inclui a ficha, as leituras com geo em nível de cidade, os alertas e a custódia do rolo. Sem dados pessoais: não leva hashes de IP nem de dispositivo nem coordenadas exatas. Fica registrado quem exportou.',

  'labels.units.history.title': 'Histórico de estado',
  'labels.units.history.description':
    'Append-only: as ações manuais sobre esta etiqueta, com o seu motivo.',
  'labels.units.history.empty': 'Esta etiqueta não teve ações manuais.',
  'labels.units.history.column.when': 'Quando',
  'labels.units.history.column.change': 'Mudança',
  'labels.units.history.column.reason': 'Motivo',
  'labels.units.history.column.actor': 'Quem',
  'labels.units.history.column.audit': 'Auditoria',
  'labels.orders.title': 'Pedidos de etiquetas',
  'labels.orders.description': 'O que foi comprado da gráfica e em que estado está cada pedido.',
  'labels.orders.count': '{count} pedidos carregados',
  'labels.orders.empty': 'Nenhum pedido com este filtro.',
  'labels.orders.empty.detail': 'Um pedido nasce como rascunho e não reserva nenhum código até ser gerado.',
  'labels.orders.back': '← Voltar aos pedidos',
  'labels.orders.new': 'Novo pedido',
  'labels.orders.new.title': 'Novo pedido de etiquetas',
  'labels.orders.new.description': 'O pedido nasce como rascunho: ainda não reserva códigos e pode ser cancelado.',
  'labels.orders.create': 'Criar pedido',
  'labels.orders.filter.status': 'Status',
  'labels.orders.filter.all': 'Todos os status',
  'labels.orders.column.code': 'Pedido',
  'labels.orders.column.supplier': 'Fornecedor',
  'labels.orders.column.quantity': 'Etiquetas',
  'labels.orders.column.rolls': 'Rolos',
  'labels.orders.column.serials': 'Faixa de séries',
  'labels.orders.column.status': 'Status',
  'labels.orders.column.generated': 'Gerado',
  'labels.orders.column.created': 'Cadastro',
  'labels.order.status.draft': 'Rascunho',
  'labels.order.status.generating': 'Gerando',
  'labels.order.status.generated': 'Gerado',
  'labels.order.status.cancelled': 'Cancelado',
  'labels.order.badId': 'Esse identificador de pedido não é válido.',
  'labels.order.serials.pending': 'atribuída ao gerar',
  'labels.order.field.code': 'Código do pedido',
  'labels.order.field.code.hint': 'O número da ordem de compra enviada ao fornecedor.',
  'labels.order.field.supplier': 'Fornecedor',
  'labels.order.field.quantity': 'Etiquetas a fabricar',
  'labels.order.field.quantity.invalid': 'Um número inteiro entre 1 e {max}.',
  'labels.order.field.unitsPerRoll': 'Etiquetas por rolo',
  'labels.order.field.unitsPerRoll.invalid': 'Um número inteiro entre 1 e {max}.',
  'labels.order.field.product': 'Produto',
  'labels.order.field.product.hint': 'Opcional. As etiquetas podem ser feitas antes de saber a qual SKU vão.',
  'labels.order.field.product.none': 'Sem produto atribuído',
  'labels.order.field.keyVersion': 'Versão da chave',
  'labels.order.field.notes': 'Observações',
  'labels.order.rollPreview': 'Este pedido produz {rolls} rolos para {units} etiquetas.',
  'labels.order.rollPreview.unknown': 'Preencha a quantidade e as etiquetas por rolo para ver quantos rolos saem.',
  'labels.order.generate': 'Gerar códigos',
  'labels.order.generate.section': 'Geração de códigos',
  'labels.order.generate.section.hint': 'Reserva a faixa de séries, cria os rolos e produz o arquivo cifrado para a gráfica.',
  'labels.order.generate.title': 'Gerar os códigos deste pedido',
  'labels.order.generate.description': 'É a operação que reserva definitivamente uma faixa de códigos.',
  'labels.order.generate.confirm': 'Gerar agora',
  'labels.order.generate.effect.units': 'As unidades nascem em CREATED: uma etiqueta recém-fabricada não autentica nada até ser ativada na linha de envase.',
  'labels.order.generate.effect.range': 'A faixa de séries fica reservada e não pode ser reutilizada, nem cancelando o pedido.',
  'labels.order.generate.effect.key': 'O arquivo da gráfica é cifrado antes de sair do processo. A chave é entregue fora da plataforma.',
  'labels.order.progress.title': 'Gerando {units} etiquetas…',
  'labels.order.progress.elapsed': 'Tempo decorrido',
  'labels.order.progress.serverStatus': 'Status no servidor',
  'labels.order.progress.seconds': '{seconds} s',
  'labels.order.progress.note': 'A geração roda dentro de uma transação, então não há porcentagem para ler: o status do servidor é relido a cada segundo e, ao terminar, aparecem os tempos reais por fase.',
  'labels.order.generated.title': 'Códigos gerados',
  'labels.order.generated.units': 'Unidades',
  'labels.order.generated.rolls': 'Rolos',
  'labels.order.generated.elapsed': 'Duração',
  'labels.order.generated.rate': 'Velocidade',
  'labels.order.generated.rate.value': '{rate} unidades/s',
  'labels.order.cancel': 'Cancelar pedido',
  'labels.order.cancel.title': 'Cancelar o pedido',
  'labels.order.cancel.description': 'Só dá para cancelar enquanto for rascunho.',
  'labels.order.cancel.detail': 'O pedido {code} será cancelado. Um pedido já gerado não se cancela: o que se anula ali são os rolos, um a um.',
  'labels.order.cancel.confirm': 'Cancelar o pedido',
  'labels.order.rolls.title': 'Rolos deste pedido',
  'labels.order.rolls.description': 'Cada rolo leva uma faixa contígua de séries e nenhuma faixa se sobrepõe a outra.',
  'labels.order.rolls.empty': 'Este pedido ainda não produziu rolos.',
  'labels.printFile.title': 'Arquivo para a gráfica',
  'labels.printFile.description': 'Link assinado e temporário para o arquivo cifrado de dados variáveis.',
  'labels.printFile.request': 'Pedir o link de download',
  'labels.printFile.request.hint': 'O link é gerado na hora e expira: não fica em cache.',
  'labels.printFile.notGenerated': 'Este pedido ainda não tem arquivo.',
  'labels.printFile.notGenerated.detail': 'O arquivo é produzido ao gerar os códigos.',
  'labels.printFile.key.title': 'A chave de decifração não passa por aqui',
  'labels.printFile.key.detail': 'É entregue ao fornecedor por um canal fora da plataforma. Não a envie pelo mesmo meio que este link: separadas, um link interceptado não dá acesso a nenhum segredo.',
  'labels.printFile.records': 'Registros',
  'labels.printFile.bytes': 'Tamanho (bytes)',
  'labels.printFile.expires': 'Expira em',
  'labels.printFile.expires.value': '{minutes} minutos',
  'labels.printFile.cipher': 'Cifra',
  'labels.printFile.sha256': 'SHA-256 do arquivo',
  'labels.printFile.nonce': 'Nonce',
  'labels.printFile.download': 'Baixar o arquivo cifrado',
  'labels.printFile.renew': 'Renovar o link',
  'labels.rolls.title': 'Rolos',
  'labels.rolls.description': 'Estoque físico de etiquetas e sua cadeia de custódia.',
  'labels.rolls.count': '{count} rolos carregados',
  'labels.rolls.empty': 'Nenhum rolo com este filtro.',
  'labels.rolls.empty.detail': 'Os rolos são criados ao gerar os códigos de um pedido.',
  'labels.rolls.back': '← Voltar aos rolos',
  'labels.rolls.filter.state': 'Estado de custódia',
  'labels.rolls.filter.state.hint': 'Filtre por «roubado» para ver os rolos denunciados.',
  'labels.rolls.filter.all': 'Todos os estados',
  'labels.rolls.column.code': 'Rolo',
  'labels.rolls.column.order': 'Pedido',
  'labels.rolls.column.serials': 'Faixa de séries',
  'labels.rolls.column.units': 'Etiquetas',
  'labels.rolls.column.state': 'Estado',
  'labels.rolls.column.supplier': 'Fornecedor',
  'labels.rolls.column.updated': 'Última novidade',
  'labels.roll.badId': 'Esse identificador de rolo não é válido.',
  'labels.roll.noSupplier': 'Sem fornecedor registrado',
  'labels.roll.index': 'Número do rolo no pedido',
  'labels.roll.carrier': 'Transportadora',
  'labels.roll.tracking': 'Código de rastreio',
  'labels.roll.printedAt': 'Impresso',
  'labels.roll.shippedAt': 'Despachado',
  'labels.roll.receivedAt': 'Recebido',
  'labels.roll.workerStatus': 'Status projetado (motor de regras)',
  'labels.roll.state.ordered': 'Pedido',
  'labels.roll.state.printed': 'Impresso',
  'labels.roll.state.shipped': 'Despachado',
  'labels.roll.state.received': 'Recebido',
  'labels.roll.state.in_use': 'Em uso',
  'labels.roll.state.consumed': 'Consumido',
  'labels.roll.state.stolen': 'Roubado',
  'labels.roll.state.destroyed': 'Destruído',
  'labels.roll.state.voided': 'Anulado',
  'labels.chain.title': 'Cadeia de custódia',
  'labels.chain.interrupted': 'A cadeia foi interrompida: o rolo está em «{state}».',
  'labels.transition.title': 'Mover o rolo',
  'labels.transition.description': 'Só são oferecidos os destinos legítimos a partir do estado atual. Nada aponta para trás: uma correção é registrada como evento novo.',
  'labels.transition.terminal': 'O rolo está em «{state}» e não tem movimentos possíveis.',
  'labels.transition.to': 'Novo estado',
  'labels.transition.to.hint': 'O roubo não está aqui: tem ação própria, com confirmação.',
  'labels.transition.location': 'Local',
  'labels.transition.reference': 'Referência',
  'labels.transition.reference.hint': 'Nota de remessa, número de rastreio ou ata de destruição.',
  'labels.transition.carrier': 'Transportadora',
  'labels.transition.tracking': 'Código de rastreio',
  'labels.transition.note': 'Observação',
  'labels.transition.submit': 'Registrar o movimento',
  'labels.transition.done': 'Movimento registrado: o rolo ficou em «{to}» e afetou {units} etiquetas.',
  'labels.custody.title': 'Histórico de custódia',
  'labels.custody.description': 'Append-only: os eventos não são editados nem apagados. Uma cadeia de custódia editável não responde nada.',
  'labels.custody.empty': 'Este rolo ainda não tem eventos de custódia.',
  'labels.custody.empty.detail': 'O primeiro evento é registrado ao mover o rolo de «Pedido».',
  'labels.custody.from': 'de {from}',
  'labels.custody.units': 'Etiquetas afetadas',
  'labels.custody.location': 'Local',
  'labels.custody.reference': 'Referência',
  'labels.custody.actor': 'Registrado por',
  'labels.custody.actor.value': 'usuário #{id}',
  'labels.custody.complete': 'Início da cadeia.',
  'labels.stolen.action': 'Denunciar roubo',
  'labels.stolen.title': 'Denunciar o rolo como roubado',
  'labels.stolen.step1.description': 'Revise o alcance e conte o que aconteceu. Isto não pode ser desfeito.',
  'labels.stolen.step2.description': 'Digite o código do rolo para confirmar qual será denunciado.',
  'labels.stolen.scope.units': 'Etiquetas do rolo',
  'labels.stolen.effects.units': 'Todas as etiquetas do rolo ainda não ativadas passam a «roubada» e deixam de poder autenticar.',
  'labels.stolen.effects.alerts': 'Cada leitura posterior de qualquer dessas etiquetas gera um alerta crítico, com a evidência de onde apareceu.',
  'labels.stolen.effects.activated': 'As unidades já ativadas não são tocadas: produto legítimo que já está no mercado não é desativado.',
  'labels.stolen.effects.irreversible': '«Roubado» é um estado sem saída: o rolo não pode mais ser movido.',
  'labels.stolen.note': 'O que aconteceu',
  'labels.stolen.note.hint': 'Obrigatório. O alerta crítico que isto gera será lido por um investigador que não estava aqui.',
  'labels.stolen.note.tooShort': 'Conte o que aconteceu, nem que seja em uma linha.',
  'labels.stolen.policeReport': 'Número do boletim de ocorrência',
  'labels.stolen.policeReport.hint': 'Opcional, mas é o que sustenta o caso diante de um auditor.',
  'labels.stolen.location': 'Local do roubo',
  'labels.stolen.confirm': 'Denunciar o roubo',
  'labels.stolen.reporting': 'Registrando a denúncia…',
  'labels.stolen.confirm.summary': 'Você vai denunciar o roubo do rolo {code}, com {units} etiquetas.',
  'labels.stolen.typeCode': 'Digite {code} para confirmar',
  'labels.stolen.done.title': 'Roubo registrado',
  'labels.stolen.done.detail': 'O rolo {code} ficou marcado como roubado e {units} etiquetas deixaram de poder autenticar.',
  'labels.stolen.done.next': 'A partir de agora, qualquer leitura dessas etiquetas gera um alerta crítico na caixa de entrada.',
  'labels.stolen.banner': 'Rolo denunciado como roubado',
  'labels.stolen.banner.detail': 'A denúncia foi registrada em {when}. Cada leitura de suas etiquetas gera um alerta crítico.',
  // programação de relatórios (#70)
  'reports.schedule.title': 'Envio programado',
  'reports.schedule.description':
    'O relatório é gerado no servidor e sai por e-mail, sem ninguém precisar entrar. Cada envio fica na auditoria.',
  'reports.schedule.adminOnly':
    'Programar um envio é de administrador: decide quais dados da marca chegam a outras pessoas, toda semana, sem ninguém revisar de novo.',
  'reports.schedule.form.period': 'Que período cobre',
  'reports.schedule.form.cadence': 'Com que frequência',
  'reports.schedule.form.weekday': 'Dia da semana',
  'reports.schedule.form.monthday': 'Dia do mês',
  'reports.schedule.form.hour': 'Hora',
  'reports.schedule.form.recipients': 'Quem recebe',
  'reports.schedule.form.recipientsHint':
    'Somente usuários da sua marca. Um envio automático para uma caixa externa tiraria dados da marca toda semana sem ninguém revisar.',
  'reports.schedule.form.submit': 'Programar',
  'reports.schedule.form.saving': 'Programando…',
  'reports.schedule.period.lastWeek': 'A semana passada',
  'reports.schedule.period.lastMonth': 'O mês passado',
  'reports.schedule.period.last30d': 'Os últimos 30 dias',
  'reports.schedule.cadence.weekly': 'Semanal',
  'reports.schedule.cadence.monthly': 'Mensal',
  'reports.schedule.weekday.1': 'Segunda-feira',
  'reports.schedule.weekday.2': 'Terça-feira',
  'reports.schedule.weekday.3': 'Quarta-feira',
  'reports.schedule.weekday.4': 'Quinta-feira',
  'reports.schedule.weekday.5': 'Sexta-feira',
  'reports.schedule.weekday.6': 'Sábado',
  'reports.schedule.weekday.7': 'Domingo',
  'reports.schedule.monthlyOn': 'No dia',
  'reports.schedule.empty': 'Não há envios programados',
  'reports.schedule.emptyHint': 'Programe um e o relatório sai sozinho, com o período já calculado.',
  'reports.schedule.paused': 'Em pausa',
  'reports.schedule.column.what': 'Relatório',
  'reports.schedule.column.when': 'Quando sai',
  'reports.schedule.column.recipients': 'Destinatários',
  'reports.schedule.column.next': 'Próximo envio',
  'reports.schedule.column.actions': 'Ações',
  'reports.schedule.runNow': 'Executar agora',
  'reports.schedule.pause': 'Pausar',
  'reports.schedule.resume': 'Retomar',
  'reports.schedule.delete': 'Excluir',
  'reports.schedule.runs.title': 'Envios',
  'reports.schedule.runs.description':
    'Quando saiu, para quem e com qual resultado. Excluir uma programação não apaga este registro.',
  'reports.schedule.runs.column.period': 'Período',
  'reports.schedule.runs.column.status': 'Status',
  'reports.schedule.runs.column.recipients': 'Destinatários',
  'reports.schedule.runs.column.trigger': 'Origem',
  'reports.schedule.runs.column.when': 'Quando',
  'reports.schedule.runs.refresh': 'Atualizar',
  'reports.schedule.runs.manual': 'Manual',
  'reports.schedule.runs.scheduled': 'Por calendário',
  'reports.schedule.run.queued': 'Na fila',
  'reports.schedule.run.running': 'Gerando',
  'reports.schedule.run.sent': 'Enviado',
  'reports.schedule.run.failed': 'Falhou',

  // ───────────────────────────── verificação · mensagens ao consumidor (#90)
  'module.verification': 'Verificação',
  'module.verification.description':
    'O que lê quem escaneia o seu produto, em espanhol, inglês e português.',

  'verification.title': 'Mensagens da página pública',
  'verification.description':
    'O que lê alguém que acabou de escanear o seu produto. O que você deixar em branco usa o texto padrão da XYVONA.',
  'verification.readOnly': 'Somente leitura',
  'verification.save': 'Publicar',
  'verification.saving': 'Publicando…',
  'verification.saved': 'Publicado. Os novos escaneamentos já veem.',

  'verification.locale.es': 'Español',
  'verification.locale.en': 'English',
  'verification.locale.pt': 'Português',
  'verification.locale.hint':
    'Cada idioma é guardado separadamente: o que você deixar em branco em um usa o texto padrão NAQUELE idioma, nunca o de outro.',

  'verification.field.title': 'Título',
  'verification.field.body': 'Texto',
  'verification.field.bodyHint': 'Em branco = o texto padrão.',
  'verification.field.bodyVars':
    'Você pode usar {date} e {place}: são substituídos pela data e pela cidade da primeira verificação.',
  'verification.field.defaultPlaceholder': 'Em branco: usa-se o texto padrão',

  'verification.slot.seeIt': 'Ver',
  'verification.slot.genuine': 'Produto verificado',
  'verification.slot.genuine.hint':
    'Primeira verificação de uma unidade ativada. É o único estado que afirma autenticidade.',
  'verification.slot.already': 'Já verificado antes',
  'verification.slot.already.hint':
    'O código é válido, mas alguém já escaneou. Não é uma acusação: pode ter sido a mesma pessoa.',
  'verification.slot.unverified': 'Não conseguimos verificar',
  'verification.slot.unverified.hint':
    'Código ilegível, etiqueta danificada, unidade ainda não ativada ou bobina denunciada. Os quatro casos compartilham esta mensagem de propósito.',
  'verification.slot.recalled': 'Lote recolhido (alerta sanitário)',
  'verification.slot.recalled.hint':
    'A única mensagem que afirma algo. Precisa dizer o que fazer com o produto e para quem ligar.',
  'verification.slot.throttled': 'Muitas tentativas',
  'verification.slot.throttled.hint':
    'Aparece ao enviar vários relatos seguidos do mesmo aparelho. Não tem estado de pré-visualização próprio.',
  'verification.slot.contact': 'Rodapé: como falar com você',
  'verification.slot.contact.hint':
    'O cabeçalho do canal de denúncia. Aparece nos estados que oferecem contato; telefone e e-mail vêm das configurações da sua marca.',

  'verification.preview.title': 'Pré-visualização',
  'verification.preview.description': 'A página real, com dados de amostra.',
  'verification.preview.states': 'Estado a pré-visualizar',
  'verification.preview.frameTitle': 'Página do consumidor, pré-visualização',
  'verification.preview.note':
    'É a página pública em modo demonstração: não consulta nenhuma unidade nem lote real, e o texto não publicado só se vê aqui.',
  'verification.state.genuine': 'Verificado',
  'verification.state.already_seen': 'Já verificado',
  'verification.state.unverified': 'Não verificado',
  'verification.state.recalled': 'Lote recolhido',

  'verification.rule.title': 'Por que não existe uma mensagem de «produto não genuíno»',
  'verification.rule.what':
    'A quem escaneia nunca se afirma que o produto seja ilegítimo. A pior mensagem possível é «não conseguimos verificar este código», junto com como falar com você.',
  'verification.rule.why':
    'Um aviso errado com esse texto diz a um comprador legítimo que o que ele tem na mão não é. Errar uma única vez é um problema jurídico para a sua marca e para a XYVONA. E avisar de um roubo a quem está com o produto ou acusa quem comprou de boa-fé, ou avisa justamente quem não deveria ser avisado.',
  'verification.rule.where':
    'A certeza chega até você: a unidade, o lote, onde e quando foi escaneado e a evidência completa aparecem na sua caixa de alertas.',
  'verification.rule.recall':
    'A única exceção é o recolhimento de lote: ali o risco de não avisar supera o de avisar, e a mensagem aparece como alerta sanitário.',

  'verification.rejected.field': 'Não pode ser publicado: {terms}.',
  'verification.rejected.item': '{locale} · {slot} · {field}: {terms}',


  // ───────────────────────────────── back-office (#109)
  'module.clients.description': 'Cadastro de marcas, suspensão e métricas por cliente.',
  'module.requests.description': 'O que os clientes pedem: rolos de etiquetas.',
  'clients.title': 'Clientes',
  'clients.description': 'As marcas que operam na XYVONA.',
  'clients.new': 'Cadastrar cliente',
  'clients.count': '{count} clientes',
  'clients.empty': 'Ainda não há clientes.',
  'clients.col.client': 'Cliente',
  'clients.col.industry': 'Setor',
  'clients.col.units': 'Unidades',
  'clients.col.activated': 'Ativadas',
  'clients.col.users': 'Usuários',
  'clients.col.lastScan': 'Último escaneamento',
  'clients.col.state': 'Estado',
  'clients.state.active': 'Ativo',
  'clients.state.suspended': 'Suspenso',
  'clients.suspend': 'Suspender',
  'clients.reactivate': 'Reativar',
  'clients.suspend.confirm': 'Suspender corta o acesso de TODOS os usuários desta marca na hora. As etiquetas já impressas continuam verificando como genuínas.',
  'clients.suspend.reason': 'Motivo (fica na auditoria)',
  'clients.suspend.reasonHint': 'Pelo menos {min} caracteres: quem tiver que explicar isto vai ler.',
  'clients.suspend.failed': 'Não foi possível mudar o estado. Tente de novo.',
  'clients.reactivate.confirm': 'Reativar devolve o acesso a TODOS os usuários desta marca no próximo login.',
  'clients.form.slug': 'Identificador',
  'clients.form.slugHint': 'É com isso que entra no dashboard. Não pode ser alterado depois.',
  'clients.form.legalName': 'Razão social',
  'clients.form.country': 'País',
  'clients.form.industry': 'Setor',
  'clients.form.portal': 'Portal do consumidor',
  'clients.form.portalPharma': 'Proibido em pharma: quais medicamentos uma pessoa identificada escaneou é dado de saúde.',
  'clients.form.adminEmail': 'E-mail do primeiro administrador',
  'clients.form.adminName': 'Nome',
  'clients.form.submit': 'Criar cliente',
  'clients.created.title': 'Cliente criado',
  'clients.created.password': 'Senha temporária',
  'clients.created.once': 'Mostrada UMA vez. Se perder, redefina — não dá para consultar de novo.',
  'clients.created.copy': 'Copiar',
  'clients.created.copied': 'Copiado',
  'requests.title': 'Pedidos de rolos',
  'requests.description': 'O que os clientes pediram. Pendentes primeiro, mais antigos no topo.',
  'requests.empty': 'Sem pedidos.',
  'requests.filter.all': 'Todos',
  'requests.filter.pending': 'Pendentes',
  'requests.col.client': 'Cliente',
  'requests.col.quantity': 'Unidades',
  'requests.col.neededBy': 'Para',
  'requests.col.notes': 'Notas',
  'requests.col.state': 'Estado',
  'requests.state.pending': 'Pendente',
  'requests.state.approved': 'Aprovado',
  'requests.state.fulfilled': 'Entregue',
  'requests.state.rejected': 'Recusado',
  'requests.approve': 'Aprovar',
  'requests.reject': 'Recusar',
  'requests.fulfill': 'Marcar como entregue',
  'requests.resolution': 'Resposta ao cliente',
  'stock.title': 'Estoque de etiquetas',
  'stock.description': 'Rolos em nome da XYVONA, ainda sem cliente.',
  'stock.empty': 'Sem rolos em estoque. Gere um pedido em Etiquetas.',
  'stock.showAll': 'Mostrar também os entregues',
  'stock.col.roll': 'Rolo',
  'stock.col.order': 'Pedido',
  'stock.col.supplier': 'Fornecedor',
  'stock.col.units': 'Unidades',
  'stock.col.range': 'Séries',
  'stock.col.state': 'Custódia',
  'stock.col.owner': 'Atribuído a',
  'stock.assign': 'Entregar a um cliente',
  'stock.assign.title': 'Entregar o rolo',
  'stock.assign.client': 'Cliente',
  'stock.assign.warning': 'Move o rolo e suas unidades ao cliente. Fica na auditoria dos dois lados e não dá para desfazer daqui.',
  'stock.assign.submit': 'Entregar',
  'stock.assigned': '{count} unidades entregues.',
  'hq.free': 'Em estoque',
  'hq.close': 'Fechar',
  'labels.verify.request': 'Verificar o arquivo',
  'labels.verify.request.hint': 'Descriptografa em memória e compara com o banco: cada código e cada segredo. É o que se olha antes de enviar ao fornecedor.',
  'labels.verify.ok': 'O arquivo corresponde às unidades do banco.',
  'labels.verify.fail': 'O arquivo NÃO corresponde. Não envie.',
  'labels.verify.records': 'Registros',
  'labels.verify.checked': 'Verificados',
  'labels.verify.sampled': 'amostra',
  'labels.verify.codes': 'Códigos inválidos',
  'labels.verify.secrets': 'Segredos que não conferem',
  'labels.verify.sha256': 'Integridade do objeto',
  'labels.verify.sha256.ok': 'confere com o registrado',
  'labels.verify.sha256.fail': 'NÃO confere: o arquivo mudou',
  'labels.verify.preview': 'Primeiras linhas do arquivo',
  'labels.verify.col.roll': 'Rolo',
  'labels.verify.col.code': 'Código',
  'labels.verify.col.secret': 'Segredo',
  'labels.verify.masked': 'O segredo vai mascarado: em claro só existe no arquivo cifrado e sob a camada de raspagem.',
  'labels.verify.full': 'Verificar o arquivo inteiro',

  // ── pedidos de rolos do cliente (#142)
  'labels.tab.requests': 'Solicitações',
  'rollRequests.title': 'Solicitações de rolos',
  'rollRequests.description':
    'O que você pede à XYVONA. Nós geramos os códigos, mandamos imprimir e entregamos os rolos.',
  'rollRequests.new': 'Solicitar rolos',
  'rollRequests.new.description':
    'A quantidade é em etiquetas. Quantas cabem em cada rolo definimos com o fornecedor conforme a tiragem.',
  'rollRequests.send': 'Enviar solicitação',
  'rollRequests.empty': 'Você ainda não solicitou rolos',
  'rollRequests.empty.detail':
    'Depois de solicitar, o pedido fica aqui com seu status até ser entregue.',
  'rollRequests.col.quantity': 'Etiquetas',
  'rollRequests.col.neededBy': 'Para quando',
  'rollRequests.col.notes': 'Notas',
  'rollRequests.col.state': 'Status',
  'rollRequests.col.resolution': 'Resposta',
  'rollRequests.field.quantity': 'Quantidade de etiquetas',
  'rollRequests.field.quantity.hint': 'Em etiquetas, não em rolos.',
  'rollRequests.field.quantity.invalid': 'Um número inteiro entre 1 e {max}.',
  'rollRequests.field.product': 'Produto',
  'rollRequests.field.product.hint': 'Opcional. Ajuda a preparar a tiragem.',
  'rollRequests.field.product.none': 'Sem produto atribuído',
  'rollRequests.field.neededBy': 'Data em que precisa delas',
  'rollRequests.field.neededBy.hint': 'Opcional. É uma referência, não um compromisso.',
  'rollRequests.field.notes': 'Notas',
  'rollRequests.field.notes.hint': 'Nível de segurança, destino, o que for preciso esclarecer.',

  // ── #147 · clave del pedido y herramientas del back-office
  'module.tooling': 'Ferramentas',
  'module.tooling.description': 'A página do impressor e as permissões da equipe.',
  'labels.orderKey.request': 'Ver a chave deste pedido',
  'labels.orderKey.request.hint': 'Só aparece quando você pede, e fica registrado quem olhou e quando.',
  'labels.orderKey.working': 'Solicitando…',
  'labels.orderKey.value': 'Chave do pedido {order}',
  'labels.orderKey.copy': 'Copiar a chave',
  'labels.orderKey.copied': 'Copiada',
  'labels.orderKey.hide': 'Ocultar',
  'labels.orderKey.warning.title': 'Envie por um canal diferente do link de download',
  'labels.orderKey.warning.detail': 'Se o arquivo e a chave viajarem pelo mesmo meio, as duas camadas viram uma só. Telefone, WhatsApp ou pessoalmente — nunca o mesmo e-mail do link.',
  'labels.orderKey.audited': 'Esta solicitação ficou na auditoria. A chave não: apenas que você a viu.',
  'tooling.supplier.title': 'Página para o impressor',
  'tooling.supplier.description': 'O arquivo com que o fornecedor abre o .xyvc1. Enviado uma vez e guardado por ele.',
  'tooling.supplier.version': 'Versão',
  'tooling.supplier.bytes': 'Tamanho (bytes)',
  'tooling.supplier.sha256': 'SHA-256',
  'tooling.supplier.download': 'Baixar a página',
  'tooling.supplier.downloading': 'Baixando…',
  'tooling.supplier.sendOnce': 'Não precisa reenviar a cada pedido: só quando a versão subir.',
  'tooling.permissions.title': 'Permissões da equipe XYVONA',
  'tooling.permissions.description': 'Quem pode ver a chave de descriptografia de um pedido. Concedida uma a uma e auditada.',
  'tooling.permissions.empty': 'Não há usuários da XYVONA.',
  'tooling.permissions.col.person': 'Pessoa',
  'tooling.permissions.col.permission': 'Ver a chave',
  'tooling.permissions.col.action': 'Ação',
  'tooling.permissions.granted': 'Concedida',
  'tooling.permissions.none': 'Sem permissão',
  'tooling.permissions.grant': 'Conceder',
  'tooling.permissions.revoke': 'Revogar',
  'tooling.permissions.note': 'Motivo (fica na auditoria)',
  'tooling.permissions.noteRequired': 'Detalhe (obrigatório com «outro»)',
  'tooling.permissions.reason': 'Motivo',
  'tooling.permissions.reason.reprint': 'Reimpressão',
  'tooling.permissions.reason.printer_claim': 'Reclamação da gráfica',
  'tooling.permissions.reason.audit': 'Auditoria',
  'tooling.permissions.reason.other': 'Outro',
  'tooling.permissions.duration': 'Vence em',
  'tooling.permissions.duration.4': '4 horas',
  'tooling.permissions.duration.24': '24 horas',
  'tooling.permissions.duration.72': '3 dias',
  'tooling.permissions.duration.168': '7 dias',
  'tooling.permissions.duration.720': '30 dias',
  'tooling.permissions.expires': 'Vence em {when}',
  'tooling.permissions.expired': 'Venceu em {when}',

  // ── secciones de la barra lateral
  'nav.section.catalog': 'Catálogo',
  'nav.section.plant': 'Planta',
  'nav.section.activity': 'Atividade',
  'nav.section.protection': 'Proteção de marca',
  'nav.section.config': 'Configuração',
  'nav.section.hq': 'XYVONA',

  // ── ficha de etiqueta, encabezado
  'labels.units.detail.label': 'Etiqueta ·',
  'labels.units.detail.batchInline': 'Lote {lot}',
  'labels.units.detail.rollInline': 'Rolo {roll}',
  'labels.units.detail.openAlertsBadge': '{count} alertas abertas',

  // ── listado de etiquetas
  'labels.units.countTotal': '{count} no total',
  'labels.units.quickFind.placeholder': 'Busque pelo código impresso, ou informe o id do lote ou do rolo abaixo',
  'labels.units.quickFind.submit': 'Buscar',
  'labels.units.column.batch': 'Lote',
  'labels.units.column.created': 'Criada',
  'labels.units.column.actions': 'Ações',
  'labels.units.column.qr': 'QR',

  // ── ámbito activo del listado de etiquetas
  'labels.units.scoped.all': 'Todos os códigos da marca, do mais novo para o mais antigo.',
  'labels.units.scoped.batch': 'Etiquetas do lote {value}. Mude o âmbito abaixo para ver outro.',
  'labels.units.scoped.roll': 'Etiquetas do rolo {value}. Mude o âmbito abaixo para ver outro.',
  'labels.units.scoped.code': 'Busca do código {value}.',
};

export const MESSAGES: Record<Language, Catalog> = { es, en, pt };
